// ============================================================================
// ROLE LEARNING MAPS — Canonical role-based learning content
//
// Static, product-defined learning content for each RBAC role.
// UI-only config. Never written to Firestore. Does not affect RBAC, Gate,
// workflow state, or any clinical pipeline.
//
// User progress is stored separately at: users/{uid}/learning/state
// ============================================================================

import { ROLES, ROLE_DISPLAY, SECTORS, DEFAULT_LANDING, getAllowedRoutes } from '../core/rbac.js';

// ============================================================================
// SKELETON BUILDER — produces a valid but unconfigured map for roles that
// have not yet received full learning content.
// ============================================================================

function buildSkeletonMap(role) {
  const display = ROLE_DISPLAY[role] || { name: role, description: '' };
  const landing = DEFAULT_LANDING[role] || 'dashboard';

  return {
    role,
    roleDisplayName: display.name,

    workflow: {
      id: landing,
      label: display.name,
      description: 'Learning map not yet configured for this role.',
    },

    valueNeeded: 'Learning map not yet configured for this role.',
    objective: 'Learning map not yet configured for this role.',

    roleFormMap: {
      intent: 'Learning map not yet configured for this role.',
      decision: 'Learning map not yet configured for this role.',
      criteria: ['Learning map not yet configured for this role.'],
      activities: ['Learning map not yet configured for this role.'],
      checkpoints: ['Learning map not yet configured for this role.'],
      action: 'Learning map not yet configured for this role.',
      form: 'Learning map not yet configured for this role.',
      metrics: ['Learning map not yet configured for this role.'],
      valueCreated: 'Learning map not yet configured for this role.',
      learningMoments: [],
    },

    orientationCard: {
      loggedInAs: display.name,
      yourJob: 'Learning map not yet configured for this role.',
      mainWorkspace: {
        label: display.name,
        route: `/${landing}`,
      },
      can: ['Learning map not yet configured for this role.'],
      cannot: ['Learning map not yet configured for this role.'],
      mainSuccessAction: 'Learning map not yet configured for this role.',
      noChaosRule: 'Learning map not yet configured for this role.',
    },

    roleClarityQuestions: {
      whoAmI: `I am the ${display.name}.`,
      whatAmIHereToDo: 'Learning map not yet configured for this role.',
      whatCanITouch: 'Learning map not yet configured for this role.',
      whatCanINotTouch: 'Learning map not yet configured for this role.',
      whatActionCreatesProgress: 'Learning map not yet configured for this role.',
      whatFormMustExistAfterIAct: 'Learning map not yet configured for this role.',
    },

    guidedModules: [],
  };
}

// ============================================================================
// GP EMPLOYEE — Creates referral form through the Gate
// ============================================================================

const GP_EMPLOYEE_MAP = {
  role: ROLES.GP_EMPLOYEE,
  roleDisplayName: ROLE_DISPLAY[ROLES.GP_EMPLOYEE].name,

  workflow: {
    id: 'gate',
    label: 'Gate',
    description: 'The referral creation and submission flow where the GP enters clinical information against a specialist-authored Pack.',
  },

  valueNeeded: 'Clarity on what the specialist needs before the referral can move forward.',

  objective: 'Submit a complete, correctly routed referral that produces a Gate outcome.',

  roleFormMap: {
    intent: 'Create referral form from patient need and specialist criteria.',
    decision: 'Does this referral belong in this pathway, and is it complete enough to submit?',
    criteria: [
      'Selected specialty and pathway',
      'Pack criteria for this pathway',
      'Redirect flags that indicate the wrong specialty',
      'Red flags that indicate urgency',
      'Required clinical fields for completeness',
      'Required attachments for intake',
      'Not-Yet requirements when fields are missing',
    ],
    activities: [
      'Select the correct referral pathway',
      'Enter patient and referral information',
      'Complete clinical fields defined by the Pack',
      'Answer red flag questions',
      'Add required attachments',
      'Submit the referral through the Gate',
      'Review the outcome and receipt',
    ],
    checkpoints: [
      'Correct pathway selected for this patient',
      'All required clinical fields completed',
      'Required attachments added or missing reason provided',
      'Gate outcome generated and reviewed',
      'Referral submitted only when valid',
    ],
    action: 'Submit referral through the Gate.',
    form: 'For non-redirect submissions: referral core, metrics event, audit log, workflow/state, and workflowEvents are created atomically.',
    metrics: [
      'Total submissions',
      'Not-Yet rate',
      'Redirect rate',
      'Completion rate',
    ],
    valueCreated: 'The GP creates clean referral form before it enters operations, reducing downstream rework and delays.',
    learningMoments: [
      {
        id: 'gp_pathway_selection',
        teaches: 'How to select the correct referral pathway for the clinical concern.',
        prevents: 'Submitting to the wrong specialty, which causes a redirect and delays care.',
        successLooksLike: 'The referral reaches the correct specialty without being redirected.',
      },
      {
        id: 'gp_clinical_completeness',
        teaches: 'Why each required clinical field matters and what happens when fields are missing.',
        prevents: 'Incomplete referrals that produce a Not-Yet outcome and require rework.',
        successLooksLike: 'The referral produces a Routine outcome with all required information present.',
      },
      {
        id: 'gp_red_flag_awareness',
        teaches: 'What red flags indicate and why answering them accurately matters.',
        prevents: 'Missed urgent clinical findings that delay necessary escalation.',
        successLooksLike: 'Red flags are answered honestly, and urgent referrals are flagged immediately.',
      },
      {
        id: 'gp_outcome_understanding',
        teaches: 'What each Gate outcome means and what happens next in the pipeline.',
        prevents: 'Confusion about why a referral was marked Not-Yet, Redirect, or Urgent.',
        successLooksLike: 'The GP understands the outcome and takes the correct next action.',
      },
    ],
  },

  orientationCard: {
    loggedInAs: ROLE_DISPLAY[ROLES.GP_EMPLOYEE].name,
    yourJob: 'Create referrals against specialist-authored criteria so the right information reaches the right team.',
    mainWorkspace: {
      label: 'Gate + Not-Yet Inbox',
      route: '/gate',
    },
    can: [
      'Select a referral pathway',
      'Complete clinical criteria defined by the Pack',
      'Add required attachments',
      'Submit referrals through the Gate',
      'Review Routine, Urgent, Not-Yet, or Redirect outcomes',
      'Complete missing information for Not-Yet referrals',
    ],
    cannot: [
      'Access operational workqueues',
      'Change downstream workflow status',
      'Perform triage overrides',
      'Schedule appointments',
      'Close the referral loop',
    ],
    mainSuccessAction: 'Submit a complete and correctly routed referral, or complete missing information when a referral is Not-Yet.',
    noChaosRule: 'No referral should enter the pipeline with missing, vague, or wrongly routed information.',
  },

  roleClarityQuestions: {
    whoAmI: 'I am the Referring Provider.',
    whatAmIHereToDo: 'I create referral form against specialist-authored Packs.',
    whatCanITouch: 'I can touch the referral creation flow, Pack answers, attachments, and submission.',
    whatCanINotTouch: 'I cannot change downstream workflow state, triage decisions, appointments, or closed-loop outcomes.',
    whatActionCreatesProgress: 'Submitting a valid referral through the Gate.',
    whatFormMustExistAfterIAct: 'For non-redirect submissions, referral core, workflow/state, metrics, audit logs, and workflowEvents must exist.',
  },

  guidedModules: [
    {
      moduleId: 'packet_inbox_intro',
      title: 'Packet Inbox',
      route: '/packet-inbox',
      sector: SECTORS.PACKET_INBOX,
      role: ROLES.GP_EMPLOYEE,
      steps: [
        {
          stepId: 'intro_workspace',
          title: 'Start from your referral workspace',
          body: 'Packet Inbox is where source material becomes referral action.\n\nThree ways to start a new referral:\n\n- Start from an EHR launch when the patient chart opens ReferralGate.\n- Upload documents for a patient when the referral starts from PDFs, scans, or saved files.\n- Create a manual referral when no packet exists.',
          teaches: 'Use this page to turn source material into a referral-ready packet.',
          prevents: 'Starting from the wrong place can create extra manual work or leave source documents disconnected from the referral.',
        },
        {
          stepId: 'upload_packet',
          title: 'Upload source documents',
          body: 'Use Upload Packet when you have PDFs or images for one patient.\n\nReferralGate creates a packet so you can review the source material before moving it into the Gate.',
          teaches: 'Upload documents first when the referral starts from faxed, scanned, or saved source material.',
          prevents: 'Do not start a manual referral if the source documents should stay attached to the packet.',
        },
        {
          stepId: 'start_manual_referral',
          title: 'Start a manual referral when no packet exists',
          body: 'Use Start Manual Referral when you need to create a referral directly and there is no EHR launch or packet to start from.\n\nThis opens the Referral Gate without a packet.',
          teaches: 'Manual referral is the fallback path when there is no source packet.',
          prevents: 'Do not use manual start when an EHR-launched or uploaded packet is already waiting.',
        },
        {
          stepId: 'inbox_tab',
          title: 'Work the Inbox first',
          body: 'The Inbox shows packets still waiting for action.\n\nOpen a packet, review its documents, add support if needed, then continue it to the Referral Gate.',
          teaches: 'Inbox is your active queue for packets that still need to become referrals.',
          prevents: 'Leaving packets in the Inbox slows the referral before it reaches the Gate.',
          focusTarget: 'packet_inbox_tab_inbox',
        },
        {
          stepId: 'completed_tab',
          title: 'Check completed packets',
          body: 'Completed shows packets that already moved into the referral flow.\n\nUse it to confirm a packet was already used, instead of starting the same work again.',
          teaches: 'Completed helps you verify that source material already became referral action.',
          prevents: 'Do not restart from a completed packet unless you intentionally need a new referral.',
          focusTarget: 'packet_inbox_tab_completed',
        },
        {
          stepId: 'deleted_tab',
          title: 'Deleted Packets',
          body: 'Deleted shows packets removed from active work.\n\nUse this tab to check packets that were cleared out, so you do not lose track of removed source material.',
          teaches: 'Deleted keeps removed packets visible outside the active Inbox.',
          prevents: 'Do not delete a packet unless it should leave active referral work.',
          focusTarget: 'packet_inbox_tab_deleted',
        },
        {
          stepId: 'packet_snippet',
          title: 'Read one packet card',
          body: 'Each packet card shows:\n\n* when the packet was created\n* how many documents it has\n* whether scanning or EHR candidates are ready\n* the packet ID\n\nUse the card details to choose the next action.',
          teaches: 'The packet card shows the packet\'s current state before you move it forward.',
          prevents: 'Do not continue to the Gate until you check whether the packet is ready.',
          focusTarget: 'packet_card',
        },
        {
          stepId: 'packet_buttons',
          title: 'Use the packet buttons to move the work',
          body: 'Review Documents opens the packet contents.\n\nAdd supporting documents attaches more source material.\n\nContinue to Referral Gate moves the packet into the Gate.\n\nDelete removes the packet from active work.',
          teaches: 'Choose the button that moves the packet one step closer to a complete referral.',
          prevents: 'Do not delete or continue a packet until you know whether its source material is ready.',
        },
      ],
    },
    {
      moduleId: 'dashboard_activity_intro',
      title: 'Dashboard Activity Intro',
      route: '/dashboard',
      sector: SECTORS.GATE,
      role: ROLES.GP_EMPLOYEE,
      steps: [
        {
          stepId: 'understand_dashboard',
          title: 'Your dashboard shows your active work',
          body: 'This is your starting point as a Referring Provider.\n\nFrom here, you can start a new referral, resolve missing information, or review referrals you already submitted.',
          teaches: 'The dashboard is your work entry point.',
          prevents: 'Treating the dashboard as a blank landing page.',
          focusTarget: 'primary_dashboard_actions',
        },
        {
          stepId: 'start_new_referral_activity',
          title: 'Start New Referral',
          body: 'Use Start New Referral when a patient needs specialist care and you need to create a new referral.\n\nThe Gate will guide you through the required questions, documents, and outcome.',
          teaches: 'Start New Referral is where new referrals begin.',
          prevents: 'Incomplete or wrongly routed referrals entering the pipeline.',
          focusTarget: 'start_new_referral_button',
        },
        {
          stepId: 'resolve_referrals_activity',
          title: 'Not Yet Inbox — Resolve Referrals',
          getBody: (ctx) => ctx?.notYetCount > 0
            ? 'Use the Not Yet Inbox when referrals are waiting for missing information.\n\nThese referrals are already started, but they cannot move forward until you complete what is missing.'
            : 'When referrals come back needing more information, you will find them in your Not Yet Inbox. Right now you have none — once you do, a Resolve Referrals shortcut will appear here on your dashboard.',
          body: 'Use the Not Yet Inbox when referrals are waiting for missing information.\n\nThese referrals are already started, but they cannot move forward until you complete what is missing.',
          getTeaches: (ctx) => ctx?.notYetCount > 0
            ? 'The Not Yet Inbox is where you repair referrals that need missing information.'
            : 'Your Not Yet Inbox is where incomplete referrals wait for you. When one appears, you can resolve it from your dashboard or directly from the inbox.',
          teaches: 'The Not Yet Inbox is where you repair referrals that need missing information.',
          prevents: 'Referrals staying stuck because missing information was never addressed.',
          getFocusTarget: (ctx) => ctx?.notYetCount > 0 ? 'resolve_referrals_button' : 'not_yet_nav_item',
          focusTarget: 'resolve_referrals_button',
        },
        {
          stepId: 'action_required_alert',
          title: 'Action Required Alert',
          getBody: (ctx) => ctx?.notYetCount > 0
            ? 'When this alert appears, it means referrals need your input before they can move forward.\n\nResolve these first so existing referrals do not stay stalled.'
            : 'When referrals need your attention, an Action Required alert will appear on your dashboard. You have none right now — your Not Yet Inbox is where they will appear when they do.',
          body: 'When this alert appears, it means referrals need your input before they can move forward.\n\nResolve these first so existing referrals do not stay stalled.',
          getTeaches: (ctx) => ctx?.notYetCount > 0
            ? 'Stalled referrals are active work that needs your attention.'
            : 'When a referral needs missing information from you, it shows up as action required. Your Not Yet Inbox will hold these items.',
          teaches: 'Stalled referrals are active work that needs your attention.',
          prevents: 'Referrals that need your input being overlooked.',
          getFocusTarget: (ctx) => ctx?.notYetCount > 0 ? 'action_required_alert' : 'not_yet_nav_item',
          focusTarget: 'action_required_alert',
        },
        {
          stepId: 'my_submissions_activity',
          title: 'My Submissions',
          body: 'Use My Submissions to review referrals you already submitted.\n\nThis is where you can check what you sent and follow your referral history.',
          teaches: 'My Submissions is for review and tracking.',
          prevents: 'Confusing submitted referral history with the Not Yet Inbox, where missing information is completed.',
          focusTarget: 'my_submissions_nav_item',
        },
        {
          stepId: 'choose_next_action',
          title: 'Choose the right next action',
          getBody: (ctx) => ctx?.notYetCount > 0
            ? 'If you are creating a new referral, start with Start New Referral.\n\nIf the system is waiting on you, open the Not Yet Inbox and resolve those referrals first.\n\nIf you want to review what you already sent, open My Submissions.'
            : 'Right now, your main action is to start a new referral. Once referrals come back needing more information, your Not Yet Inbox will show you what to resolve.\n\nTo review what you already sent, open My Submissions.',
          body: 'If you are creating a new referral, start with Start New Referral.\n\nIf the system is waiting on you, open the Not Yet Inbox and resolve those referrals first.\n\nIf you want to review what you already sent, open My Submissions.',
          getTeaches: (ctx) => ctx?.notYetCount > 0
            ? 'The dashboard helps you choose between creating, repairing, and reviewing referrals.'
            : 'When you have no stalled referrals, your next step is to start a new one or review what you already sent in My Submissions.',
          teaches: 'The dashboard helps you choose between creating, repairing, and reviewing referrals.',
          prevents: 'Starting new referrals while existing referrals remain stuck waiting for your input.',
          getFocusTarget: (ctx) => ctx?.notYetCount > 0 ? 'primary_dashboard_actions' : 'start_new_referral_button',
          focusTarget: 'primary_dashboard_actions',
        },
      ],
    },
    {
      moduleId: 'gate_intro',
      title: 'Gate Introduction',
      route: '/gate',
      sector: SECTORS.GATE,
      role: ROLES.GP_EMPLOYEE,
      steps: [
        {
          stepId: 'patient_information',
          title: 'Patient Information',
          body: 'Start here. Enter the patient details that identify this referral. This section does not affect the clinical decision — it ensures the referral can be matched to the right patient downstream.',
          teaches: 'Why patient identification is collected first and kept separate from clinical logic.',
          prevents: 'Skipping patient details and creating an unidentifiable referral.',
          focusTarget: 'gate_patient_information_section',
        },
        {
          stepId: 'select_referral_pathway',
          title: 'Select Referral Pathway',
          body: 'Choose the specialty and Pack that matches the clinical concern. The Pack is authored by the specialist and defines every question the Gate will ask. Once selected, the form adapts to that Pack.',
          teaches: 'How pathway selection determines which clinical criteria, red flags, and routing checks apply.',
          prevents: 'Starting the form without a Pack, which means the Gate has no criteria to evaluate.',
          focusTarget: 'gate_pathway_selection_section',
        },
        {
          stepId: 'routing_check',
          title: 'Routing Check',
          body: 'Routing check statements help confirm whether the selected Pack is the right owner for this referral.\n\nIf none of the routing statements apply, the selected Pack is likely the right fit — you can skip this section and continue.\n\nIf a routing statement does apply, the system can guide the referral to a better pathway before it is submitted.',
          teaches: 'Routing checks help confirm whether this referral belongs in the selected Pack.',
          prevents: 'Submitting a referral into a pathway that does not own the clinical question.',
          focusTarget: 'gate_routing_section',
        },
        {
          stepId: 'visit_information',
          title: 'Visit Information',
          body: 'Record the visit type and purpose. If the visit purpose is Urgent Consult, you must also select a reason for escalation. These fields set the clinical context before the Pack-specific questions begin.',
          teaches: 'How visit metadata frames the referral before clinical criteria are evaluated.',
          prevents: 'Leaving visit context blank, which removes important framing for the specialist.',
          focusTarget: 'gate_visit_information_section',
        },
        {
          stepId: 'red_flags',
          title: 'Red flags',
          body: 'Answer red flag questions carefully.\n\nRed flags identify urgent safety concerns that may need faster specialist attention. If a red flag applies, the referral is treated as urgent.',
          teaches: 'Red flags are safety checks that can escalate the referral\'s urgency.',
          prevents: 'Missing urgent findings that should receive faster attention.',
          focusTarget: 'gate_red_flags_section',
        },
        {
          stepId: 'required_workup',
          title: 'Required Minimum Workup',
          body: 'These are the clinical fields the specialist defined as required. Every field must be completed for a Routine outcome. Missing any required field produces a Not-Yet outcome, which means the referral cannot move forward.',
          teaches: 'The direct relationship between required-field completeness and Gate outcome.',
          prevents: 'Submitting incomplete referrals that produce Not-Yet and require rework.',
          focusTarget: 'gate_required_workup_section',
        },
        {
          stepId: 'decision_preview',
          title: 'Decision Preview',
          body: 'The Decision Preview updates live as you fill in the form. It shows the current outcome — Routine, Urgent, or Not-Yet — and lists any missing fields. Use it to verify your progress before submitting.',
          teaches: 'How to read the live preview and understand what the Gate will decide.',
          prevents: 'Submitting without checking the preview and being surprised by the outcome.',
          focusTarget: 'gate_decision_preview_section',
        },
        {
          stepId: 'override',
          title: 'Override',
          body: 'If the outcome is Not-Yet but you believe the referral should proceed, the Override panel lets you submit as Routine with a documented reason. This action is logged and auditable. It appears only when the current outcome is Not-Yet.',
          teaches: 'When and how to use a clinical override responsibly.',
          prevents: 'Using override as a shortcut instead of completing the required workup.',
          focusTarget: 'gate_override_section',
        },
        {
          stepId: 'attachments',
          title: 'Attachments',
          body: 'Attach supporting documents such as lab results, imaging, or clinical notes. Some Packs define specific attachment requirements. Missing required attachments can trigger rework requests from the intake team after submission.',
          teaches: 'Which documents the specialist expects and how attachments affect downstream processing.',
          prevents: 'Incomplete submissions that stall in the workqueue waiting for documents.',
          focusTarget: 'gate_attachments_section',
        },
        {
          stepId: 'submit_referral',
          title: 'Submit Referral',
          body: 'When the form is complete, click Submit Referral Decision to finalize and record the referral. This action writes the referral, audit log, and workflow state in one atomic step. Once submitted, you cannot edit the referral — you will see the result on the next screen.',
          teaches: 'What happens when you submit and why the action is final.',
          prevents: 'Submitting before the form is ready, or expecting to edit the referral afterward.',
          focusTarget: 'gate_submit_referral_section',
        },
      ],
    },
    {
      moduleId: 'submission_result_intro',
      title: 'Submission Result',
      route: '/gate',
      trigger: 'context',
      contextKey: 'submittedPhase',
      sector: SECTORS.GATE,
      role: ROLES.GP_EMPLOYEE,
      steps: [
        {
          stepId: 'submission_outcome',
          title: 'Outcome',
          body: 'This banner shows the Gate decision for this referral — Routine, Urgent, or Not Yet. It also includes the Run ID, specialty, Pack version, and completeness score. Use this to confirm the referral was recorded correctly.',
          teaches: 'How to read the outcome banner and verify the referral result.',
          prevents: 'Missing the outcome or confusing which referral was just submitted.',
          focusTarget: 'submission_outcome_section',
        },
        {
          stepId: 'patient_note',
          title: 'Patient Note',
          body: 'The Patient Note is a plain-language message you can give or send to the patient. It contains no PHI — it confirms the referral status and any next steps. Click Copy to copy it to your clipboard.',
          teaches: 'How to use the Patient Note and why it is safe to share.',
          prevents: 'Forgetting to inform the patient about their referral status.',
          focusTarget: 'submission_patient_note_section',
        },
        {
          stepId: 'receipt_exports',
          title: 'Export Receipt',
          body: 'Two export options are available. Copy EHR Note copies a structured, zero-PHI note for pasting into your EHR. Download Full Receipt PDF generates a print-ready document that includes patient identity and is access-logged.',
          teaches: 'The difference between the EHR note (no PHI) and the full receipt (PHI, logged).',
          prevents: 'Pasting PHI into an unsecured system or skipping documentation entirely.',
          focusTarget: 'submission_receipt_exports_section',
        },
        {
          stepId: 'next_step_after_submission',
          title: 'What happens next',
          getBody: (ctx) => ctx?.isResuming
            ? 'Once the missing information is submitted, the referral can continue through the normal review process. You can track the updated referral in My Submissions.'
            : 'You can start a new referral or return to the Dashboard. The referral is now in the intake queue and will be reviewed by the operations team.',
          body: 'You can start a new referral or return to the Dashboard. The referral is now in the intake queue and will be reviewed by the operations team.',
          teaches: 'Where the referral goes after submission and what actions are available.',
          prevents: 'Uncertainty about what happens next or whether any follow-up is needed.',
          focusTarget: 'submission_next_actions_section',
        },
      ],
    },
    {
      moduleId: 'not_yet_completion_intro',
      title: 'Not-Yet Inbox',
      route: '/not-yet-inbox',
      sector: SECTORS.NOT_YET,
      role: ROLES.GP_EMPLOYEE,
      steps: [
        {
          stepId: 'scan_referral_list',
          title: 'Scan the referral list',
          body: 'The left panel lists referrals that need your attention.\n\nEach card shows the specialty, missing-item count, and how long it has been waiting.',
          teaches: 'How to read the Not Yet Inbox at a glance and choose which referral to fix first.',
          prevents: 'Overlooking a stalled referral that has been waiting the longest.',
          getFocusTarget: (ctx) => ctx?.selectedReferralId ? null : 'not_yet_referral_list',
          focusTarget: 'not_yet_referral_list',
        },
        {
          stepId: 'open_referral_detail',
          title: 'Open a referral to see what is missing',
          body: 'Click any card to open the detail panel on the right.\n\nThe detail panel shows exactly what still needs to be completed before the referral can continue.',
          teaches: 'How to read the detail panel and understand each missing item.',
          prevents: 'Submitting a partial fix that leaves the referral in Not Yet.',
          getFocusTarget: (ctx) => ctx?.selectedReferralId ? 'not_yet_detail_panel' : 'not_yet_first_card',
          focusTarget: 'not_yet_first_card',
        },
        {
          stepId: 'complete_referral_action',
          title: 'Complete the referral',
          body: 'For Not-Yet referrals, click Complete This Referral to return to the form with previous answers pre-filled.\n\nFill only what is missing, then submit the update.',
          teaches: 'You are repairing an existing referral, not starting from scratch.',
          prevents: 'Changing unrelated information or leaving required items incomplete.',
          getFocusTarget: (ctx) => ctx?.selectedReferralId ? 'not_yet_complete_referral_button' : null,
          focusTarget: null,
        },
        {
          stepId: 'after_completion',
          title: 'What happens next',
          body: 'Once you submit the missing information, the referral can continue through the normal review process.\n\nYou can track the updated referral in My Submissions.',
          teaches: 'Where to check progress after completing missing information.',
          prevents: 'Going back to the Not Yet Inbox expecting the completed referral to remain listed there.',
        },
      ],
    },
    {
      moduleId: 'gate_missing_info_completion',
      title: 'Completing a Not-Yet Referral',
      route: '/gate',
      trigger: 'context',
      contextKey: 'resumeReferral',
      sector: SECTORS.NOT_YET,
      role: ROLES.GP_EMPLOYEE,
      steps: [
        {
          stepId: 'context_banner',
          title: 'You are completing a Not-Yet referral',
          body: 'This is not a new referral.\n\nYou came here from the Not Yet Inbox to complete missing information. Previous answers are pre-filled so you only need to focus on what is missing.',
          teaches: 'Why the form looks partially filled and what completion mode means.',
          prevents: 'Accidentally clearing pre-filled answers or thinking this is a fresh referral.',
          focusTarget: 'gate_missing_info_context_banner',
        },
        {
          stepId: 'fill_missing_fields',
          title: 'Fill in the missing fields',
          body: 'Review the highlighted or empty required fields.\n\nComplete the missing information requested by the system before submitting.',
          teaches: 'How to identify and complete the specific fields that caused the Not-Yet outcome.',
          prevents: 'Submitting again with the same gaps, which produces another Not-Yet outcome.',
          focusTarget: 'gate_required_workup_section',
        },
        {
          stepId: 'resubmit',
          title: 'Submit the completed referral',
          body: 'When the missing information is complete, submit the referral again.\n\nThe Gate will check the updated referral and produce the next outcome.',
          teaches: 'How resubmission triggers a full Gate re-evaluation.',
          prevents: 'Expecting the old Not-Yet outcome to persist after a successful resubmission.',
          focusTarget: 'gate_missing_info_submit_area',
        },
        {
          stepId: 'after_resubmit',
          title: 'What happens next',
          body: 'Once the missing information is submitted, the referral can continue through the normal review process.\n\nYou can track the updated referral in My Submissions.',
          teaches: 'The lifecycle after resubmission and where to monitor progress.',
          prevents: 'Going back to the Not Yet Inbox expecting the completed referral to remain listed there.',
        },
      ],
    },
    {
      moduleId: 'my_submissions_intro',
      title: 'My Submissions — Track Your Referrals',
      route: '/my-submissions',
      sector: 'SECTOR_GATE',
      role: 'gp_employee',
      steps: [
        {
          stepId: 'understand_my_submissions',
          title: 'Your Referral History',
          body: 'This page shows every referral you have submitted. Each row includes the specialty, pack, outcome, and current pipeline status so you can track progress without calling the office.',
          teaches: 'Where to find all your submitted referrals and what information each row contains.',
          prevents: 'Calling the specialist office to ask about referral status.',
          focusTarget: 'my_submissions_page_header',
        },
        {
          stepId: 'use_submission_tabs',
          title: 'Filter your submissions',
          body: 'Use these tabs to quickly narrow your referral history.\n\nAll Submissions shows everything you have sent.\nIn Progress shows referrals still moving through the process.\nCompleted shows referrals that have finished.',
          teaches: 'The tabs help you separate all referrals, active referrals, and completed referrals.',
          prevents: 'Scrolling through your full history when you only need one group of submissions.',
          focusTarget: 'my_submissions_tabs',
        },
        {
          stepId: 'filter_submission_history',
          title: 'Search and Filter',
          body: 'Use the search bar to find a specific referral by ID. Narrow results further with the date range buttons and outcome filter to find exactly what you need.',
          teaches: 'How to combine search, date range, and outcome filters to locate specific referrals.',
          prevents: 'Missing a referral because the list is too long to scan manually.',
          focusTarget: 'my_submissions_filters',
        },
        {
          stepId: 'read_submission_row',
          title: 'Read the Submissions Table',
          body: 'Each row shows the submission date, referral ID, specialty and pack, the Gate outcome (Routine, Not Yet, or Urgent), and the current workflow status. The status column updates as the referral moves through the pipeline.',
          teaches: 'How to read each column in the submissions table and understand what the status badges mean.',
          prevents: 'Confusing the Gate outcome with the current workflow status.',
          focusTarget: 'my_submissions_table',
        },
        {
          stepId: 'open_submission_details',
          title: 'View Referral Details',
          body: 'Click the eye icon in the View column to open the full referral receipt. The receipt shows everything that was submitted: clinical answers, the Gate decision, and the referral ID for your records.',
          teaches: 'How to open a referral receipt to review the full submission details.',
          prevents: 'Not knowing how to access the detailed referral information after submission.',
          focusTarget: 'my_submissions_view_action',
        },
      ],
    },
    {
      moduleId: 'view_receipt_intro',
      title: 'Understanding Your Referral Receipt',
      route: '/my-submissions',
      trigger: 'modal',
      modalId: 'receipt',
      steps: [
        {
          stepId: 'receipt_header_and_outcome',
          title: 'Receipt header and Gate outcome',
          body: 'The top of the receipt shows the referral receipt identity, confidentiality notice, and the Gate outcome. Use this area to confirm you are viewing the official submitted receipt and to see the outcome produced by the Gate.',
          teaches: 'The receipt header confirms the record, and the Gate outcome tells you what happened after submission.',
          prevents: 'Missing the Gate outcome or overlooking that this receipt may contain confidential information.',
          focusTarget: 'receipt_header_outcome_area',
        },
        {
          stepId: 'referral_summary',
          title: 'Referral summary',
          body: 'This section shows the referral ID, specialty, Pack, and when the receipt was generated. Use it to confirm you are reviewing the correct referral and pathway.',
          teaches: 'The referral summary identifies the submitted referral and the Pack it was created against.',
          prevents: 'Confusing one referral receipt with another.',
          focusTarget: 'receipt_referral_summary_bar',
        },
        {
          stepId: 'patient_phi_section',
          title: 'Patient information and PHI',
          body: 'This section contains patient and insurance information. Because it may contain protected health information, handle it according to HIPAA and tenant policy.',
          teaches: 'Patient details are available for review, but they must be handled carefully.',
          prevents: 'Sharing, printing, or exposing patient information without proper care.',
          focusTarget: 'receipt_patient_phi_section',
        },
        {
          stepId: 'criteria_completeness_snapshot',
          title: 'Criteria and completeness snapshot',
          body: 'This section shows the submitted criteria, completeness result, and whether a Gate Override was applied. Use it to understand what information supported the Gate outcome.',
          teaches: 'The snapshot explains how the referral was completed and whether any override was used.',
          prevents: 'Reviewing the outcome without understanding the submitted criteria behind it.',
          focusTarget: 'receipt_criteria_snapshot_section',
        },
        {
          stepId: 'attachments_section',
          title: 'Attachments',
          body: 'This section shows documents connected to the referral. Use the view action when you need to inspect a supporting document.',
          teaches: 'Attachments support the submitted referral record.',
          prevents: 'Missing important supporting documents when reviewing the referral.',
          focusTarget: 'receipt_attachments_section',
        },
        {
          stepId: 'timeline_section',
          title: 'Referral status and timeline',
          body: 'This section shows the current referral status and the activity timeline after submission. Use it to follow what happened and where the referral currently stands.',
          teaches: 'The status and timeline help you track referral progress over time.',
          prevents: 'Losing track of what happened after the referral was submitted.',
          focusTarget: 'receipt_timeline_section',
        },
        {
          stepId: 'download_pdf',
          title: 'Download PDF',
          body: 'Use Download PDF when you need a copy of the full referral receipt for approved clinical or administrative use. Because the PDF may contain patient information, handle it according to HIPAA and tenant policy.',
          teaches: 'Download PDF exports the receipt outside the app, so it must be handled carefully.',
          prevents: 'Downloading, sharing, or storing referral receipts without proper care.',
          focusTarget: 'receipt_download_pdf_button',
        },
      ],
    },
  ],

  learningScenarios: [
    {
      scenarioId: 'gp_routine_complete',
      title: 'Complete Routine referral',
      description: 'All required fields are filled, no red flags, correct pathway selected.',
      expectedOutcome: 'ROUTINE',
      teachesLearningMoment: 'gp_clinical_completeness',
      setup: { allFieldsComplete: true, redFlagsPresent: false, correctPathway: true },
    },
    {
      scenarioId: 'gp_routine_no_red_flags',
      title: 'Routine with red flag questions answered negatively',
      description: 'Red flag questions are present but answered negatively. All required fields complete.',
      expectedOutcome: 'ROUTINE',
      teachesLearningMoment: 'gp_red_flag_awareness',
      setup: { allFieldsComplete: true, redFlagsPresent: false, redFlagQuestionsAnswered: true },
    },
    {
      scenarioId: 'gp_routine_with_attachments',
      title: 'Routine with all required attachments',
      description: 'All required clinical fields and attachments are present.',
      expectedOutcome: 'ROUTINE',
      teachesLearningMoment: 'gp_clinical_completeness',
      setup: { allFieldsComplete: true, attachmentsComplete: true },
    },
    {
      scenarioId: 'gp_urgent_red_flag',
      title: 'Urgent referral triggered by red flag',
      description: 'A red flag is answered positively, triggering an URGENT outcome regardless of field completeness.',
      expectedOutcome: 'URGENT',
      teachesLearningMoment: 'gp_red_flag_awareness',
      setup: { redFlagsPresent: true },
    },
    {
      scenarioId: 'gp_not_yet_missing_fields',
      title: 'Not-Yet due to missing required fields',
      description: 'One or more required fields are not completed. The Gate returns NOT_YET with next steps.',
      expectedOutcome: 'NOT_YET',
      teachesLearningMoment: 'gp_clinical_completeness',
      setup: { allFieldsComplete: false, redFlagsPresent: false },
    },
    {
      scenarioId: 'gp_not_yet_missing_attachment',
      title: 'Not-Yet due to missing attachment',
      description: 'Required attachments are missing. The referral cannot proceed without them.',
      expectedOutcome: 'NOT_YET',
      teachesLearningMoment: 'gp_clinical_completeness',
      setup: { allFieldsComplete: true, attachmentsComplete: false },
    },
    {
      scenarioId: 'gp_redirect_wrong_pathway',
      title: 'Redirect to correct specialty',
      description: 'A redirect flag matches, indicating the wrong specialty was selected. No referral is written.',
      expectedOutcome: 'REDIRECT',
      teachesLearningMoment: 'gp_pathway_selection',
      setup: { redirectFlagTriggered: true },
    },
    {
      scenarioId: 'gp_rework_missing_attachment',
      title: 'Rework request for missing attachment',
      description: 'After submission, intake identifies a missing document and creates a rework request. The GP must upload the missing item.',
      expectedOutcome: 'ROUTINE',
      teachesLearningMoment: 'gp_outcome_understanding',
      setup: { submittedSuccessfully: true, reworkRequestCreated: true },
    },
  ],
};

// ============================================================================
// INTAKE COORDINATOR — Receives, checks, and advances incoming referrals
// ============================================================================

const INTAKE_COORDINATOR_MAP = {
  role: ROLES.INTAKE_COORDINATOR,
  roleDisplayName: ROLE_DISPLAY[ROLES.INTAKE_COORDINATOR].name,

  workflow: {
    id: 'workqueue',
    label: 'Workqueue',
    description: 'The operational queue where incoming referrals are received, reviewed, and moved toward scheduling.',
  },

  valueNeeded: 'Clarity — Intake needs to know what arrived, what is missing, who owns it, and what the next step should be.',

  objective: 'Every referral must become either Needs Info or Ready to Schedule.',

  roleMission: 'Make sure every new referral has an owner, a completeness decision, and a clear next action.',

  roleFormMap: {
    intent: 'Receive new referrals, claim ownership, identify missing information, create rework requests, and move complete referrals forward.',
    decision: 'Is this referral complete enough to move forward, or does it need more information?',
    criteria: [
      'Current workflow status of the referral',
      'Gate outcome from the original submission',
      'Missing items identified during review',
      'Required attachments and their availability',
      'Open rework requests and their resolution status',
      'Overall referral completeness for scheduling',
    ],
    activities: [
      'Understand the Workqueue',
      'Claim a referral',
      'Review referral completeness',
      'Decide the next action',
      'Create a rework request',
      'Move complete referrals forward',
    ],
    checkpoints: [
      'Referral is claimed with an assigned owner',
      'Missing items are named and documented',
      'Rework request exists if the referral is incomplete',
      'Status is not left ambiguous or unclaimed',
      'Workflow event is created for every status change',
    ],
    action: 'Claim referral, create rework request if needed, or move referral to READY_TO_SCHEDULE.',
    form: 'workflow/state is updated with the new status and owner, workflowEvents are appended, and reworkRequests are created when items are missing.',
    metrics: [
      'Unclaimed NEW referrals in the queue',
      'Referrals currently in NEEDS_INFO',
      'Rework request completion rate',
      'Time from NEW to READY_TO_SCHEDULE',
    ],
    valueCreated: 'Intake does not rewrite the clinical decision. Intake protects flow by making completeness visible, owned, and actionable.',
    learningMoments: [
      {
        id: 'intake_claim_ownership',
        teaches: 'Why claiming a referral matters and what happens when referrals remain unclaimed.',
        prevents: 'Referrals sitting in NEW without anyone taking responsibility.',
        successLooksLike: 'Every referral in the queue has a named owner within the target response time.',
      },
      {
        id: 'intake_identify_missing',
        teaches: 'How to identify missing information and create a rework request.',
        prevents: 'Moving incomplete referrals forward, which causes downstream delays.',
        successLooksLike: 'Missing items are documented in a rework request and the referral moves to NEEDS_INFO.',
      },
      {
        id: 'intake_ready_to_schedule',
        teaches: 'When a referral is complete enough to move to READY_TO_SCHEDULE.',
        prevents: 'Holding complete referrals in the queue unnecessarily.',
        successLooksLike: 'Complete referrals move to READY_TO_SCHEDULE promptly with all required information present.',
      },
    ],
  },

  orientationCard: {
    loggedInAs: ROLE_DISPLAY[ROLES.INTAKE_COORDINATOR].name,
    yourJob: 'Receive new referrals, claim ownership, identify missing information, create rework requests, and move complete referrals forward.',
    mainWorkspace: {
      label: 'Workqueue',
      route: '/workqueue',
    },
    can: [
      'Review NEW referrals in the Workqueue',
      'Claim referral ownership',
      'Check completeness',
      'Identify missing items',
      'Create rework requests',
      'Move complete referrals to Ready to Schedule',
      'Move incomplete referrals to Needs Info',
    ],
    cannot: [
      'Rewrite the Gate decision',
      'Change clinical outcome',
      'Perform clinical triage overrides',
      'Schedule appointments',
      'Close the referral loop',
      'Edit Pack criteria',
    ],
    mainSuccessAction: 'Every referral must leave Intake with a named next action:\n\n• Needs Info — missing items were identified and a rework request was created\n• Ready to Schedule — the referral is complete enough to move forward',
    noChaosRule: 'No referral should remain unclear, unclaimed, or stuck without an owner and a documented next action.',
  },

  roleClarityQuestions: {
    whoAmI: 'I am the Intake Coordinator.',
    whatAmIHereToDo: 'Make sure every new referral has an owner, a completeness decision, and a clear next action.',
    whatCanITouch: 'Reviewing NEW referrals, claiming ownership, checking completeness, identifying missing items, creating rework requests, and moving referrals to Needs Info or Ready to Schedule.',
    whatCanINotTouch: 'I cannot rewrite the Gate decision, change clinical outcome, perform triage overrides, schedule appointments, close the referral loop, or edit Pack criteria.',
    whatActionCreatesProgress: 'Claiming the referral and moving it to either Needs Info or Ready to Schedule.',
    whatFormMustExistAfterIAct: 'workflow/state must show the updated operational status, and workflowEvents must record the action.',
  },

  guidedModules: [
    {
      moduleId: 'workqueue_activity_1',
      title: 'Understand the Workqueue',
      route: '/workqueue',
      sector: SECTORS.WORKQUEUE,
      role: ROLES.INTAKE_COORDINATOR,
      steps: [
        {
          stepId: 'intake_your_workspace',
          title: 'Your Intake Workspace',
          body: 'Your workspace separates action from visibility.\n\nActive Queue shows the work Intake owns now: New, Needs Info, and Ready for Intake Review.\n\nReferral Outcomes shows what happened after Intake handed the referral forward.\n\nQueue Health shows delays, bottlenecks, and referrals that need attention.',
          teaches: 'Start in Active Queue. That is where Intake work begins.',
          prevents: 'Treating downstream states like Ready to Schedule, Scheduled, or Seen as Intake work.',
          focusTarget: 'intake_sidebar',
        },
        {
          stepId: 'intake_your_workqueue',
          title: 'Your Workqueue',
          body: 'The Workqueue is the central place where newly submitted referrals arrive for first operational review.\n\nThis is not just a list — it is your action queue. Every referral here needs attention, ownership, and a decision.',
          teaches: 'The Workqueue is Intake\'s action queue, not a place to check later.',
          prevents: 'Treating the Workqueue as an inbox instead of active work to move now.',
          focusTarget: 'workqueue_main_list',
        },
        {
          stepId: 'intake_active_queue_tabs',
          title: 'Active Queue Tabs',
          body: 'The Active Queue shows Intake-owned work — referrals you can act on now.\n\nUse the status tabs to filter:\n• All — every active Intake referral\n• New — unworked referrals needing first Intake action\n• Needs Info — missing information to request, follow up, and clear\n• Ready for Intake Review — recovered Not-Yet referrals the Referring Provider completed; ready for your final review\n\nThe Gate Outcome filter narrows by clinical decision: Routine, Urgent, or Not Yet.\n\nWhat happened after Intake handed the referral forward lives in Referral Outcomes, not here.',
          teaches: 'The Active Queue is Intake-owned work: New, Needs Info, and Ready for Intake Review.',
          prevents: 'Treating the Active Queue like a full pipeline viewer instead of an Intake action queue.',
          focusTarget: 'active_queue_tabs',
        },
        {
          stepId: 'intake_referral_outcomes',
          title: 'Referral Outcomes',
          body: 'Referral Outcomes shows what happened after Intake handed the referral forward.\n\n• After Intake — Ready to Schedule, Scheduled, and Seen (post-handoff visibility)\n• Terminal — Closed Loop, No-Show, or Cancelled\n\nThese are not Active Queue work. Use the filters to confirm referrals moved forward and to check resolution patterns.',
          teaches: 'Referral Outcomes holds post-Intake states (After Intake) and terminal results — visibility, not active work.',
          prevents: 'Searching the Active Queue for downstream or completed referrals.',
          focusTarget: 'referral_outcomes_tab',
        },
        {
          stepId: 'intake_new_referrals',
          title: 'New Referrals',
          body: 'NEW referrals appear in the Workqueue after a Referring Provider submits through the Gate.\n\nEach referral shows the specialty, pathway, Gate outcome, priority, and submission time. Your first step is to identify which referrals need action now.',
          teaches: 'NEW referrals are unworked — they need Intake review and a completeness decision.',
          prevents: 'Skipping over NEW referrals or leaving them unreviewed.',
          focusTarget: 'workqueue_new_tab',
        },
        {
          stepId: 'intake_read_referral_card',
          title: 'Read the Referral Card',
          body: 'Each referral card gives you the first signals before you open the referral.\n\nCheck the status, Gate outcome, priority, specialty, pathway, and submission time. These signals help you decide what needs attention first.',
          teaches: 'Referral cards show the key signals before review begins.',
          prevents: 'Opening referrals blindly without understanding urgency, pathway, or current status.',
          focusTarget: 'workqueue_first_referral_card',
        },
        {
          stepId: 'intake_open_referral',
          title: 'Open the Referral',
          body: 'Click on a referral card to open the Intake Review Panel.\n\nThis is where you see the full referral summary, clinical answers, attachments, workflow state, and assignment. Everything you need to make a completeness decision is inside.',
          teaches: 'How to access the full referral detail from the Workqueue.',
          prevents: 'Making decisions from the list view without seeing the full referral.',
          focusTarget: 'workqueue_first_referral_card',
        },
      ],
    },
    {
      moduleId: 'workqueue_activity_3',
      title: 'Review and Move a Referral',
      route: '/workqueue',
      trigger: 'modal',
      modalId: 'referral-detail',
      sector: SECTORS.WORKQUEUE,
      role: ROLES.INTAKE_COORDINATOR,
      steps: [
        {
          stepId: 'intake_referral_summary',
          title: 'Referral Summary',
          body: 'Referral Summary shows what came in from the Gate.\n\nCheck the outcome, specialty, pathway, submitted time, and submitter before you take action.\n\nThis gives you the context for what the Referring Provider sent and what the Gate decided.',
          teaches: 'How to read the referral context before working it.',
          prevents: 'Moving a referral forward without understanding what was submitted.',
          focusTarget: 'referral-summary-section',
        },
        {
          stepId: 'intake_workflow_state',
          title: 'Workflow State',
          body: 'Workflow State shows where the referral sits now.\n\nCheck the status, priority, owner, touch attempts, and last update before you work the referral.\n\nStatus tells you where the referral is in Intake. Owner tells you who is responsible for moving it forward.',
          teaches: 'How to check the referral\'s current operational position.',
          prevents: 'Working a referral without knowing its owner, status, or last movement.',
          focusTarget: 'workflow-state-section',
        },
        {
          stepId: 'intake_actions',
          title: 'Intake Actions',
          body: 'Intake Actions are the controls that move the referral forward.\n\nWork in this order:\n\n1. Claim ownership — take responsibility before you work the referral.\n2. Ready to Schedule — use this when the referral has everything needed to move forward.\n3. Request Missing Items — use this when required information or documents are missing.\n4. Log Touch — record outreach, follow-up, or contact attempts so the team knows what happened.\n\nClaim first. Then either clear the referral forward or create the missing-info path.',
          teaches: 'How to claim, clear, request, and log the next action.',
          prevents: 'Leaving the referral unclaimed, unclear, or stuck without documented movement.',
          focusTarget: 'intake-actions-section',
        },
        {
          stepId: 'intake_referral_ping',
          title: 'Referral Ping',
          body: 'Use Referral Ping when another role needs action, clarification, or follow-up on this referral.\n\nReferral Pings create one referral-bound action thread between you and the receiving user.\n\nUse them to coordinate work: ask Triage for clarification, respond to Scheduling blockers, confirm returned intake work, review Closed-Loop requests, or communicate operational follow-up tied to this referral.\n\nReferral Pings are not chat and not document exchange.\n\nKeep replies short, operational, and free of PHI.\n\nThe referral itself remains the work surface. Use Open Referral to complete the actual referral work.',
          teaches: 'Referral Pings coordinate referral-level action between users.',
          prevents: 'Using replies for PHI, loose conversation, or trying to complete referral work inside the thread.',
          focusTarget: 'intake_review_referral_ping',
        },
        {
          stepId: 'intake_missing_items',
          title: 'Missing Items',
          body: 'Missing Items shows the information or documents already requested from the team.\n\nUse this section to see what is still open, what needs follow-up, and what must be cleared before the referral can move forward.\n\nWhen all missing items are completed, the referral can automatically move to Ready to Schedule.',
          teaches: 'How to track requested items and clear the blockers that hold the referral back.',
          prevents: 'Requesting the same item twice, missing follow-up, or moving forward before the right files are complete.',
          focusTarget: 'missing-items-section',
        },
        {
          stepId: 'intake_messaging',
          title: 'Messaging',
          body: 'Messaging gives Intake ready-to-use, non-PHI messages for the next communication step.\n\nWork in this order:\n\n1. Choose the right message.\n2. Copy it into the correct channel — phone note, fax cover, portal message, or internal workflow.\n3. Mark Sent after outreach happens so the team knows the touch was completed.\n\nUse Referrer Information Request when you need missing items.\nUse Patient Notification when the patient needs a plain-language update.\nUse Not-Yet Explanation when the referring office needs to understand what must be completed next.',
          teaches: 'How to copy the right message and record outreach.',
          prevents: 'Sending unclear requests, duplicating outreach, or forgetting to mark communication as sent.',
          focusTarget: 'messaging-section',
        },
        {
          stepId: 'intake_patient_contact',
          title: 'Patient & Contact',
          body: 'Patient & Contact gives Intake the contact context needed for approved outreach.\n\nUse this section only when the workflow requires contact details and your role has permission to access them.',
          teaches: 'How to use contact context only when it supports the next approved action.',
          prevents: 'Looking for contact details before checking status, ownership, and next step.',
          focusTarget: 'patient-contact-section',
        },
        {
          stepId: 'intake_attachments',
          title: 'Attachments',
          body: 'Attachments show the supporting documents connected to the referral.\n\nCheck this section to confirm whether the required files are already present. If a required document is missing, use Intake Actions to request it and move the referral into the missing-info path.',
          teaches: 'How to confirm supporting documents before moving the referral forward.',
          prevents: 'Requesting files that already exist or advancing a referral without required support.',
          focusTarget: 'attachments-section',
        },
        {
          stepId: 'intake_event_history',
          title: 'Event History',
          body: 'Event History shows the referral\'s previous movement.\n\nUse it to check prior touches, status changes, requests, and updates before you act again.\n\nThis helps you continue the work instead of repeating it.',
          teaches: 'How to check what already happened before taking the next action.',
          prevents: 'Duplicate outreach, repeated requests, or missing a previous update.',
          focusTarget: 'event-history-section',
        },
        {
          stepId: 'intake_two_outcomes',
          title: 'Two Intake Outcomes',
          body: 'Every referral in Intake ends in one of two actions:\n\n• Needs Info — something is missing and must be requested before the referral can move forward.\n• Ready to Schedule — the referral is complete and can advance to the scheduling team.\n\nIntake checks completeness and moves the referral to the right next step.',
          teaches: 'The Intake decision is binary: Needs Info or Ready to Schedule.',
          prevents: 'Leaving referrals in an ambiguous state without a clear next action.',
          focusTarget: 'intake-actions-section',
        },
        {
          stepId: 'intake_needs_info_path',
          title: 'Needs Info Path',
          body: 'If required information or documents are missing, move the referral to Needs Info.\n\nThis tells the team that the referral cannot proceed until the missing items are provided.\n\nAlways create a rework request when choosing this path so the Referring Provider knows exactly what to send.',
          teaches: 'When to choose Needs Info and why a rework request must accompany it.',
          prevents: 'Marking Needs Info without documenting what is actually missing.',
          focusTarget: 'needs-info-status-button',
        },
        {
          stepId: 'intake_ready_to_schedule_path',
          title: 'Ready to Schedule Path',
          body: 'If the referral is complete — required information is present, documents are attached, and no blockers remain — move it to Ready to Schedule.\n\nThis advances the referral to the scheduling team.',
          teaches: 'When a referral is complete enough to leave Intake.',
          prevents: 'Holding complete referrals in the queue unnecessarily.',
          focusTarget: 'ready-to-schedule-button',
        },
        {
          stepId: 'intake_request_missing_items',
          title: 'Request Missing Items',
          body: 'Use Request Missing Items to open the rework form.\n\nName the missing item, describe what the Referring Provider needs to send, and submit the request.\n\nThis creates a tracked record that connects the missing item to the referral and tells the team what is still needed.',
          teaches: 'How to create a specific, trackable rework request.',
          prevents: 'Informal chasing without a documented request or unclear asks that delay the response.',
          focusTarget: 'request-missing-items-button',
        },
        {
          stepId: 'intake_log_touch',
          title: 'Log Touch',
          body: 'Use Log Touch to record outreach, follow-up calls, faxes, or contact attempts.\n\nEvery touch creates a timestamped record so the team can see what communication has happened and when.\n\nLog the touch after the outreach happens — not before.',
          teaches: 'How to document communication attempts so the team has a clear outreach trail.',
          prevents: 'Undocumented outreach that forces the next person to start over.',
          focusTarget: 'log-touch-button',
        },
        {
          stepId: 'intake_final_completeness',
          title: 'Final Completeness Check',
          body: 'Before moving a referral forward, confirm that no blocking items remain.\n\nCheck the missing items count, attachment status, and any resolved rework requests.\n\nA referral should only leave Intake when it is genuinely complete.',
          teaches: 'How to confirm a referral is truly complete before advancing it.',
          prevents: 'Moving referrals forward with unresolved blockers that will cause downstream delays.',
          focusTarget: 'missing-items-section',
        },
        {
          stepId: 'intake_move_to_ready',
          title: 'Move to Ready to Schedule',
          body: 'Click Ready to Schedule to move the referral out of Intake and into the scheduling queue.\n\nThis is the positive exit — the referral has an owner, completeness has been confirmed, and the next team can pick it up.',
          teaches: 'How to advance a complete referral to the scheduling team.',
          prevents: 'Complete referrals sitting in Intake when they are ready for the next step.',
          focusTarget: 'ready-to-schedule-button',
        },
        {
          stepId: 'intake_referral_moved',
          title: 'Referral Moved Forward',
          body: 'The referral status has updated and the referral has left Intake.\n\nThe system records the action, who performed it, and when. The referral now appears in the scheduling queue for the next team.',
          teaches: 'What happens after a referral is moved forward and how the system records it.',
          prevents: 'Uncertainty about whether the referral actually advanced or where it went.',
          focusTarget: 'workflow-state-section',
        },
      ],
    },
    {
      moduleId: 'not_yet_completion_intro',
      title: 'Not-Yet Inbox',
      route: '/not-yet-inbox',
      sector: SECTORS.NOT_YET,
      role: ROLES.INTAKE_COORDINATOR,
      steps: [
        {
          stepId: 'scan_referral_list',
          title: 'Scan the referral list',
          body: 'The left panel lists referrals that need attention.\n\nEach card shows the specialty, missing-item count, and how long it has been waiting.',
          teaches: 'How to read the Not Yet Inbox at a glance and choose which referral to fix first.',
          prevents: 'Overlooking a stalled referral that has been waiting the longest.',
          getFocusTarget: (ctx) => ctx?.selectedReferralId ? null : 'not_yet_referral_list',
          focusTarget: 'not_yet_referral_list',
        },
        {
          stepId: 'open_referral_detail',
          title: 'Open a referral to see what is missing',
          body: 'Click any card to open the detail panel on the right.\n\nThe detail panel shows exactly what still needs to be completed before the referral can continue.',
          teaches: 'How to read the detail panel and understand each missing item.',
          prevents: 'Submitting a partial fix that leaves the referral in Not Yet.',
          getFocusTarget: (ctx) => ctx?.selectedReferralId ? 'not_yet_detail_panel' : 'not_yet_first_card',
          focusTarget: 'not_yet_first_card',
        },
        {
          stepId: 'complete_referral_action',
          title: 'Complete the referral',
          body: 'For Not-Yet referrals, click Complete This Referral to return to the form with previous answers pre-filled.\n\nFill only what is missing, then submit the update.',
          teaches: 'You are repairing an existing referral, not starting from scratch.',
          prevents: 'Changing unrelated information or leaving required items incomplete.',
          getFocusTarget: (ctx) => ctx?.selectedReferralId ? 'not_yet_complete_referral_button' : null,
          focusTarget: null,
        },
        {
          stepId: 'after_completion',
          title: 'What happens next',
          body: 'Once the missing information is submitted, the referral can continue through the normal review process.\n\nThe updated referral will appear back in the Workqueue.',
          teaches: 'Where to check progress after completing missing information.',
          prevents: 'Going back to the Not Yet Inbox expecting the completed referral to remain listed there.',
        },
      ],
    },
    {
      moduleId: 'gate_missing_info_completion',
      title: 'Completing a Not-Yet Referral',
      route: '/gate',
      trigger: 'context',
      contextKey: 'resumeReferral',
      sector: SECTORS.NOT_YET,
      role: ROLES.INTAKE_COORDINATOR,
      steps: [
        {
          stepId: 'context_banner',
          title: 'You are completing a Not-Yet referral',
          body: 'This is not a new referral.\n\nYou came here from the Not Yet Inbox to complete missing information. Previous answers are pre-filled so you only need to focus on what is missing.',
          teaches: 'Why the form looks partially filled and what completion mode means.',
          prevents: 'Accidentally clearing pre-filled answers or thinking this is a fresh referral.',
          focusTarget: 'gate_missing_info_context_banner',
        },
        {
          stepId: 'fill_missing_fields',
          title: 'Fill in the missing fields',
          body: 'Review the highlighted or empty required fields.\n\nComplete the missing information requested by the system before submitting.',
          teaches: 'How to identify and complete the specific fields that caused the Not-Yet outcome.',
          prevents: 'Submitting again with the same gaps, which produces another Not-Yet outcome.',
          focusTarget: 'gate_required_workup_section',
        },
        {
          stepId: 'resubmit',
          title: 'Submit the completed referral',
          body: 'When the missing information is complete, submit the referral again.\n\nThe Gate will check the updated referral and produce the next outcome.',
          teaches: 'How resubmission triggers a full Gate re-evaluation.',
          prevents: 'Expecting the old Not-Yet outcome to persist after a successful resubmission.',
          focusTarget: 'gate_missing_info_submit_area',
        },
        {
          stepId: 'after_resubmit',
          title: 'What happens next',
          body: 'Once the missing information is submitted, the referral can continue through the normal review process.\n\nThe updated referral will appear back in the Workqueue.',
          teaches: 'The lifecycle after resubmission and where to monitor progress.',
          prevents: 'Going back to the Not Yet Inbox expecting the completed referral to remain listed there.',
        },
      ],
    },
    {
      moduleId: 'queue_health_intro',
      title: 'Spot Queue Bottlenecks',
      route: '/ops-home',
      sector: SECTORS.OPS,
      role: ROLES.INTAKE_COORDINATOR,
      steps: [
        {
          stepId: 'queue_health_overview',
          title: 'Queue Health',
          body: 'Queue Health shows the live operational state of the referral pipeline.\n\nUse this page to spot where referrals are piling up, which queues are slowing down, and where Intake should act next.\n\nThis is not where you complete referral work. It helps you decide where to go next.',
          teaches: 'How Queue Health helps Intake spot pressure in the workflow.',
          prevents: 'Working one referral at a time without seeing where the whole queue needs attention.',
          focusTarget: 'queue-health-title',
        },
        {
          stepId: 'live_fires_section',
          title: 'Live Fires',
          body: 'Live Fires shows queues where referrals have passed the tenant time threshold.\n\nEach fire card shows how many referrals are stuck, what percent of the queue is affected, and whether the queue is in a warning or critical state.\n\nWhen a fire lights up, go to the matching queue and move the stuck work.',
          teaches: 'How to spot queues with referrals stuck beyond the time threshold.',
          prevents: 'Missing delays because you only checked the main Workqueue list.',
          focusTarget: 'queue-health-live-fires',
        },
        {
          stepId: 'unclaimed_new_fire',
          title: 'Unclaimed New',
          body: 'Unclaimed New shows new referrals that Intake has not claimed yet.\n\nWhen this card lights up, referrals are sitting in the queue without an owner.\n\nGo to the Workqueue, claim them, and start moving them.',
          teaches: 'How to spot new referrals waiting for ownership.',
          prevents: 'Letting new referrals sit unclaimed while Intake works other tasks.',
          focusTarget: 'queue-health-unclaimed-new-fire',
          focusTargetVolume: 'queue-health-unclaimed-new-volume',
        },
        {
          stepId: 'needs_info_fire',
          title: 'Needs Info',
          body: 'Needs Info shows referrals waiting too long for missing information to be cleared.\n\nWhen this card lights up, follow-up is overdue.\n\nGo to Needs Info to see which referrals need outreach, a touch logged, or missing items cleared.',
          teaches: 'How to spot missing-info work that needs follow-up.',
          prevents: 'Referrals stalling because missing items were requested but not followed up.',
          focusTarget: 'queue-health-needs-info-fire',
          focusTargetVolume: 'queue-health-needs-info-volume',
        },
        {
          stepId: 'workload_by_stage',
          title: 'Workload by Stage',
          body: 'Workload by Stage shows how many referrals sit in each operational stage.\n\nUse these numbers to see where the queue is carrying volume: unclaimed referrals, missing-info work, or referrals waiting for the next move.\n\nThis section shows volume, not urgency. Live Fires shows urgency.',
          teaches: 'How to read total queue volume separate from SLA urgency.',
          prevents: 'Confusing a large queue with a stuck queue.',
          focusTarget: 'queue-health-workload-by-stage',
          focusTargetFallback: 'queue-health-engine-volume',
        },
        {
          stepId: 'refresh_queue_health',
          title: 'Refresh Queue Health',
          body: 'Use Refresh to reload the latest queue picture.\n\nAfter Intake claims referrals, clears missing items, or moves referrals forward, refresh to confirm the dashboard changed.',
          teaches: 'How to confirm Queue Health reflects recent movement.',
          prevents: 'Acting on stale queue information after referrals have moved.',
          focusTarget: 'queue-health-refresh-button',
        },
      ],
    },
  ],

  learningScenarios: [
    {
      scenarioId: 'intake_new_complete',
      title: 'Complete referral moves to READY_TO_SCHEDULE',
      description: 'A NEW referral arrives with all required information. The coordinator claims it and moves it directly to READY_TO_SCHEDULE.',
      expectedOutcome: 'READY_TO_SCHEDULE',
      teachesLearningMoment: 'intake_ready_to_schedule',
      setup: { referralComplete: true, missingItems: false },
    },
    {
      scenarioId: 'intake_new_incomplete',
      title: 'Incomplete referral triggers rework',
      description: 'A NEW referral is missing required documents. The coordinator claims it, creates a rework request, and moves it to NEEDS_INFO.',
      expectedOutcome: 'NEEDS_INFO',
      teachesLearningMoment: 'intake_identify_missing',
      setup: { referralComplete: false, missingItems: true },
    },
    {
      scenarioId: 'intake_rework_resolved',
      title: 'Rework resolved and referral advances',
      description: 'The GP uploads missing documents. The rework request resolves and the referral moves from NEEDS_INFO to READY_TO_SCHEDULE.',
      expectedOutcome: 'READY_TO_SCHEDULE',
      teachesLearningMoment: 'intake_ready_to_schedule',
      setup: { reworkResolved: true, missingItems: false },
    },
  ],
};

// ============================================================================
// SCHEDULER — Converts ready referrals into confirmed appointments
// ============================================================================

const SCHEDULER_MAP = {
  role: ROLES.SCHEDULER,
  roleDisplayName: ROLE_DISPLAY[ROLES.SCHEDULER].name,

  workflow: {
    id: 'scheduling',
    label: 'Scheduling',
    description: 'The scheduling queue where operationally ready referrals are converted into confirmed appointments.',
  },

  valueNeeded: 'A clean list of referrals that are ready to book, and accurate records for every appointment that changes.',

  objective: 'Every Ready to Schedule referral receives a confirmed appointment, and every appointment change is accounted for as Rescheduled, Cancelled, or No-Show.',

  roleFormMap: {
    intent: 'Convert operationally ready referral form into real-world appointment form.',
    decision: 'Which appointment slot, specialist, location, and date should be assigned?',
    criteria: [
      'Workflow status is Ready to Schedule',
      'Referral priority level',
      'Specialist and location availability',
      'Scheduling rules and constraints',
      'Patient and contact availability through approved flows',
    ],
    activities: [
      'Review Ready to Schedule referrals',
      'Select an appropriate appointment date',
      'Assign specialist and location when required',
      'Mark the referral as Scheduled',
      'Return a referral to Intake when the file is not actually ready for scheduling',
      'Reschedule booked appointments when details change',
      'Record patient cancellations',
      'Record clinic cancellations',
      'Mark appointments as No-Show when the patient misses',
    ],
    checkpoints: [
      'Referral status is Ready to Schedule before booking',
      'Appointment metadata is complete with date and details',
      'Scheduled date exists in the workflow state',
      'Specialist and location are assigned when required',
      'Workflow event is created for every scheduling action',
      'Returned referral includes a clear blocker reason',
      'Reschedule preserves the referral and records new details',
      'Cancellation records the source: patient or clinic',
      'No-Show is recorded as an outcome, not treated as Seen',
    ],
    action: 'Book appointments, reschedule when details change, and record every appointment outcome.',
    form: 'workflow/state reflects the current appointment state, and workflowEvents records every booking, reschedule, cancellation, or no-show.',
    metrics: [
      'Ready to Schedule backlog count',
      'Time from Ready to Schedule to Scheduled',
      'Reschedule rate',
      'Cancellation rate by source',
      'No-Show rate',
      'Scheduling throughput per period',
    ],
    valueCreated: 'The scheduler converts verified referral form into a committed appointment, keeps appointment records accurate through changes, and ensures no booking disappears without an explicit outcome.',
    learningMoments: [
      {
        id: 'scheduler_review_ready',
        teaches: 'What Ready to Schedule means and why only ready referrals should be scheduled.',
        prevents: 'Scheduling referrals that still have missing information or unresolved rework.',
        successLooksLike: 'Only referrals with complete information receive appointment slots.',
      },
      {
        id: 'scheduler_complete_booking',
        teaches: 'What appointment metadata is required and why each field matters.',
        prevents: 'Incomplete bookings that lack a date, specialist, or location.',
        successLooksLike: 'Every Scheduled referral has a complete appointment record with all required details.',
      },
      {
        id: 'scheduler_return_to_intake',
        teaches: 'When and how to return a referral to Intake because booking cannot proceed.',
        prevents: 'Scheduling a referral that is incomplete, incorrect, or not actually ready.',
        successLooksLike: 'The referral returns to Intake with a clear blocker reason and remains visible and recoverable.',
      },
      {
        id: 'scheduler_reschedule',
        teaches: 'How to change booking details while preserving the referral and its history.',
        prevents: 'Losing appointment history or creating duplicate referrals when a booking changes.',
        successLooksLike: 'The referral keeps its identity and the previous appointment details are preserved in events.',
      },
      {
        id: 'scheduler_cancel_patient',
        teaches: 'How to record that a patient cancelled and why the source matters.',
        prevents: 'Patient-cancelled appointments disappearing as if they were never booked.',
        successLooksLike: 'The cancellation is visible in Referral Outcomes with the patient as the recorded source.',
      },
      {
        id: 'scheduler_cancel_clinic',
        teaches: 'How to record that the clinic cancelled and route the referral back for rebooking.',
        prevents: 'Clinic-side cancellations disappearing from the queue.',
        successLooksLike: 'The referral returns to Ready to Schedule and the clinic cancellation is recorded.',
      },
      {
        id: 'scheduler_no_show',
        teaches: 'How to mark a missed appointment without treating it as a completed visit.',
        prevents: 'A missed appointment being treated as a completed visit or Closed Loop.',
        successLooksLike: 'No-Show is recorded as an explicit appointment outcome.',
      },
    ],
  },

  orientationCard: {
    loggedInAs: ROLE_DISPLAY[ROLES.SCHEDULER].name,
    yourJob: 'Convert Ready to Schedule referrals into confirmed appointments, then keep appointment records accurate when bookings change.',
    mainWorkspace: {
      label: 'Scheduling',
      route: '/scheduling',
    },
    can: [
      'Review referrals ready for scheduling',
      'Choose an appointment date',
      'Assign a specialist or location when required',
      'Mark referrals as Scheduled',
      'Return blocked referrals to Intake',
      'Reschedule booked appointments',
      'Record patient cancellations',
      'Record clinic cancellations',
      'Mark appointments as No-Show',
    ],
    cannot: [
      'Create rework requests directly',
      'Change clinical Gate decisions',
      'Perform triage overrides',
      'Close the referral loop',
      'Edit Pack criteria or clinical answers',
    ],
    mainSuccessAction: 'Every Ready to Schedule referral should either become Scheduled, return to Intake with a blocker reason, or have every appointment change clearly accounted for as Rescheduled, Cancelled, or No-Show.',
    noChaosRule: 'No ready referral or booked appointment should disappear between triage, booking, and appointment outcome. A referral returned to Intake must remain visible, owned, and recoverable.',
  },

  roleClarityQuestions: {
    whoAmI: 'I am the Scheduler.',
    whatAmIHereToDo: 'I turn Ready to Schedule referrals into confirmed appointments and keep appointment records accurate through reschedules, cancellations, and no-shows.',
    whatCanITouch: 'I can book appointments, return referrals to Intake when they are not ready, reschedule, record patient or clinic cancellations, and mark no-shows.',
    whatCanINotTouch: 'I cannot change clinical decisions, create rework requests directly, perform triage overrides, or close the referral loop.',
    whatActionCreatesProgress: 'Booking an appointment moves the referral to Scheduled. Returning to Intake flags a blocker so the file can be corrected. Reschedule, cancel, and no-show keep appointment records accurate.',
    whatFormMustExistAfterIAct: 'After booking, the workflow state reflects Scheduled with appointment metadata. After any appointment change, a workflow event records what happened.',
  },

  guidedModules: [
    {
      moduleId: 'scheduling_queue',
      title: 'Scheduling Queue',
      route: '/scheduling',
      trigger: 'route',
      sector: SECTORS.SCHEDULING,
      role: ROLES.SCHEDULER,
      steps: [
        {
          stepId: 'scheduler_queue_overview',
          title: 'Scheduling Queue',
          body: 'Use the Scheduling Queue to find referrals that cleared intake and triage and now need a confirmed appointment.\n\nReady to Schedule means the referral is ready for booking. Review the referral, confirm booking details, and move it into Scheduled.\n\nDo not change the Gate decision or triage decision.',
          teaches: 'Find referrals ready for booking.',
          prevents: 'Letting a triage-cleared referral sit without a scheduling action.',
          checkpoint: 'User can identify the Ready to Schedule tab and explain what Ready to Schedule means.',
          focusTarget: 'scheduling-ready-tab',
          completionType: 'manual',
        },
        {
          stepId: 'scheduler_queue_tabs',
          title: 'Queue Tabs',
          body: 'Use the tabs to separate scheduling work by stage.\n\nReady to Schedule holds referrals waiting for a booking action.\n\nScheduled holds referrals that already have an appointment booked.\n\nReferral Outcomes holds appointment results, such as No-Show, Cancelled by Patient, or Cancelled by Clinic.',
          teaches: 'Separate referrals waiting for booking from referrals already booked or already resolved by an appointment outcome.',
          prevents: 'Mixing ready referrals, booked appointments, and appointment outcomes into one unclear list.',
          checkpoint: 'User can explain the difference between Ready to Schedule, Scheduled, and Referral Outcomes.',
          focusTarget: 'scheduling-ready-tab',
          completionType: 'manual',
        },
        {
          stepId: 'scheduler_open_referral',
          title: 'Open a Referral',
          body: 'Scan each referral card before you open it. Check the specialty, pathway, referral reference, priority, received date, and assignment status.\n\nClick Review to open the referral and take the next scheduling action.\n\nOpening the referral is where booking work begins. The queue card helps you choose what to open first.',
          teaches: 'Open the right referral before taking booking action.',
          prevents: 'Acting on the wrong referral or missing priority/context before booking.',
          checkpoint: 'User can click Review on a referral card and explain that detailed booking starts after opening it.',
          focusTarget: 'scheduling-first-referral',
          completionType: 'manual',
        },
      ],
    },
    {
      moduleId: 'scheduling_actions',
      title: 'Book a Referral',
      route: '/scheduling',
      trigger: 'context',
      contextKey: 'schedulingDrawerOpen',
      sector: SECTORS.SCHEDULING,
      role: ROLES.SCHEDULER,
      steps: [
        {
          stepId: 'scheduler_referral_summary',
          title: 'Referral Summary',
          body: 'Use Referral Summary to confirm the referral\'s core identity before booking.\n\nCheck the outcome, specialty, Pack, submission time, and submitter.\n\nDo not change the Gate outcome, specialty, Pack, or submitted details.',
          teaches: 'Confirm the referral identity before taking booking action.',
          prevents: 'Booking the wrong pathway or treating the Gate decision as editable.',
          checkpoint: 'User can point to the referral outcome, specialty, Pack, and submitted details.',
          focusTarget: 'referral-summary-section',
          completionType: 'manual',
        },
        {
          stepId: 'scheduler_workflow_state',
          title: 'Workflow State',
          body: 'Use Workflow State to confirm where the referral sits in the operational pipeline.\n\nFor scheduling, the key status is Ready to Schedule. That means intake and triage cleared the referral for booking.\n\nCheck the priority, owner, stage, and last updated time before you act.',
          teaches: 'Confirm the referral is ready for scheduler action.',
          prevents: 'Booking a referral that is not actually in the scheduling stage.',
          checkpoint: 'User can confirm the referral is Ready to Schedule before booking.',
          focusTarget: 'workflow-state-section',
          completionType: 'manual',
        },
        {
          stepId: 'scheduler_referral_ping',
          title: 'Referral Ping',
          body: 'Use Referral Ping when scheduling cannot continue without another role responding.\n\nReferral Pings create one referral-bound action thread between you and the receiving user.\n\nUse them to: return blockers to Intake, ask Triage for clarification, coordinate specialist scheduling questions, confirm referral readiness, or communicate operational follow-up tied to this referral.\n\nReferral Pings are not chat and not document exchange.\n\nKeep replies short, operational, and free of PHI.\n\nThe referral itself remains the work surface. Use Open Referral to complete the actual scheduling work.',
          teaches: 'Referral Pings coordinate scheduling-related referral action between users.',
          prevents: 'Sending PHI, documents, filenames, or trying to complete scheduling workflow inside replies.',
          checkpoint: 'User can explain when to use a Referral Ping to coordinate with another role.',
          focusTarget: 'scheduler_referral_ping',
          completionType: 'manual',
        },
        {
          stepId: 'scheduler_missing_items',
          title: 'Missing Items',
          body: 'Check Missing Items before booking.\n\nA Ready to Schedule referral should not have unresolved blockers. If something is missing, incorrect, expired, unreadable, or blocking booking, do not force the appointment.\n\nUse Return to Intake so Intake can resolve the blocker through the correct rework path.',
          teaches: 'Check for blockers before booking.',
          prevents: 'Scheduling a referral that still needs intake cleanup.',
          checkpoint: 'User can explain when missing items should trigger Return to Intake.',
          focusTarget: 'missing-items-section',
          completionType: 'manual',
        },
        {
          stepId: 'scheduler_patient_contact',
          title: 'Patient & Contact',
          body: 'Use Patient & Contact to coordinate the appointment with the patient.\n\nCheck the contact details needed to confirm booking information.\n\nDo not edit clinical answers, Gate decisions, triage decisions, or Pack criteria from here.',
          teaches: 'Use patient and contact details to support booking.',
          prevents: 'Mixing scheduling coordination with clinical decision editing.',
          checkpoint: 'User can find the patient/contact section and explain its scheduling purpose.',
          focusTarget: 'patient-contact-section',
          completionType: 'manual',
        },
        {
          stepId: 'scheduler_attachments',
          title: 'Attachments',
          body: 'Check Attachments before booking.\n\nConfirm that required booking-supporting documents are present and usable. If an attachment is missing, unreadable, expired, or clearly blocking booking, return the referral to Intake.\n\nDo not create the rework request yourself. Intake owns the rework path.',
          teaches: 'Use attachments to confirm the file can support booking.',
          prevents: 'Booking when required documents are not usable.',
          checkpoint: 'User can open or identify the attachments area and know when to return the referral.',
          focusTarget: 'attachments-section',
          completionType: 'manual',
        },
        {
          stepId: 'scheduler_event_history',
          title: 'Event History',
          body: 'Read Event History before taking action.\n\nUse it to see how the referral reached Scheduling, including status changes, triage updates, assignment, and prior workflow movement.\n\nEvent History is read-only context. Do not rewrite history.',
          teaches: 'Read the referral\'s movement before taking the next action.',
          prevents: 'Acting without knowing whether the referral was triaged, returned, updated, or reassigned.',
          checkpoint: 'User can find Event History and explain why it matters before booking.',
          focusTarget: 'event-history-section',
          completionType: 'manual',
        },
        {
          stepId: 'scheduler_book_appointment',
          title: 'Book Appointment',
          body: 'Use Book Appointment when the referral is ready and booking can proceed.\n\nChoose the appointment date, specialist, and location when required. Confirming the booking moves the referral into Scheduled and creates a clear appointment record.',
          teaches: 'Book Appointment converts a Ready to Schedule referral into a Scheduled referral.',
          prevents: 'Leaving a ready referral unbooked or marking it Scheduled without appointment details.',
          checkpoint: 'User can click Book Appointment and understand what details must be saved.',
          focusTarget: 'book-appointment-action',
          completionType: 'manual',
        },
        {
          stepId: 'scheduler_return_to_intake',
          title: 'Return to Intake',
          body: 'Use Return to Intake when the referral reached Scheduling but cannot be booked because the file is incomplete, incorrect, expired, unreadable, or operationally blocking the appointment.\n\nDo not create the rework request or decide the clinical fix. Flag the scheduling blocker and return the referral so Intake can resolve it through the correct rework path.\n\nThe referral must remain visible, owned, and recoverable. It must not disappear from Scheduling without appearing in Intake.',
          teaches: 'Return blocked referrals to the team that owns completeness.',
          prevents: 'Forcing a bad booking or letting a scheduling blocker disappear.',
          checkpoint: 'User can choose Return to Intake and explain why the referral leaves Scheduling but remains recoverable.',
          focusTarget: 'scheduling-return-to-intake',
          completionType: 'manual',
        },
      ],
    },
    {
      moduleId: 'scheduling_scheduled',
      title: 'Scheduled Appointments',
      route: '/scheduling',
      trigger: 'context',
      contextKey: 'schedulingTabScheduled',
      sector: SECTORS.SCHEDULING,
      role: ROLES.SCHEDULER,
      steps: [
        {
          stepId: 'scheduler_scheduled_tab',
          title: 'Scheduled Tab',
          body: 'Use the Scheduled tab to manage referrals that already have a confirmed appointment.\n\nCheck this tab when a booked appointment needs to change before the appointment date or needs an outcome after the appointment date.',
          teaches: 'Manage booked appointments from the Scheduled tab.',
          prevents: 'Treating Scheduled as the end of scheduler responsibility.',
          checkpoint: 'User can open the Scheduled tab and choose a booked appointment to modify.',
          focusTarget: 'scheduling-scheduled-tab',
          completionType: 'manual',
        },
        {
          stepId: 'scheduler_open_modify',
          title: 'Open Modify Appointment',
          body: 'Scan the scheduled referral card before changing the appointment. Check the specialty, pathway, referral reference, appointment date, specialist, and booking status.\n\nClick Modify to open the appointment action modal.\n\nUse Modify when a booked appointment needs to be rescheduled, cancelled, or marked as No-Show.',
          teaches: 'Open Modify before changing a booked appointment.',
          prevents: 'Changing the wrong appointment or acting without checking the booking context.',
          checkpoint: 'User can click Modify on a scheduled referral and understand that the next cards explain the available appointment-change actions.',
          focusTarget: 'scheduling-modify-action',
          completionType: 'manual',
        },
        {
          stepId: 'scheduler_reschedule',
          title: 'Reschedule',
          body: 'Use Reschedule when the appointment still needs to happen, but the date, time, specialist, or location changes.\n\nUpdate the booking details and keep the referral connected to the same workflow.\n\nDo not create a new referral. Do not erase the existing booking history.',
          teaches: 'Change the appointment while preserving the referral.',
          prevents: 'Losing appointment history when a booking changes.',
          checkpoint: 'User can choose Reschedule and explain that the referral remains active.',
          focusTarget: 'scheduling-modify-action',
          completionType: 'manual',
        },
        {
          stepId: 'scheduler_cancel_patient',
          title: 'Cancel — Patient',
          body: 'Use Cancel — Patient when the patient cancels the booked appointment.\n\nRecord the patient as the cancellation source. Do not mark the appointment Seen. Do not close the referral loop.\n\nKeep the cancellation visible in the correct rebooking or outcome path.',
          teaches: 'Record patient cancellations without hiding the appointment history.',
          prevents: 'A patient-cancelled appointment looking like it was never booked.',
          checkpoint: 'User can choose Cancel — Patient and explain why the cancellation source matters.',
          focusTarget: 'scheduling-modify-action',
          completionType: 'manual',
        },
        {
          stepId: 'scheduler_cancel_clinic',
          title: 'Cancel — Clinic',
          body: 'Use Cancel — Clinic when the clinic cancels or cannot honor the booked appointment.\n\nRecord the clinic as the cancellation source. Preserve the referral and the original booking history.\n\nRebook if the patient still needs the appointment, or keep the cancellation visible in the correct outcome path.',
          teaches: 'Record clinic cancellations without losing the referral.',
          prevents: 'Clinic-side cancellations disappearing from the scheduling record.',
          checkpoint: 'User can choose Cancel — Clinic and explain how it differs from Cancel — Patient.',
          focusTarget: 'scheduling-modify-action',
          completionType: 'manual',
        },
        {
          stepId: 'scheduler_no_show',
          title: 'No-Show',
          body: 'Use No-Show when the patient misses the scheduled appointment.\n\nRecord No-Show as the appointment outcome. Do not mark the appointment Seen. Do not close the referral loop.\n\nFollow the current rebooking path if the referral should be scheduled again.',
          teaches: 'Record missed appointments as No-Show, not Seen.',
          prevents: 'A missed appointment being treated as completed.',
          checkpoint: 'User can choose No-Show and explain that the referral was not completed.',
          focusTarget: 'scheduling-modify-action',
          completionType: 'manual',
        },
        {
          stepId: 'scheduler_referral_outcomes',
          title: 'Referral Outcomes',
          body: 'Use Referral Outcomes to confirm what happened after booking.\n\nThis tab keeps appointment outcomes visible, such as No-Show, Cancelled by Patient, or Cancelled by Clinic.\n\nCheck this tab after a scheduled appointment changes or reaches an endpoint.',
          teaches: 'Use Referral Outcomes to verify that appointment results stayed visible.',
          prevents: 'Losing the final appointment result after the referral leaves the Scheduled tab.',
          checkpoint: 'User can open Referral Outcomes and explain what happened to a booked referral.',
          focusTarget: 'scheduling-outcomes-tab',
          completionType: 'manual',
        },
      ],
    },
    {
      moduleId: 'scheduling_queue_health',
      title: 'Queue Health',
      route: '/scheduler-home',
      trigger: 'route',
      sector: SECTORS.SCHEDULING,
      role: ROLES.SCHEDULER,
      steps: [
        {
          stepId: 'scheduler_queue_health_overview',
          title: 'Queue Health',
          body: 'Use Queue Health to check whether scheduling work is moving or getting stuck.\n\nCheck live scheduling pressure before you work the queue. Use the headline to confirm the live date/time context and the number of active referrals included in the view.',
          teaches: 'Use Queue Health to spot scheduling pressure before it becomes hidden backlog.',
          prevents: 'Working referrals blindly without seeing where the queue is stuck.',
          checkpoint: 'User can open Queue Health and explain that it shows live scheduling health, not individual referral booking steps.',
          focusTarget: 'queue-health-header',
          completionType: 'manual',
        },
        {
          stepId: 'scheduler_fires_section',
          title: 'Fires — Stuck Items',
          body: 'Use Fires to find referrals breaching or approaching scheduling SLA.\n\nPrioritize these items first because they are already stuck or close to stuck.\n\nWork the fires before treating total volume as the priority.',
          teaches: 'Use Fires to find the most time-sensitive scheduling problems.',
          prevents: 'Ignoring breached referrals while working easier, newer, or lower-risk items first.',
          checkpoint: 'User can identify the Fires section and explain why it comes before total volume.',
          focusTarget: 'fires-section',
          completionType: 'manual',
        },
        {
          stepId: 'scheduler_ready_fire',
          title: 'Ready to Schedule Fire',
          body: 'Use Ready to Schedule Fire to find referrals that cleared triage but waited too long for booking.\n\nThis is the routine/non-urgent scheduling fire: referrals past the 48-hour scheduling SLA without an appointment.\n\nPrioritize these ready referrals because they are stuck and need booking attention first.',
          teaches: 'Prioritize ready referrals that are breaching the booking SLA.',
          prevents: 'Non-urgent referrals sitting too long without an appointment.',
          checkpoint: 'User can explain that this card means routine ready referrals are waiting too long for booking.',
          focusTarget: 'ready-to-schedule-fire-card',
          completionType: 'manual',
        },
        {
          stepId: 'scheduler_urgent_fire',
          title: 'Urgent Waiting Fire',
          body: 'Use Urgent Waiting Fire to find referrals carrying urgent scheduling pressure.\n\nThis is the urgent/high-priority scheduling fire: urgent outcome referrals or High priority referrals past the 4-hour urgent SLA without an appointment.\n\nCheck urgent scheduling work before routine booking work.',
          teaches: 'Prioritize urgent and High priority referrals that are breaching the urgent scheduling SLA.',
          prevents: 'Urgent or High priority referrals sitting too long without an appointment.',
          checkpoint: 'User can explain that this card means urgent or High priority referrals are waiting too long for booking.',
          focusTarget: 'urgent-waiting-fire-card',
          completionType: 'manual',
        },
        {
          stepId: 'scheduler_engine_section',
          title: 'Engine — Total Volume',
          body: 'Use Engine to understand the total scheduling load moving through the system.\n\nCompare total volume against Fires to see whether stuck work is isolated or part of a larger capacity problem.\n\nDo not confuse total volume with breached SLA work.',
          teaches: 'Use Engine to understand total scheduling pressure.',
          prevents: 'Confusing total volume with breached SLA work.',
          checkpoint: 'User can explain the difference between Fires and Engine.',
          focusTarget: 'engine-section',
          completionType: 'manual',
        },
        {
          stepId: 'scheduler_ready_volume',
          title: 'Ready to Schedule Volume',
          body: 'Use Ready to Schedule Volume to see all referrals that are triaged and still need an appointment.\n\nThis is the scheduler\'s total booking workload. Some referrals may still be healthy and within SLA. Others may also appear in Fires if they are breaching the scheduling clock.',
          teaches: 'Use Ready to Schedule volume to understand total booking demand.',
          prevents: 'Only reacting to fires while ignoring the size of the upcoming booking queue.',
          checkpoint: 'User can explain that Ready to Schedule volume is total booking demand, not only breached referrals.',
          focusTarget: 'ready-to-schedule-volume-card',
          completionType: 'manual',
        },
        {
          stepId: 'scheduler_urgent_volume',
          title: 'Urgent Queue Volume',
          body: 'Use Urgent Queue Volume to see all urgent or High priority referrals waiting in the scheduling system.\n\nNot every urgent referral is breached yet, but urgent work carries higher priority and a shorter SLA window.\n\nWatch this volume before it turns into an urgent fire.',
          teaches: 'Use Urgent Queue to track high-priority scheduling demand.',
          prevents: 'Missing urgent referrals before they become SLA fires.',
          checkpoint: 'User can explain that Urgent Queue is total urgent demand, while Urgent Waiting Fire is the breached urgent subset.',
          focusTarget: 'urgent-queue-volume-card',
          completionType: 'manual',
        },
        {
          stepId: 'scheduler_sla_refresh',
          title: 'SLA Clock and Refresh',
          body: 'Use the SLA clock note to understand how Queue Health measures waiting time.\n\nThe scheduling clock starts when triage is completed, then measures the wait using the tenant\'s business-hours SLA.\n\nClick Refresh to reload the latest queue health picture before deciding what to work next.',
          teaches: 'Read Queue Health as a live scheduling signal based on workflow state and SLA timing.',
          prevents: 'Making queue decisions from stale counts or misunderstanding when the scheduling clock starts.',
          checkpoint: 'User can explain that the scheduling SLA starts after triage completion and that Refresh updates the health view.',
          focusTarget: 'queue-health-footer-or-refresh',
          completionType: 'manual',
        },
      ],
    },
  ],

  learningScenarios: [
    {
      scenarioId: 'scheduler_book_standard',
      title: 'Standard appointment booking',
      description: 'A Ready to Schedule referral is assigned a date, specialist, and location. The referral moves to Scheduled.',
      expectedOutcome: 'SCHEDULED',
      teachesLearningMoment: 'scheduler_complete_booking',
      setup: { referralReady: true, slotAvailable: true },
    },
    {
      scenarioId: 'scheduler_return_to_intake',
      title: 'Referral returned to Intake',
      description: 'A Ready to Schedule referral cannot be booked because the file is incomplete or incorrect. The scheduler returns the referral to Intake with a clear blocker reason.',
      expectedOutcome: 'NEEDS_INFO',
      teachesLearningMoment: 'scheduler_return_to_intake',
      setup: { referralReady: true, blockerFound: true },
    },
    {
      scenarioId: 'scheduler_reschedule',
      title: 'Appointment rescheduled',
      description: 'A Scheduled referral needs a new date or time. The scheduler reschedules the appointment, preserving the referral and recording new details.',
      expectedOutcome: 'SCHEDULED',
      teachesLearningMoment: 'scheduler_reschedule',
      setup: { appointmentScheduled: true, detailsChanged: true },
    },
    {
      scenarioId: 'scheduler_cancel_patient',
      title: 'Patient cancels the appointment',
      description: 'The patient cancels a Scheduled appointment. The scheduler records the patient as the cancellation source. The referral appears in Referral Outcomes.',
      expectedOutcome: 'CANCELLED',
      teachesLearningMoment: 'scheduler_cancel_patient',
      setup: { appointmentScheduled: true, cancelSource: 'patient' },
    },
    {
      scenarioId: 'scheduler_cancel_clinic',
      title: 'Clinic cancels the appointment',
      description: 'The clinic cancels a Scheduled appointment. The scheduler records the clinic as the cancellation source. The referral returns to Ready to Schedule for rebooking.',
      expectedOutcome: 'READY_TO_SCHEDULE',
      teachesLearningMoment: 'scheduler_cancel_clinic',
      setup: { appointmentScheduled: true, cancelSource: 'clinic' },
    },
    {
      scenarioId: 'scheduler_no_show',
      title: 'Patient misses the appointment',
      description: 'The patient does not attend the Scheduled appointment. The scheduler marks it as No-Show. The referral appears in Referral Outcomes as a missed appointment.',
      expectedOutcome: 'NO_SHOW',
      teachesLearningMoment: 'scheduler_no_show',
      setup: { appointmentScheduled: true, patientAbsent: true },
    },
  ],
};

// ============================================================================
// TRIAGE NURSE — Learning map
// ============================================================================

const TRIAGE_NURSE_MAP = {
  role: ROLES.TRIAGE_NURSE,
  roleDisplayName: ROLE_DISPLAY[ROLES.TRIAGE_NURSE].name,

  workflow: {
    id: 'triage-queue',
    label: 'Triage Queue',
    description: 'The clinical review queue where referrals are assessed for exceptions, priority escalation, or holds before moving to scheduling.',
  },

  valueNeeded: 'Safety — Triage needs to catch clinical exceptions, escalate priority when warranted, and clear safe referrals toward scheduling.',

  objective: 'Every referral in Pending Triage must be reviewed, and either cleared for scheduling, placed on hold, or escalated.',

  roleMission: 'Review referrals for clinical exceptions, set priority, place holds when needed, and clear safe referrals toward scheduling.',

  roleFormMap: {
    intent: 'Apply bounded clinical exception handling before referrals move to scheduling.',
    decision: 'Does this referral require an override, a priority escalation, or a temporary hold?',
    criteria: [
      'Existing referral form and Gate outcome',
      'Pack authority and clinical criteria',
      'Exception policy for overrides',
      'Priority escalation rules',
      'Hold conditions',
    ],
    activities: [
      'Review the Triage Queue',
      'Assess referral signals',
      'Set or escalate priority',
      'Place referrals on hold when needed',
      'Clear safe referrals toward scheduling',
    ],
    checkpoints: [
      'Referral is reviewed and triaged',
      'Priority is set appropriately',
      'Holds have a documented reason',
      'Override has a required reason and note',
      'Original Gate decision is preserved in audit',
    ],
    action: 'Review referral, set priority, place hold if needed, or clear for scheduling.',
    form: 'workflow/state receives triage exception metadata and workflowEvents are appended.',
    metrics: [
      'Referrals pending triage',
      'Referrals currently on hold',
      'Referrals triaged today',
      'Override rate',
    ],
    valueCreated: 'Triage does not re-author the referral. Triage catches exceptions and clears safe referrals so scheduling can proceed.',
    learningMoments: [
      {
        id: 'triage_review_signals',
        teaches: 'How to read referral card signals and what each badge means.',
        prevents: 'Missing a priority escalation or override because the card signals were not understood.',
        successLooksLike: 'The nurse reads specialty, outcome, status, and priority badges correctly before acting.',
      },
      {
        id: 'triage_clear_safe',
        teaches: 'When a referral is safe to clear toward scheduling without further action.',
        prevents: 'Holding safe referrals unnecessarily, which delays scheduling.',
        successLooksLike: 'Routine referrals with no exceptions move through triage promptly.',
      },
    ],
  },

  orientationCard: {
    loggedInAs: ROLE_DISPLAY[ROLES.TRIAGE_NURSE].name,
    yourJob: 'Review referrals for clinical exceptions, escalate priority when needed, place holds with a documented reason, and clear safe referrals toward scheduling.',
    mainWorkspace: {
      label: 'Triage Queue',
      route: '/triage-queue',
    },
    can: [
      'Review referrals in Pending Triage',
      'Set or escalate priority',
      'Place referrals on hold with a reason',
      'Override the Gate decision with a documented reason',
      'Add triage notes',
      'Clear safe referrals toward scheduling',
    ],
    cannot: [
      'Rewrite the original referral',
      'Change the Gate outcome',
      'Re-author clinical answers',
      'Schedule appointments',
      'Close the referral loop',
      'Edit Pack criteria',
    ],
    mainSuccessAction: 'Every referral in Pending Triage must leave with a clear next action:\n\nCleared — safe to schedule\nOn Hold — waiting for a documented reason to be resolved\nPriority Escalated — urgency is raised with justification',
    noChaosRule: 'No referral should sit in Pending Triage without review.\n\nTriage catches exceptions.\nIt does not re-author the referral.',
  },

  roleClarityQuestions: [
    {
      question: 'Can you change the Gate outcome of a referral?',
      correctAnswer: false,
      explanation: 'The Gate outcome is set by the Pack and engine. Triage can override with a documented reason, but the original outcome is always preserved in audit.',
    },
    {
      question: 'Is your primary workspace the Triage Queue?',
      correctAnswer: true,
      explanation: 'The Triage Queue is where you review referrals, set priority, place holds, and clear safe referrals toward scheduling.',
    },
    {
      question: 'Can you schedule appointments directly?',
      correctAnswer: false,
      explanation: 'Scheduling is handled by the Scheduler role. Triage clears referrals so they can be scheduled.',
    },
  ],

  guidedModules: [
    {
      moduleId: 'triage_queue_intro',
      title: 'Understanding the Triage Queue',
      route: '/triage-queue',
      sector: SECTORS.TRIAGE,
      role: ROLES.TRIAGE_NURSE,
      steps: [
        {
          stepId: 'triage_your_queue',
          title: 'Your Triage Queue',
          body: 'The Triage Queue is your main workspace.\n\nIt has three tabs:\n\n• Pending Triage — referrals waiting for your review\n• On Hold — referrals paused with a documented reason\n• Triaged — referrals already reviewed and cleared\n\nYour job is to review each referral, spot clinical exceptions, escalate priority when needed, place holds with a reason, and clear safe referrals toward scheduling.',
          teaches: 'What each Triage Queue tab contains and what action you are responsible for.',
          prevents: 'Skipping the queue overview and jumping into individual referrals without understanding the full picture.',
          focusTarget: 'triage-queue-title',
        },
        {
          stepId: 'triage_pending_tab',
          title: 'Pending Triage',
          body: 'Pending Triage is your active worklist.\n\nEach referral card shows:\n\n• Specialty and Pack — the clinical pathway this referral entered\n• Referral ID and date — the referral record and when it arrived\n• Referral Status badge — the current workflow status\n• Outcome badge — the Gate decision, usually Routine or Urgent\n• Status badge — where the referral sits in triage\n\nStart here. Review each referral, read the card signals, and decide what happens next: clear it, place it on hold, or escalate priority.',
          teaches: 'How to read the referral card signals in Pending Triage.',
          prevents: 'Missing important signals because the badge meanings were not understood.',
          focusTarget: 'triage-queue-tabs',
        },
        {
          stepId: 'triage_open_referral',
          title: 'Open a Referral',
          body: 'Pending Triage is not just a list. It is the starting point for clinical review.\n\nChoose one referral and open it to begin triage.\n\nInside the referral, you will review the clinical details, check the Gate decision, confirm whether the referral can move forward, and decide the next action.\n\nDo not make the triage decision from the list alone. The card gives you signals. The referral detail gives you the full review context.',
          teaches: 'Open a referral from Pending Triage to begin clinical review.',
          prevents: 'Clearing, holding, or escalating a referral based only on the queue card.',
          focusTarget: 'triage-queue-pending-list',
        },
      ],
    },
    {
      moduleId: 'triage_referral_review',
      title: 'Reviewing a Referral',
      route: '/triage-queue',
      sector: SECTORS.TRIAGE,
      role: ROLES.TRIAGE_NURSE,
      trigger: 'modal',
      modalId: 'triage-drawer',
      steps: [
        {
          stepId: 'triage_referral_detail',
          title: 'Referral Detail',
          body: 'This drawer gives you the full triage context for one referral.\n\nStart at the top. Confirm the Referral ID, Specialty, Pack, Gate Decision, Referral Status, Triage Status, and Priority.\n\nThe queue card gave you signals. The referral detail gives you the review context.\n\nDo not make the triage decision from the queue card alone. Open the referral and review the full detail before acting.',
          teaches: 'Use the referral detail drawer as the source for triage review.',
          prevents: 'Clearing, holding, or escalating a referral based only on the queue card.',
          focusTarget: 'triage-drawer-detail',
        },
        {
          stepId: 'triage_receipt_review',
          title: 'Referral Receipt',
          body: 'The Referral Receipt shows what arrived from the Gate.\n\nReview the header, patient context, referral summary, decision map, criteria, attachments, rework requests, and workflow context.\n\nThis is read-only triage context. Use it to understand what the Gate decided and what information supported that decision.\n\nDo not rewrite the original referral. Triage reviews the form that already exists.',
          teaches: 'Read the referral receipt before taking triage action.',
          prevents: 'Treating triage as a second referral authoring step.',
          focusTarget: 'triage-drawer-receipt',
        },
        {
          stepId: 'triage_decision_map',
          title: 'Decision Map',
          body: 'The Decision Map shows what the Gate decided when the referral was submitted and where the referral now sits in triage.\n\nCheck:\n\n• Gate Decision — the original engine decision from the Pack\n• Gate Override — whether the referral was submitted through an override path\n• Referral Status — the current workflow status\n• Triage Status — the current triage review state\n• Priority — the current triage priority\n\nIf Gate Override is None, the referral followed the normal Gate decision.\n\nIf Gate Override is present, review the override reason before taking triage action. The override explains why the referral moved forward despite the normal Gate result.\n\nTriage does not erase the Gate decision or the Gate override. Triage reviews them as context before deciding the next action.',
          teaches: 'Check the Gate decision, Gate override, referral status, triage status, and priority before acting.',
          prevents: 'Missing that a referral reached triage through a Gate override or already has a triage state.',
          focusTarget: 'triage-drawer-decision-map',
        },
        {
          stepId: 'triage_criteria_check',
          title: 'Criteria & Completeness',
          body: 'Criteria & Completeness shows the clinical signals captured by the Gate.\n\nGreen checks show criteria that were captured.\n\nReview these criteria before clearing the referral. They tell you what information supported the Gate decision.\n\nIf something is missing, unclear, or unsafe to accept, do not force the referral forward. Place it on hold or return it to Intake for Rework.',
          teaches: 'Check the clinical criteria before clearing the referral.',
          prevents: 'Clearing a referral without understanding what criteria supported the Gate decision.',
          focusTarget: 'triage-drawer-criteria',
        },
        {
          stepId: 'triage_attachments',
          title: 'Attachments',
          body: 'Attachments show the documents available to support the referral.\n\nOpen attachments when they are needed to confirm the clinical context or check supporting evidence.\n\nIf no attachments are found, do not ignore it. Decide whether the referral can still be cleared from the available criteria, should be placed on hold, or should return to Intake for Rework.\n\nAttachments are evidence. Check them before completing triage when documents matter to the review.',
          teaches: 'Check whether supporting documents are available before clearing the referral.',
          prevents: 'Clearing a referral while missing documents that matter for review.',
          focusTarget: 'triage-drawer-attachments',
        },
        {
          stepId: 'triage_rework_requests',
          title: 'Rework Requests',
          body: 'Rework Requests show whether Intake already asked for missing information.\n\nIf there are no rework requests, the referral reached triage without an active missing-info chase.\n\nIf a rework request exists, review what was requested before deciding the next triage action.\n\nUse this section to avoid duplicating work, missing unresolved issues, or sending the referral forward before requested information is resolved.',
          teaches: 'Check whether missing information is already being worked.',
          prevents: 'Overlooking an active rework issue or creating duplicate follow-up.',
          focusTarget: 'triage-drawer-rework',
        },
        {
          stepId: 'triage_workflow_context',
          title: 'Workflow Context',
          body: 'Workflow Context shows where the referral came from and who handled it before triage.\n\nSubmitted By tells you which referrer submitted the referral.\n\nAssigned To shows the intake coordinator who claimed ownership in the Workqueue.\n\nIf Assigned To shows Unassigned, no intake coordinator has claimed it yet.\n\nUse this section to understand the referral\'s operational history before saving, holding, returning, or completing triage.\n\nThis does not make triage responsible for intake ownership. It gives triage context about who handled the referral before it reached review.',
          teaches: 'Check submission and intake ownership context before changing the referral\'s triage state.',
          prevents: 'Moving a referral without understanding who submitted it or whether it was claimed in Workqueue.',
          focusTarget: 'triage-drawer-workflow',
        },
        {
          stepId: 'triage_actions_overview',
          title: 'Triage Actions',
          body: 'After you review the referral detail, receipt, decision map, criteria, attachments, rework requests, and workflow context, choose the action that fits the referral.\n\nYou can:\n\n• Set or escalate priority\n• Place the referral on hold with a documented reason\n• Add a triage note if the field is available\n• Override the Gate decision with a documented reason\n• Return the referral to Intake for Rework if Intake must resolve something\n• Save triage updates\n• Mark the referral complete when it is safe to move forward\n\nEvery action should explain what happens next.',
          teaches: 'Choose the correct triage action based on the full referral review.',
          prevents: 'Leaving the referral in triage without a clear next action.',
          focusTarget: 'triage-drawer-actions',
        },
        {
          stepId: 'triage_referral_ping',
          title: 'Referral Ping',
          body: 'Use Referral Ping when another role must respond before triage can continue.\n\nReferral Pings create one referral-bound action thread between you and the receiving user.\n\nUse them to: ask Intake for missing clarification, respond to specialist questions, coordinate scheduling blockers, request follow-up on referral movement, or communicate operational next steps tied to this referral.\n\nReferral Pings are not chat and not document exchange.\n\nKeep replies short, operational, and free of PHI.\n\nThe referral itself remains the work surface. Use Open Referral to complete the actual referral work.',
          teaches: 'Referral Pings coordinate referral-level action between users.',
          prevents: 'Sending PHI, documents, filenames, or trying to complete clinical workflow inside replies.',
          focusTarget: 'triage_review_referral_ping',
        },
        {
          stepId: 'triage_clear_next',
          title: 'Clear Next State',
          body: 'Every referral must leave triage with one clear next state.\n\nMark Complete — the referral is clinically cleared and can move toward scheduling.\n\nPlace on Hold — the referral needs a documented issue resolved before triage can finish.\n\nReturn to Intake for Rework — Intake must fix or collect something before the referral can move forward.\n\nSave — keeps your triage updates without falsely completing the referral.\n\nDo not mistake Save for completion. Save records updates. Mark Complete clears the referral for the next workflow step.',
          teaches: 'End triage with the right next state.',
          prevents: 'Saving partial review work and mistaking it for triage completion.',
          focusTarget: 'triage-drawer-footer',
        },
      ],
    },
    {
      moduleId: 'triage_queue_health',
      title: 'Queue Health',
      route: '/triage-queue-health',
      trigger: 'route',
      steps: [
        {
          stepId: 'queue_health_overview',
          title: 'Queue Health Overview',
          body: 'Queue Health shows the real-time operational state of the triage queue.\n\nThis is not a list of individual referrals. It is a signal board.\n\nThe screen answers two questions:\n\n• Are any referrals stuck past their triage SLA?\n• How many referrals are waiting in each triage sub-category?\n\nUse this screen at the start of a shift, after returning from a break, or whenever you need a quick read on where the queue stands.\n\nEverything on this screen is derived from live workflow states and tenant SLA settings. Nothing here touches PHI.',
          teaches: 'Queue Health is a signal board, not a referral list.',
          prevents: 'Opening individual referrals from the wrong screen or confusing Queue Health with the triage worklist.',
          focusTarget: 'queue-health-header',
        },
        {
          stepId: 'queue_health_live_fires',
          title: 'Live Fires',
          body: 'Live Fires shows referrals that have exceeded their configured triage SLA.\n\nSLA is measured in business hours using the tenant\'s business-hours calendar from Impact Settings.\n\nIf any card in this section shows a count above zero, at least one referral in that sub-category has been waiting longer than the SLA allows.\n\nA pulsing dot next to the Live Fires heading means at least one stuck referral exists right now.\n\nIf all cards show zero, the green bar appears confirming all triage items are within SLA.',
          teaches: 'Live Fires highlights SLA breaches, not total volume.',
          prevents: 'Confusing stuck count with total queue size.',
          focusTarget: 'queue-health-live-fires',
        },
        {
          stepId: 'queue_health_stuck_urgent',
          title: 'Stuck Urgent',
          body: 'Stuck Urgent counts referrals with an Urgent outcome that have exceeded the urgent triage SLA.\n\nThese are referrals where the Gate flagged a red flag and the referral has been waiting in triage longer than the configured urgent SLA threshold.\n\nIf this card is red, at least one urgent referral needs immediate triage attention.\n\nClicking this card takes you to the Pending tab in the triage queue filtered for action.',
          teaches: 'Stuck Urgent means a red-flag referral has exceeded the urgent SLA.',
          prevents: 'Ignoring an urgent referral that has been waiting too long for triage.',
          focusTarget: 'queue-health-stuck-urgent',
        },
        {
          stepId: 'queue_health_stuck_routine',
          title: 'Stuck Routine',
          body: 'Stuck Routine counts referrals with a Routine outcome that have exceeded the routine triage SLA.\n\nThese referrals passed the Gate as complete and safe but have been waiting in triage longer than the configured routine SLA threshold.\n\nIf this card shows a count above zero, routine referrals are aging past the expected triage turnaround.\n\nClicking this card takes you to the Pending tab in the triage queue.',
          teaches: 'Stuck Routine means a complete referral has exceeded the routine triage SLA.',
          prevents: 'Letting routine referrals age silently while focusing only on urgent items.',
          focusTarget: 'queue-health-stuck-routine',
        },
        {
          stepId: 'queue_health_stuck_hold',
          title: 'Stuck on Hold',
          body: 'Stuck on Hold counts referrals that were placed on clinical hold and have exceeded the hold SLA.\n\nA referral on hold is waiting for a documented issue to be resolved before triage can finish. If the hold exceeds the configured SLA, the referral appears here.\n\nThis card does not mean the hold was wrong. It means the hold has lasted longer than the SLA expects.\n\nClicking this card takes you to the On Hold tab in the triage queue.',
          teaches: 'Stuck on Hold means a held referral has exceeded the hold SLA.',
          prevents: 'Forgetting referrals that were placed on hold and never revisited.',
          focusTarget: 'queue-health-stuck-hold',
        },
        {
          stepId: 'queue_health_within_sla',
          title: 'Within SLA',
          body: 'When all stuck counts are zero, a green bar appears confirming that every triage item is within SLA.\n\nThis is the target state. It means no referral in any sub-category has exceeded its configured triage SLA threshold.\n\nThe green bar disappears as soon as any referral crosses its SLA boundary.\n\nIf you see the green bar, the queue is healthy. If you do not, check the Live Fires cards above to find what needs attention.',
          teaches: 'The green bar confirms zero SLA breaches across all triage sub-categories.',
          prevents: 'Assuming the queue is healthy without checking the SLA signal.',
          focusTarget: 'queue-health-within-sla',
        },
        {
          stepId: 'queue_health_total_volume',
          title: 'Engine — Total Volume',
          body: 'Total Volume shows the raw count of referrals in each triage sub-category regardless of SLA status.\n\nUrgent Queue — referrals with an Urgent outcome waiting for triage.\n\nRoutine Queue — referrals with a Routine outcome waiting for triage.\n\nHold Queue — referrals currently on clinical hold.\n\nThese counts include both within-SLA and stuck referrals. They tell you how much work exists, not how urgent it is.\n\nUse Total Volume alongside Live Fires to understand both the size and the health of the queue.',
          teaches: 'Total Volume shows queue size. Live Fires shows queue health.',
          prevents: 'Confusing total volume with SLA status or treating all volume as equally urgent.',
          focusTarget: 'queue-health-total-volume',
        },
        {
          stepId: 'queue_health_act_from_signal',
          title: 'Act From the Signal',
          body: 'Queue Health gives you the signal. The triage queue is where you act.\n\nUse this screen to decide where to focus:\n\n• Red cards in Live Fires — go to the triage queue and address stuck referrals first.\n• Green bar — the queue is healthy. Check Total Volume to see remaining work.\n• High volume but no fires — the queue is large but nothing has breached SLA yet.\n\nDo not open individual referrals from this screen. Use the stuck cards to navigate to the right tab in the triage queue, then review referrals there.\n\nQueue Health is the dashboard. The triage queue is the workspace.',
          teaches: 'Read the signal here, then act in the triage queue.',
          prevents: 'Trying to triage referrals from the dashboard instead of the queue.',
          focusTarget: 'queue-health-page',
        },
      ],
    },
  ],

  learningScenarios: [
    {
      scenarioId: 'triage_clear_routine',
      title: 'Clear a routine referral',
      description: 'A referral with a ROUTINE outcome and no exceptions is reviewed and cleared for scheduling.',
      expectedOutcome: 'READY_TO_SCHEDULE',
      teachesLearningMoment: 'triage_clear_safe',
      setup: { referralRoutine: true, noExceptions: true },
    },
    {
      scenarioId: 'triage_place_hold',
      title: 'Place a referral on hold',
      description: 'A referral requires additional information or review before it can proceed. The nurse places it on hold with a documented reason.',
      expectedOutcome: 'ON_HOLD',
      teachesLearningMoment: 'triage_review_signals',
      setup: { requiresHold: true },
    },
  ],
};

// ============================================================================
// SPECIALIST — Reviews referrals, completes specialist actions, maintains Packs
// ============================================================================

const SPECIALIST_MAP = {
  role: ROLES.SPECIALIST,
  roleDisplayName: ROLE_DISPLAY[ROLES.SPECIALIST].name,

  workflow: {
    id: 'specialist-worklist',
    label: 'Specialist Worklist',
    description: 'The specialist workspace where scheduled and action-needed referrals are reviewed, acted on, and advanced toward closure.',
  },

  valueNeeded: 'Timely specialist review and action on every referral that reaches the worklist.',

  objective: 'Every scheduled or action-needed referral is reviewed with full Gate and workflow context, advanced through the allowed specialist action, and never lost between appointment, review, and closure.',

  roleFormMap: {
    intent: 'Review referral form created by the GP and Gate, act on it as the receiving specialist.',
    decision: 'What is the appropriate specialist action for this referral given its Gate outcome, priority, and clinical context?',
    criteria: [
      'Referral is in Scheduled or action-needed state',
      'Gate outcome and Pack criteria',
      'Priority level',
      'Workflow history and context',
      'Specialty-specific clinical judgment',
    ],
    activities: [
      'Review today\'s scheduled referrals',
      'Review upcoming appointments',
      'Open referral details with Gate and workflow context',
      'Act on referrals that need specialist input',
      'Complete the specialist referral action after review',
      'Review Queue Health for specialty bottlenecks',
      'Build and maintain Pack criteria',
    ],
    checkpoints: [
      'Referral opened from the correct worklist tab',
      'Gate outcome and priority reviewed before acting',
      'Specialist action completed through the approved flow',
      'Workflow event records the specialist action',
      'Pack criteria kept accurate and current',
    ],
    action: 'Review referrals, complete the specialist action, and maintain Pack accuracy.',
    form: 'workflow/state reflects the specialist action, workflowEvents records every review and action, and Pack criteria stay accurate.',
    metrics: [
      'Referrals reviewed per period',
      'Time from Scheduled to specialist action',
      'Action-needed backlog count',
      'Pack update frequency',
    ],
    valueCreated: 'The specialist closes the loop between referral and clinical action, ensures no referral is lost between scheduling and closure, and keeps Pack criteria accurate so upstream referrals arrive correctly.',
    learningMoments: [],
  },

  orientationCard: {
    loggedInAs: ROLE_DISPLAY[ROLES.SPECIALIST].name,
    yourJob: 'Review scheduled and action-needed referrals, use Gate context to prepare for each appointment, complete the specialist action after review, and keep Pack criteria accurate.',
    workspaces: [
      { label: 'My Worklist', route: '/specialist-worklist' },
      { label: 'Queue Health', route: '/queue-health' },
      { label: 'Pack Builder', route: '/pack-builder' },
    ],
    can: [
      'Review today\'s scheduled referrals',
      'Review upcoming appointments',
      'Prepare for appointments using Gate context',
      'Review and complete referrals that need specialist input',
      'Review Queue Health for specialty bottlenecks',
      'Build and maintain Pack criteria',
    ],
    cannot: [
      'Create intake rework requests directly',
      'Book, reschedule, or cancel appointments',
      'Perform triage overrides',
      'Change original Gate decisions',
      'Rewrite GP clinical answers',
      'Move referrals through Intake or Scheduling manually',
      'Store PHI outside the approved referral flow',
    ],
    mainSuccessAction: 'Every referral the Specialist touches should end with a clear specialist disposition: seen, completed, action needed, or handed to the correct next owner. No referral should remain clinically unresolved after specialist review.',
    noChaosRule: 'No scheduled or action-needed referral should disappear between appointment, specialist review, and closure. If another role must act, the referral must remain visible, owned, and recoverable.',
  },

  roleClarityQuestions: {
    whoAmI: 'I am the Specialist.',
    whatAmIHereToDo: 'I review scheduled and action-needed referrals, use Gate context to prepare, complete the specialist action, and keep Pack criteria accurate.',
    whatCanITouch: 'I can review referrals on my worklist, complete the specialist action, review Queue Health, and build or update Pack criteria.',
    whatCanINotTouch: 'I cannot create rework requests, book or cancel appointments, perform triage overrides, change Gate decisions, or rewrite GP clinical answers.',
    whatActionCreatesProgress: 'Reviewing a referral with its Gate and workflow context and completing the specialist action moves the referral toward closure.',
    whatFormMustExistAfterIAct: 'After acting, the workflow state reflects the specialist action, a workflow event records what happened, and Pack criteria remain accurate.',
  },

  guidedModules: [
    {
      moduleId: 'specialist_worklist_today',
      title: 'Today',
      route: '/specialist-worklist',
      trigger: 'context',
      contextKey: 'specialistTabToday',
      sector: SECTORS.SPECIALIST_WORKLIST,
      role: ROLES.SPECIALIST,
      steps: [
        {
          stepId: 'today_overview',
          title: 'Today',
          body: 'The Today tab shows referrals scheduled for today.\n\nThis is not just a calendar list. It is the Specialist\'s same-day worklist: referrals that reached appointment stage after moving through the criteria your specialty defined.\n\nUse Today to see which patients are booked, what pathway they came through, and which referrals need your attention now.\n\nYour job is to prepare from Gate context, see the patient, and leave each referral with a clear disposition.',
          teaches: 'Today shows the same-day appointments that reached you through specialist-defined criteria.',
          prevents: 'Treating Today like a generic schedule instead of the final clinical worklist created by the Gate workflow.',
          checkpoint: 'User can explain what the Today tab represents and how referrals arrive there.',
          focusTarget: 'specialist-today-tab',
          completionType: 'manual',
        },
        {
          stepId: 'read_referral_card',
          title: 'Read the Referral Card',
          body: 'Each card gives you the referral snapshot before you open it.\n\nCheck the pathway, referral ID, appointment time, consultant, priority, and Gate outcome. These details help you confirm what the referral is, when the patient is booked, and what context the Gate produced before the appointment.\n\nWhen you are ready to review the case, open the card.',
          teaches: 'Use the referral card to orient yourself before opening the case.',
          prevents: 'Starting a review without checking the appointment time, pathway, or Gate context.',
          checkpoint: 'User can identify pathway, priority, and Gate outcome on a referral card before opening it.',
          focusTarget: 'specialist-referral-card',
          completionType: 'manual',
        },
      ],
    },
    {
      moduleId: 'specialist_worklist_needs_action',
      title: 'Needs Action',
      route: '/specialist-worklist',
      trigger: 'context',
      contextKey: 'specialistTabNeedsAction',
      sector: SECTORS.SPECIALIST_WORKLIST,
      role: ROLES.SPECIALIST,
      steps: [
        {
          stepId: 'needs_action_overview',
          title: 'Needs Action',
          body: 'The Needs Action tab shows referrals that need specialist attention before they can move forward or close.\n\nThis tab catches incomplete specialist work after an appointment has entered the real-world visit stage. A referral appears here when the appointment has passed but the visit was not reconciled, or when the patient was marked seen but the consult note is still missing.\n\nUse Needs Action to clear unfinished specialist work and prevent referrals from getting stuck between Scheduled, Seen, and Closed Loop.',
          teaches: 'Needs Action shows referrals that need specialist follow-through after appointment movement.',
          prevents: 'Letting referrals stay unresolved after the appointment date or patient visit.',
          checkpoint: 'User can explain what the Needs Action tab captures and why referrals appear here.',
          focusTarget: 'specialist-needs-action-tab',
          completionType: 'manual',
        },
        {
          stepId: 'unreconciled_visits',
          title: 'Unreconciled Visits',
          body: 'Unreconciled Visits are past-due scheduled referrals.\n\nThe appointment date has passed, but the referral was not marked as Seen. These referrals need closure because the system still does not know what happened to the appointment.\n\nOpen the referral, check the visit context, and resolve the missing appointment outcome.',
          teaches: 'Unreconciled Visits are appointments that passed without a recorded Seen outcome.',
          prevents: 'A completed or missed appointment remaining stuck as if it were still scheduled.',
          checkpoint: 'User can identify an unreconciled visit and explain the resolution action.',
          focusTarget: 'specialist-unreconciled-section',
          completionType: 'manual',
        },
        {
          stepId: 'pending_consult_notes',
          title: 'Pending Consult Notes',
          body: 'Pending Consult Notes are referrals already marked as Seen but not yet closed.\n\nThe visit was acknowledged, but the consult note or closing action is still missing. Open the referral, complete the consult documentation, and close the referral loop when ready.',
          teaches: 'Pending Consult Notes are Seen referrals waiting for specialist documentation and closure.',
          prevents: 'A seen patient remaining open without a documented specialist outcome.',
          checkpoint: 'User can identify a pending consult note and explain the closure action.',
          focusTarget: 'specialist-pending-notes-section',
          completionType: 'manual',
        },
      ],
    },
    {
      moduleId: 'specialist_close_referral_loop',
      title: 'Closing Referral Loop',
      route: '/specialist-worklist',
      trigger: 'context',
      contextKey: 'specialistClosingDrawer',
      sector: SECTORS.SPECIALIST_WORKLIST,
      role: ROLES.SPECIALIST,
      steps: [
        {
          stepId: 'upload_consult_document',
          title: 'Upload Consult Document',
          body: 'Before closing the referral loop, you can attach the consult document.\n\nThis is the clinical record of the visit: a letter, a report, or a summary that documents what happened during the consultation. Uploading it here stores the document through the PHI-safe attachment path so the referring provider and the operations team have access to a verified clinical artifact.\n\nUploading a consult document is not mandatory to close the loop, but it is the strongest form of closure evidence. If no document is uploaded, a consult note is required instead.',
          teaches: 'The consult document upload provides PHI-safe closure evidence for the referral.',
          prevents: 'Closing a referral without attaching the clinical documentation that downstream teams need.',
          checkpoint: 'User can locate the Upload Consult Document box and explain what it stores.',
          focusTarget: 'specialist-consult-upload',
          completionType: 'manual',
        },
        {
          stepId: 'consult_note',
          title: 'Consult Note',
          body: 'The Consult Note is a short, non-PHI summary of the consultation outcome.\n\nThis is not a clinical letter. It is a workflow annotation: what disposition was reached, whether follow-up is needed, and any context the operations team should know. The note is stored in the non-PHI workflow layer, so it must never contain patient identifiers, contact details, or clinical data that belongs in the consult document.\n\nEither a consult note or an uploaded consult document is required to close the referral loop. If both are missing, the Close Referral Loop button stays disabled.',
          teaches: 'The consult note is a non-PHI workflow annotation, not a clinical document.',
          prevents: 'Writing PHI into the consult note field or trying to close without any closure evidence.',
          checkpoint: 'User can explain the difference between the consult note and the consult document.',
          focusTarget: 'specialist-consult-note',
          completionType: 'manual',
        },
        {
          stepId: 'close_referral_loop',
          title: 'Close Referral Loop',
          body: 'Close Referral Loop is the final specialist action.\n\nWhen you press this button, the referral transitions to Closed Loop. This is a terminal state: the referral is complete, the outcome is documented, and the workflow becomes append-only. The system records who closed it, when, and what evidence was provided.\n\nThe button is disabled until at least one form of closure evidence exists: a consult note or an uploaded consult document. Once closed, the referral cannot be reopened.',
          teaches: 'Close Referral Loop is the terminal workflow action that seals the referral.',
          prevents: 'Attempting to close without closure evidence or expecting the referral to be editable afterward.',
          checkpoint: 'User can explain what happens when the referral loop is closed and why it is irreversible.',
          focusTarget: 'specialist-close-loop-btn',
          completionType: 'manual',
        },
      ],
    },
    {
      moduleId: 'specialist_unreconciled_visit_actions',
      title: 'Unreconciled Visit Actions',
      route: '/specialist-worklist',
      trigger: 'context',
      contextKey: 'specialistUnreconciledDrawer',
      sector: SECTORS.SPECIALIST_WORKLIST,
      role: ROLES.SPECIALIST,
      steps: [
        {
          stepId: 'resolve_visit_outcome',
          title: 'Resolve the Visit Outcome',
          body: 'This referral is here because the appointment date has passed, but the system does not yet know what happened.\n\nChoose the action that matches the real appointment outcome: the patient was seen, the referral can be closed now, or the patient did not show.',
          teaches: 'Unreconciled Visits need a recorded appointment outcome.',
          prevents: 'Leaving a past appointment stuck as if it were still scheduled.',
          checkpoint: 'User can identify the three resolution actions for an unreconciled visit.',
          focusTarget: 'specialist-unreconciled-actions',
          completionType: 'manual',
        },
        {
          stepId: 'mark_as_seen',
          title: 'Mark as Seen',
          body: 'Use Mark as Seen when the patient attended the appointment, but the referral is not ready to close yet.\n\nThis records that the visit happened and moves the referral into the next specialist step, usually Pending Consult Notes.',
          teaches: 'Mark as Seen confirms the patient was seen without closing the referral loop.',
          prevents: 'Closing too early when the consult note or final documentation is not ready.',
          checkpoint: 'User can explain when to use Mark as Seen instead of Close Referral Loop.',
          focusTarget: 'specialist-mark-seen-btn',
          completionType: 'manual',
        },
        {
          stepId: 'close_referral_loop',
          title: 'Close Referral Loop',
          body: 'Use Close Referral Loop when the patient was seen and the specialist outcome is ready to document.\n\nThe referral must have closure evidence: either a consult note or an uploaded consult document. Closing the loop completes the specialist work and removes the referral from open action queues.',
          teaches: 'Close the loop only when the specialist outcome is documented.',
          prevents: 'Leaving a seen referral open without final documentation.',
          checkpoint: 'User can explain the closure evidence requirement.',
          focusTarget: 'specialist-close-loop-btn',
          completionType: 'manual',
        },
        {
          stepId: 'patient_did_not_show',
          title: 'Patient Did Not Show',
          body: 'Use Patient Did Not Show when the appointment passed but the patient did not attend.\n\nThis records the appointment outcome clearly so the referral does not remain unresolved. If the patient needs another appointment, that should follow the existing rebooking workflow.',
          teaches: 'Record No-Show when no visit occurred.',
          prevents: 'Treating a missed appointment as if the patient was seen.',
          checkpoint: 'User can explain when to use Patient Did Not Show and what it records.',
          focusTarget: 'specialist-no-show-btn',
          completionType: 'manual',
        },
      ],
    },
    {
      moduleId: 'specialist_worklist_upcoming',
      title: 'Upcoming',
      route: '/specialist-worklist',
      trigger: 'context',
      contextKey: 'specialistTabUpcoming',
      sector: SECTORS.SPECIALIST_WORKLIST,
      role: ROLES.SPECIALIST,
      steps: [
        {
          stepId: 'upcoming_overview',
          title: 'Upcoming',
          body: 'The Upcoming tab shows referrals scheduled for a future date.\n\nUse it to look ahead, prepare for future appointments, and understand what referral volume is coming toward your specialty. These referrals are already scheduled, but they are not today\'s work and they do not need specialist closure yet.\n\nOpen a referral only when you need to preview its context before the appointment date.',
          teaches: 'Upcoming shows future scheduled referrals that are not ready for same-day action yet.',
          prevents: 'Treating future appointments like Today or Needs Action work.',
          checkpoint: 'User understands Upcoming is for look-ahead only, not same-day action.',
          completionType: 'manual',
        },
      ],
    },
    {
      moduleId: 'specialist_worklist_closed_referrals',
      title: 'Closed Referrals',
      route: '/specialist-worklist',
      trigger: 'context',
      contextKey: 'specialistTabClosedReferrals',
      sector: SECTORS.SPECIALIST_WORKLIST,
      role: ROLES.SPECIALIST,
      steps: [
        {
          stepId: 'closed_referrals_overview',
          title: 'Closed Referrals',
          body: 'The Closed Referrals tab shows referrals that have already reached Closed Loop.\n\nUse it to review completed referral records when you need to confirm what was finished, when it was closed, and which pathway it came through. These referrals are no longer Today work, Upcoming work, or Needs Action work.\n\nOpen a closed referral only when you need to reference the completed record.',
          teaches: 'Closed Referrals shows completed referrals that have left open specialist work.',
          prevents: 'Treating closed referrals like active appointments or unresolved specialist tasks.',
          checkpoint: 'User understands Closed Referrals is a read-only archive, not an active work queue.',
          completionType: 'manual',
        },
      ],
    },

    // ------------------------------------------------------------------
    // TODAY REFERRAL DETAIL WALKTHROUGH — 10-card drawer walkthrough
    // ------------------------------------------------------------------
    {
      moduleId: 'specialist_today_referral_detail',
      title: 'Today — Referral Detail Walkthrough',
      route: '/specialist-worklist',
      trigger: 'context',
      contextKey: 'specialistTodayDrawer',
      sector: SECTORS.SPECIALIST_WORKLIST,
      role: ROLES.SPECIALIST,
      steps: [
        {
          stepId: 'referral_summary_header',
          title: 'Referral Summary Header',
          focusTarget: 'referral-summary-header',
          body: 'The header at the top of the drawer shows the specialty, pathway name, Gate outcome, and workflow status at a glance.\n\nUse it to confirm the referral reached the right pathway and to see where it stands in the pipeline before reading the detail sections below.',
          teaches: 'The header is the fastest way to confirm specialty, pathway, outcome, and current status.',
          prevents: 'Scrolling through detail sections to answer questions the header already answers.',
          checkpoint: 'User can read specialty, pathway, outcome, and status from the header without scrolling.',
          completionType: 'manual',
        },
        {
          stepId: 'patient_contact_insurance',
          title: 'Patient · Contact · Insurance',
          focusTarget: 'patient-contact-insurance',
          body: 'These three sections display the patient identity, contact details, and insurance information submitted with the referral.\n\nReview them to confirm you have the right patient and that the contact and coverage information is present before the appointment.',
          teaches: 'Patient, Contact, and Insurance sections confirm identity and coverage before the visit.',
          prevents: 'Starting an appointment without verifying patient identity or insurance status.',
          checkpoint: 'User knows where to find patient identity, contact, and insurance in the drawer.',
          completionType: 'manual',
        },
        {
          stepId: 'attachments',
          title: 'Attachments',
          focusTarget: 'attachments',
          body: 'The Attachments section lists documents uploaded by the referring provider — lab results, imaging, clinical notes, or other supporting files.\n\nCheck this section before the appointment to see if the referring provider provided the records you need. Missing attachments may have been requested through rework.',
          teaches: 'Attachments shows the documents the referring provider uploaded to support this referral.',
          prevents: 'Going into an appointment without reviewing available supporting documents.',
          checkpoint: 'User knows where uploaded documents appear and can check for completeness.',
          completionType: 'manual',
        },
        {
          stepId: 'upload_consult_document',
          title: 'Upload Consult Document',
          focusTarget: 'upload-consult-document',
          body: 'This section lets you upload a consult document — a specialist report, procedure note, or follow-up summary — back into the referral record.\n\nUploading a consult document satisfies the requirement for closing the referral loop when no inline consult note is written.',
          teaches: 'Consult document upload provides an alternative to writing an inline consult note for loop closure.',
          prevents: 'Being unable to close a referral loop because no consult note or document is attached.',
          checkpoint: 'User understands that a consult document or consult note is needed to close the loop.',
          completionType: 'manual',
        },
        {
          stepId: 'gate_decision',
          title: 'Gate Decision',
          focusTarget: 'gate-decision',
          body: 'The Gate Decision section shows the outcome the engine produced when the referring provider submitted this referral — ROUTINE, NOT YET, or URGENT.\n\nThis is the specialist-authored Pack\’s judgment on whether the referral was complete and safe at submission time. It does not change after submission.',
          teaches: 'Gate Decision reflects the Pack engine\’s assessment at submission — ROUTINE, NOT YET, or URGENT.',
          prevents: 'Confusing the Gate outcome with the current workflow status or triage overrides.',
          checkpoint: 'User can distinguish Gate outcome from workflow status and knows it is immutable.',
          completionType: 'manual',
        },
        {
          stepId: 'referral_visit_info',
          title: 'Referral Details · Visit Information',
          focusTarget: 'referral-visit-info',
          body: 'These two sections show the referral metadata and visit information — who referred the patient, when, which specialty and pathway, and the visit type and reason for escalation.\n\nUse this to confirm the referral origin and visit context before reviewing clinical answers.',
          teaches: 'Referral Details and Visit Information provide origin, timing, pathway, and visit context.',
          prevents: 'Reviewing clinical answers without knowing the referral source, pathway, or visit type.',
          checkpoint: 'User can find who referred, when, which pathway, and the visit type.',
          completionType: 'manual',
        },
        {
          stepId: 'clinical_answers',
          title: 'Clinical Answers',
          focusTarget: 'clinical-answers',
          body: 'Clinical Answers shows the referring provider\'s responses to the Pack\'s clinical fields — the structured data the specialist defined as necessary for this pathway.\n\nThis is the core clinical content of the referral. Review it to understand what the referring provider reported and whether the answers align with the Gate outcome.',
          teaches: 'Clinical Answers contains the referring provider\'s structured responses to specialist-defined Pack fields.',
          prevents: 'Treating the referral as a free-text letter instead of structured, Pack-driven clinical data.',
          checkpoint: 'User understands Clinical Answers are Pack-driven and form the basis of the Gate decision.',
          completionType: 'manual',
        },
        {
          stepId: 'appointment_triage',
          title: 'Appointment · Triage Annotations',
          focusTarget: 'appointment-triage',
          body: 'The Appointment section shows scheduling details — date, consultant, and location. Triage Annotations show any overrides, priority changes, or holds applied by the triage nurse.\n\nCheck these to understand when and where the patient is booked, and whether triage modified the original Gate decision.',
          teaches: 'Appointment and Triage show scheduling details and any clinical overrides applied before your visit.',
          prevents: 'Missing a triage override or priority escalation that changes how to approach the appointment.',
          checkpoint: 'User knows where to find appointment details and triage modifications.',
          completionType: 'manual',
        },
        {
          stepId: 'rework_workflow_history',
          title: 'Rework Items · Workflow History',
          focusTarget: 'rework-workflow-history',
          body: 'Rework Items shows any documents or information that were requested from the referring provider after submission. Workflow History shows every state transition the referral has been through.\n\nUse Rework Items to see if missing documents were resolved. Use Workflow History to trace who acted on the referral and when.',
          teaches: 'Rework Items tracks missing-document requests and Workflow History logs every state change.',
          prevents: 'Not knowing whether requested documents were received or who moved the referral through each stage.',
          checkpoint: 'User can trace rework resolution and the full workflow timeline.',
          completionType: 'manual',
        },
        {
          stepId: 'consult_note_actions',
          title: 'Consult Note · Actions',
          focusTarget: 'consult-note-actions',
          body: 'The sticky footer contains the consult note field and the action buttons — Mark as Seen and Close Referral Loop.\n\nWrite a non-PHI consult note or upload a consult document before closing the loop. Mark as Seen transitions a SCHEDULED referral to SEEN. Close Referral Loop completes the referral lifecycle.',
          teaches: 'The footer is where you write the consult note and perform Mark as Seen or Close Referral Loop.',
          prevents: 'Closing the loop without a consult note or document, or confusing Mark as Seen with Close.',
          checkpoint: 'User understands the two-step flow: Mark as Seen first, then Close Referral Loop.',
          completionType: 'manual',
        },
      ],
    },

    // ------------------------------------------------------------------
    // PACK BUILDER LIST — 1-card criteria library overview
    // ------------------------------------------------------------------
    {
      moduleId: 'specialist_pack_builder_list',
      title: 'Pack Builder',
      route: '/packs',
      trigger: 'context',
      contextKey: 'packBuilderList',
      sector: SECTORS.PACKS,
      role: ROLES.SPECIALIST,
      steps: [
        {
          stepId: 'pack_builder_list_overview',
          title: 'Pack Builder',
          focusTarget: 'pack-builder-list',
          body: 'Pack Builder shows the referral pathways your specialty owns.\n\nEach Pack defines the criteria the Gate uses to decide whether a referral is Routine, Urgent, or Not Yet. Use this screen to create a new Pack, edit existing criteria, or archive a pathway that should no longer be active.\n\nNew Pack starts a new pathway. Edit opens the Pack criteria. Archive removes a Pack from active use without treating it like current referral work.',
          teaches: 'Pack Builder is where your specialty maintains the criteria that shape referral quality.',
          prevents: 'Treating Packs like simple forms instead of the standards that control what reaches your worklist.',
          checkpoint: 'User understands that Pack Builder is the criteria library and can identify New Pack, Edit, and Archive actions.',
          completionType: 'manual',
        },
      ],
    },

    // ------------------------------------------------------------------
    // PACK EDITOR INTRO — 1-card orientation before section guidance
    // ------------------------------------------------------------------
    {
      moduleId: 'specialist_pack_editor_intro',
      title: 'Pack Editor',
      route: '/packs',
      trigger: 'context',
      contextKey: 'packEditorOpen',
      sector: SECTORS.PACKS,
      role: ROLES.SPECIALIST,
      steps: [
        {
          stepId: 'pack_editor_intro',
          title: 'Pack Editor',
          focusTarget: 'pack-editor-guidance-icon',
          body: 'The Pack Editor is where you define the specialist criteria that the Gate uses to evaluate referrals.\n\nWork through the sections in order: Pack Setup, Scope, Redirects, Red Flags, Clinical Fields, Attachments, Not Yet Steps, and Patient Messages.\n\nEach section has built-in guidance. Use the ? icon when you need to check what the section is for, what question to ask yourself, what good input looks like, and what to avoid.',
          teaches: 'Use section guidance while authoring each part of the Pack.',
          prevents: 'Treating Pack editing like free-form setup instead of criteria design.',
          checkpoint: 'User understands the Pack Editor defines Gate criteria and knows how to access section guidance via the ? icon.',
          completionType: 'manual',
        },
      ],
    },

    // ------------------------------------------------------------------
    // QUEUE HEALTH SIGNALS — 7-card signal-reading walkthrough
    // ------------------------------------------------------------------
    {
      moduleId: 'specialist_queue_health_signals',
      title: 'Queue Health — Reading the Signals',
      route: '/specialist-queue-health',
      sector: SECTORS.SPECIALIST_WORKLIST,
      role: ROLES.SPECIALIST,
      steps: [
        {
          stepId: 'queue_health_overview',
          title: 'Queue Health Overview',
          focusTarget: 'queue-health-header',
          body: 'Queue Health is your live operational dashboard. It pulls directly from workflow states scoped to your specialist assignments and shows what needs attention right now.\n\nThe header confirms the dashboard is live, shows today\'s date, and displays a quick patient count. Use it to orient before reading the signal cards below.',
          teaches: 'Queue Health is a live, specialist-scoped dashboard that surfaces operational signals from workflow data.',
          prevents: 'Treating Queue Health as a static report instead of a live signal system.',
          checkpoint: 'User understands that Queue Health is live and scoped to their specialist assignments.',
          completionType: 'manual',
        },
        {
          stepId: 'live_fires',
          title: 'Action Required — Live Fires',
          focusTarget: 'queue-health-live-fires',
          body: 'The Action Required section contains three signal cards that surface items needing immediate attention. When a card turns red with a fire indicator, it means the threshold has been crossed and action is overdue.\n\nScan this section first every time you open Queue Health. Red fire signals are your highest priority.',
          teaches: 'Action Required cards turn red when thresholds are crossed — these are live fire signals, not informational counts.',
          prevents: 'Ignoring fire signals by treating all cards as equal-weight informational tiles.',
          checkpoint: 'User knows that red fire indicators in Action Required mean thresholds are crossed and action is needed.',
          completionType: 'manual',
        },
        {
          stepId: 'todays_patients',
          title: 'Today\'s Patients',
          focusTarget: 'queue-health-todays-patients',
          body: 'Today\'s Patients shows the count of referrals with a SCHEDULED status and an appointment date of today. This is your day\'s workload at a glance.\n\nUse it to confirm how many patients you expect to see before starting your clinic day.',
          teaches: 'Today\'s Patients counts referrals SCHEDULED for today — your expected clinic workload.',
          prevents: 'Starting a clinic day without knowing how many patients are booked.',
          checkpoint: 'User understands that Today\'s Patients reflects SCHEDULED referrals with today\'s appointment date.',
          completionType: 'manual',
        },
        {
          stepId: 'unreconciled_visits',
          title: 'Unreconciled Visits',
          focusTarget: 'queue-health-unreconciled-visits',
          body: 'Unreconciled Visits counts referrals that are still SCHEDULED but have an appointment date in the past. These are patients who should have been seen but whose workflow was never updated.\n\nAny count above zero fires red. Click the card to navigate to your Needs Action tab and reconcile them — mark as seen, no-show, or reschedule.',
          teaches: 'Unreconciled Visits are past-due SCHEDULED referrals — the workflow was not updated after the appointment.',
          prevents: 'Letting past appointments remain in SCHEDULED status, which corrupts downstream metrics and delays loop closure.',
          checkpoint: 'User knows that unreconciled visits must be resolved and can navigate to the worklist to act on them.',
          completionType: 'manual',
        },
        {
          stepId: 'pending_consult_notes',
          title: 'Pending Consult Notes',
          focusTarget: 'queue-health-pending-consult-notes',
          body: 'Pending Consult Notes counts referrals in SEEN status that have not yet been closed. The fire threshold is SLA-based — when a SEEN referral exceeds the configured SLA window (default 72 hours), the card fires red.\n\nClick the card when it fires to navigate to your Needs Action tab and write the consult note or upload a consult document to close the loop.',
          teaches: 'Pending Consult Notes tracks SEEN referrals awaiting loop closure — SLA breach triggers the fire signal.',
          prevents: 'Letting SEEN referrals age past the SLA window without writing a consult note or uploading a document.',
          checkpoint: 'User understands the SLA-based fire threshold and knows how to resolve pending consult notes.',
          completionType: 'manual',
        },
        {
          stepId: 'engine_total_volume',
          title: 'Clinical Volume',
          focusTarget: 'queue-health-total-volume',
          body: 'The Clinical Volume section shows three trend indicators: Upcoming Volume (SCHEDULED in the next 14 days), Closed Loop Volume (referrals closed in the last 30 days), and No-Show Rate.\n\nThese are not action signals — they are trend context. Use them to understand your workload trajectory, closure throughput, and patient reliability patterns over time.',
          teaches: 'Clinical Volume provides trend context — upcoming load, closure throughput, and no-show patterns.',
          prevents: 'Confusing volume trend cards with action-required signals that need immediate response.',
          checkpoint: 'User can distinguish action signals (top row) from trend context (bottom row).',
          completionType: 'manual',
        },
        {
          stepId: 'act_from_signal',
          title: 'Act from Signal',
          focusTarget: 'queue-health-act-from-signal',
          body: 'Queue Health is scoped to your specialist assignments, uses SLA-based fire thresholds, and pulls live workflow data. The footer confirms these properties.\n\nThe operational pattern is: open Queue Health, scan for fire signals, click to navigate, and resolve. This dashboard exists so you never have to wonder what needs attention — the signals tell you.',
          teaches: 'The operational loop is: open Queue Health, scan fires, navigate, resolve.',
          prevents: 'Using Queue Health passively without acting on the signals it surfaces.',
          checkpoint: 'User can describe the full signal-to-action loop: scan, navigate, resolve.',
          completionType: 'manual',
        },
      ],
    },
  ],
};

// ============================================================================
// SPECIALIST ADMIN LEARNING MAP
// ============================================================================

const SPECIALIST_ADMIN_MAP = {
  role: ROLES.SPECIALIST_ADMIN,
  roleDisplayName: ROLE_DISPLAY[ROLES.SPECIALIST_ADMIN].name,

  workflow: {
    id: 'specialist-admin-workplace',
    label: 'Specialist Admin Workplace',
    description: 'The specialist admin workspace where referrals are reviewed, closed, and documented after the specialist visit.',
  },

  valueNeeded: 'Timely closure of every seen referral with proper documentation.',

  objective: 'Every referral that reaches Seen status is documented with closure evidence and advanced to Closed Loop so no referral stays open without a recorded specialist outcome.',

  roleFormMap: {
    intent: 'Close the referral loop after the specialist visit by documenting the outcome.',
    decision: 'Is there sufficient closure evidence (consult document or consult note) to seal this referral?',
    criteria: [
      'Referral is in Seen status',
      'Consult document uploaded or consult note written',
      'Closure evidence is non-PHI compliant in the note field',
    ],
    activities: [
      'Review today\'s seen referrals',
      'Upload consult documents through the PHI-safe attachment path',
      'Write non-PHI consult notes',
      'Close the referral loop',
    ],
    output: 'Referral transitions to Closed Loop with documented closure evidence.',
  },

  orientationCard: {
    loggedInAs: ROLE_DISPLAY[ROLES.SPECIALIST_ADMIN].name,
    yourJob: 'Ensure every seen referral gets documented closure evidence and is advanced to Closed Loop so no referral stays open without a recorded specialist outcome.',
    workspaces: [
      { label: 'My Workplace', route: '/specialist-admin-workplace' },
      { label: 'Queue Health', route: '/specialist-admin-queue-health' },
    ],
    can: [
      'Review today\'s seen referrals',
      'Upload consult documents through the PHI-safe attachment path',
      'Write non-PHI consult notes',
      'Close the referral loop',
      'Review closed referrals in the Closed Loop tab',
      'Monitor closing-loop pressure in Queue Health',
    ],
    cannot: [
      'Create intake rework requests',
      'Book, reschedule, or cancel appointments',
      'Perform triage overrides',
      'Change original Gate decisions',
      'Rewrite GP clinical answers',
      'Write PHI into the consult note field',
      'Reopen a closed referral',
    ],
    mainSuccessAction: 'Every seen referral should end with documented closure evidence and a Closed Loop status. No referral should remain in Seen without a recorded outcome.',
    noChaosRule: 'No seen referral should age silently without closure. If the consult document is missing or the note is empty, the referral must remain visible and actionable until sealed.',
  },

  roleClarityQuestions: [
    {
      question: 'What is the specialist admin\'s primary responsibility?',
      choices: [
        'Triage incoming referrals',
        'Close the referral loop after the specialist visit',
        'Schedule patient appointments',
        'Write clinical Pack criteria',
      ],
      correctIndex: 1,
      explanation: 'The specialist admin documents the visit outcome and closes the referral loop. Triage, scheduling, and Pack authoring belong to other roles.',
    },
    {
      question: 'What evidence is required before closing a referral loop?',
      choices: [
        'A signed prescription',
        'Either a consult document or a consult note',
        'A follow-up appointment date',
        'Approval from the referring provider',
      ],
      correctIndex: 1,
      explanation: 'At least one form of closure evidence — an uploaded consult document or a consult note — must exist before the Close Referral Loop button becomes active.',
    },
  ],

  guidedModules: [
    {
      moduleId: 'specialist_admin_today_intro',
      title: 'My Workplace — Today',
      route: '/specialist-admin-workplace',
      trigger: 'context',
      contextKey: 'specialistAdminTodayTab',
      sector: SECTORS.SPECIALIST_WORKLIST,
      role: ROLES.SPECIALIST_ADMIN,
      steps: [
        {
          stepId: 'todays_seen_referrals',
          title: 'Today\'s Seen Referrals',
          body: 'Today shows referrals connected to patients the specialist has seen or is seeing today.\n\nUse this tab as your daily closing-loop starting point. Each card gives you the specialty, pack, outcome, referral status, appointment time, and specialist assignment so you know which referral needs attention.\n\nThis view helps you move from the visit to documented closure without searching across the whole system.',
          teaches: 'Today is your daily work surface for seen referrals that may need closing-loop action.',
          prevents: 'Letting a seen referral sit without a documented outcome.',
          checkpoint: 'User can explain what the Today tab shows and why it is the starting point for closing loops.',
          focusTarget: 'specialist-admin-today-tab',
          completionType: 'manual',
        },
        {
          stepId: 'open_the_referral',
          title: 'Open the Referral',
          body: 'Click the referral card to open the full referral detail panel.\n\nInside the referral, you can review the visit context, confirm the referral details, upload a consult document, write a non-PHI consult note, and close the referral loop when closure evidence exists.\n\nStart by opening the referral.',
          teaches: 'Clicking the referral moves you from the worklist into the closing-loop workspace.',
          prevents: 'Trying to close the loop from the list without reviewing the referral detail.',
          checkpoint: 'User clicks a referral card and the referral detail panel opens.',
          focusTarget: 'specialist-admin-referral-card',
          completionType: 'manual',
        },
      ],
    },
    {
      moduleId: 'specialist_admin_needs_close_loop',
      title: 'Needs Close Loop',
      route: '/specialist-admin-workplace',
      trigger: 'context',
      contextKey: 'specialistAdminNeedsCloseLoopTab',
      sector: SECTORS.SPECIALIST_WORKLIST,
      role: ROLES.SPECIALIST_ADMIN,
      steps: [
        {
          stepId: 'needs_close_loop_overview',
          title: 'Needs Close Loop',
          body: 'The Needs Close Loop tab shows referrals already marked as Seen but not yet closed.\n\nUse this tab to finish the specialist admin work after the visit has happened. Open each referral, review the required closure details, complete the consult documentation or closing action, and move the referral into Closed Loop when ready.\n\nA referral should not stay here once the visit has been acknowledged and the outcome is ready to document.',
          teaches: 'Needs Close Loop shows Seen referrals waiting for final closure.',
          prevents: 'Leaving a Seen referral open without a documented specialist outcome.',
          checkpoint: 'User can explain what the Needs Close Loop tab shows and why referrals appear here.',
          focusTarget: 'specialist-admin-needs-close-loop-tab',
          completionType: 'manual',
        },
        {
          stepId: 'select_a_referral',
          title: 'Select a Referral',
          body: 'Choose a referral from this list to start the closure work.\n\nOpen the referral, confirm the Seen status, review the referral details, complete the required consult note or closing documentation, and close the loop when the record is ready.',
          teaches: 'Open a Seen referral to complete the final Closed Loop action.',
          prevents: 'Letting Seen referrals sit unresolved after the visit.',
          checkpoint: 'User selects a referral from the Needs Close Loop list.',
          focusTarget: 'specialist-admin-needs-close-loop-card',
          completionType: 'manual',
        },
      ],
    },
    {
      moduleId: 'specialist_admin_close_referral_loop',
      title: 'Closing Referral Loop',
      route: '/specialist-admin-workplace',
      trigger: 'context',
      contextKey: 'specialistAdminClosingDrawer',
      sector: SECTORS.SPECIALIST_WORKLIST,
      role: ROLES.SPECIALIST_ADMIN,
      steps: [
        {
          stepId: 'upload_consult_document',
          title: 'Upload Consult Document',
          body: 'Before closing the referral loop, you can attach the consult document.\n\nThis is the clinical record of the visit: a letter, a report, or a summary that documents what happened during the consultation. Uploading it here stores the document through the PHI-safe attachment path so the referring provider and the operations team have access to a verified clinical artifact.\n\nUploading a consult document is not mandatory to close the loop, but it is the strongest form of closure evidence. If no document is uploaded, a consult note is required instead.',
          teaches: 'The consult document upload provides PHI-safe closure evidence for the referral.',
          prevents: 'Closing a referral without attaching the clinical documentation that downstream teams need.',
          checkpoint: 'User can locate the Upload Consult Document box and explain what it stores.',
          focusTarget: 'specialist-admin-consult-upload',
          completionType: 'manual',
        },
        {
          stepId: 'referral_ping',
          title: 'Referral Ping',
          body: 'Use Referral Ping when another role must coordinate work before the referral can close.\n\nReferral Pings create one referral-bound action thread between you and the receiving user.\n\nUse them to: request operational clarification, coordinate missing follow-up, notify referring teams about closure readiness, resolve downstream blockers, or communicate workflow actions tied to this referral.\n\nReferral Pings are not clinical documentation and not document exchange.\n\nKeep replies short, operational, and free of PHI.\n\nThe referral itself remains the work surface. Closure evidence still belongs in the consult document or consult note.',
          teaches: 'Referral Pings coordinate referral-level operational action during closure.',
          prevents: 'Using replies as clinical documentation, sending PHI in messages, or trying to replace closure evidence with chat.',
          checkpoint: 'User can explain when a Referral Ping coordinates closure and that it does not replace closure evidence.',
          focusTarget: 'specialist-admin-referral-ping',
          completionType: 'manual',
        },
        {
          stepId: 'consult_note',
          title: 'Consult Note',
          body: 'The Consult Note is a short, non-PHI summary of the consultation outcome.\n\nThis is not a clinical letter. It is a workflow annotation: what disposition was reached, whether follow-up is needed, and any context the operations team should know. The note is stored in the non-PHI workflow layer, so it must never contain patient identifiers, contact details, or clinical data that belongs in the consult document.\n\nEither a consult note or an uploaded consult document is required to close the referral loop. If both are missing, the Close Referral Loop button stays disabled.',
          teaches: 'The consult note is a non-PHI workflow annotation, not a clinical document.',
          prevents: 'Writing PHI into the consult note field or trying to close without any closure evidence.',
          checkpoint: 'User can explain the difference between the consult note and the consult document.',
          focusTarget: 'specialist-admin-consult-note',
          completionType: 'manual',
        },
        {
          stepId: 'close_referral_loop',
          title: 'Close Referral Loop',
          body: 'Close Referral Loop is the final action.\n\nWhen you press this button, the referral transitions to Closed Loop. This is a terminal state: the referral is complete, the outcome is documented, and the workflow becomes append-only. The system records who closed it, when, and what evidence was provided.\n\nThe button is disabled until at least one form of closure evidence exists: a consult note or an uploaded consult document. Once closed, the referral cannot be reopened.',
          teaches: 'Close Referral Loop is the terminal workflow action that seals the referral.',
          prevents: 'Attempting to close without closure evidence or expecting the referral to be editable afterward.',
          checkpoint: 'User can explain what happens when the referral loop is closed and why it is irreversible.',
          focusTarget: 'specialist-admin-close-loop-btn',
          completionType: 'manual',
        },
      ],
    },
    {
      moduleId: 'specialist_admin_queue_health',
      title: 'Queue Health',
      route: '/specialist-admin-queue-health',
      trigger: 'context',
      contextKey: 'specialistAdminQueueHealth',
      sector: SECTORS.SPECIALIST_WORKLIST,
      role: ROLES.SPECIALIST_ADMIN,
      steps: [
        {
          stepId: 'queue_health_overview',
          title: 'Queue Health Overview',
          body: 'Queue Health shows the closing-loop pressure across your specialist workspace.\n\nUse this page to see whether seen referrals are moving into documented closure. The cards do not replace the worklist. They tell you where the work is building up so you know where to act next.\n\nThis view is scoped to your tenant and specialist admin access. It should include the specialists and specialties the Specialist Admin is allowed to support.',
          teaches: 'Queue Health turns seen referrals, needs-action counts, and closed loops into one live operational signal.',
          prevents: 'Waiting for referrals to age silently after the patient was already seen.',
          checkpoint: 'User can explain what Queue Health shows and how it relates to closing-loop pressure.',
          focusTarget: 'specialist-admin-queue-health-header',
          completionType: 'manual',
        },
        {
          stepId: 'seen_today',
          title: 'Seen Today',
          body: 'Seen Today counts patients who were marked seen today.\n\nThis tells you how much closure work may enter the Specialist Admin workflow today. A seen referral is not finished yet. It still needs closure evidence before it becomes Closed Loop.\n\nUse this number as the daily starting signal.',
          teaches: 'Seen Today shows how much same-day closure work may need follow-up.',
          prevents: 'Assuming a seen referral is complete just because the appointment happened.',
          checkpoint: 'User can explain the difference between a seen referral and a closed referral.',
          focusTarget: 'specialist-admin-seen-today',
          completionType: 'manual',
        },
        {
          stepId: 'needs_action',
          title: 'Needs Action',
          body: 'Needs Action shows seen referrals still awaiting Closed Loop.\n\nThis is the main action card for the Specialist Admin. These referrals already passed through the visit stage, but the outcome has not been sealed yet. Open My Workplace to upload the consult document, add a non-PHI consult note, and close the referral loop.\n\nWhen past-SLA referrals appear, the fire chip tells you how many are overdue. Act on those first.',
          teaches: 'Needs Action is the live count of seen referrals that still need documented closure.',
          prevents: 'Letting completed visits remain unresolved in the workflow.',
          checkpoint: 'User can explain why Needs Action is the primary Specialist Admin fire signal.',
          focusTarget: 'specialist-admin-needs-action',
          completionType: 'manual',
        },
        {
          stepId: 'closed_loop_7d',
          title: 'Closed Loop 7d',
          body: 'Closed Loop 7d shows how many referral loops were closed in the last seven days.\n\nUse this card to confirm that closure work is actually moving. If Needs Action is high but Closed Loop 7d is low, the team is seeing patients but not sealing the referral record fast enough.\n\nAct from the signal: Needs Action tells you where to go; closed-loop volume tells you whether the queue is clearing.',
          teaches: 'Closed Loop 7d shows recent completion flow.',
          prevents: 'Only watching today\'s appointments without checking whether referral loops are being finished.',
          checkpoint: 'User can explain how to read Needs Action and Closed Loop 7d together as a health signal.',
          focusTarget: 'specialist-admin-closed-loop-7d',
          completionType: 'manual',
        },
      ],
    },
    {
      moduleId: 'specialist_admin_closed_loop_tab',
      title: 'Closed Loop',
      route: '/specialist-admin-workplace',
      trigger: 'context',
      contextKey: 'specialistAdminClosedLoopTab',
      sector: SECTORS.SPECIALIST_WORKLIST,
      role: ROLES.SPECIALIST_ADMIN,
      steps: [
        {
          stepId: 'closed_referrals',
          title: 'Closed Referrals',
          body: 'Closed Loop shows referrals that have been fully closed: the specialist saw the patient, closure evidence was provided, and the referral reached its terminal state.\n\nThese records are read-only. They cannot be reopened or edited. Use this tab to confirm that a referral was closed, review the closure timestamp, or verify that the outcome was documented.\n\nIf a referral is missing from this list, it has not been closed yet. Check the Today or Needs Close Loop tabs for referrals still awaiting closure.',
          teaches: 'Closed Loop is a read-only archive of completed referrals, not an active work surface.',
          prevents: 'Searching Closed Loop for referrals that still need action or expecting to edit a closed referral.',
          checkpoint: 'User can explain that Closed Loop referrals are terminal and read-only.',
          focusTarget: 'specialist-admin-closed-loop-tab',
          completionType: 'manual',
        },
      ],
    },
  ],
};

// ============================================================================
// OPS MANAGER — Reads operational signals and directs team attention
//
// Read-and-direct executive role. Two workspaces:
//   • Operations Command Center (/ops-dashboard, SECTOR_ANALYTICS)
//   • Impact Settings (/impact-settings, read-only via SECTOR_TENANT_ADMIN)
//
// Guided modules teach the dashboard as a command system (fires → flow →
// appointment leakage → quality & value) and how Impact Settings shape it.
// All steps reference the data-learning-target hooks already present on the
// dashboard and Impact Settings views. Missing targets degrade gracefully
// (the panel simply renders the card without a highlight).
// ============================================================================

const OPS_MANAGER_MAP = {
  role: ROLES.OPS_MANAGER,
  roleDisplayName: ROLE_DISPLAY[ROLES.OPS_MANAGER].name,

  workflow: {
    id: 'ops-dashboard',
    label: 'Operations Command Center',
    description: 'The read-only operational control panel where the Ops Manager reads signals, spots leakage, and directs team attention.',
  },

  valueNeeded: 'A clear, current picture of where work is stuck, where quality is leaking, where scheduled capacity is being lost, and where the Gate is creating value.',
  objective: 'Read the operational signals and direct team attention to the right problem first.',

  roleFormMap: {
    intent: 'Keep the referral operation clear by making stuck work, quality leakage, scheduling loss, and capacity value visible.',
    decision: 'Where does management attention need to go right now?',
    criteria: [
      'Which operational exceptions are live (stuck, aging, leaking).',
      'How work is moving from Gate to Closed Loop.',
      'Where scheduled capacity is being lost after booking.',
      'Whether Gate quality, rework burden, and capacity value are healthy.',
    ],
    activities: [
      'Read Live Fires for act-now exceptions.',
      'Read the Referral Funnel for throughput.',
      'Read Appointment Exceptions for post-booking leakage.',
      'Read System Quality & Value for quality, rework, and capacity.',
      'Review Impact Settings thresholds and assumptions.',
    ],
    checkpoints: [
      'Orientation', 'Live Fires', 'Referral Funnel', 'Appointment Exceptions',
      'System Quality & Value', 'Impact Settings',
    ],
    action: 'Direct team attention based on the signals; the Ops Manager does not act inside referrals.',
    form: 'No referral form is written by this role; the output is directed attention and configured operating assumptions.',
    metrics: [
      'Live Fires (stuck counts by stage)', 'Referral Funnel stages', 'Appointment Exception counts',
      'Gate Quality ratios', 'Rework Reality counts', 'Time Reclaimed',
    ],
    valueCreated: 'Faster intervention on stuck work, less capacity leakage, and a shared view of operational value.',
    learningMoments: [],
  },

  orientationCard: {
    loggedInAs: ROLE_DISPLAY[ROLES.OPS_MANAGER].name,
    yourJob: 'Read operational signals, spot where work is breaking down, and direct your team to the right problem first.',
    mainWorkspace: {
      label: 'Operations Command Center',
      route: '/ops-dashboard',
    },
    can: [
      'Read operational risk, referral movement, and appointment-stage leakage.',
      'Read Gate quality, rework burden, and capacity created.',
      'Review and configure Impact Settings thresholds and assumptions.',
      'Direct team attention based on the signals.',
    ],
    cannot: [
      'Edit referral answers or rewrite Gate decisions.',
      'Schedule, close, or rework referrals from this dashboard.',
      'Read or change PHI.',
      'Act as Intake, Triage, Scheduler, Specialist, or Specialist Admin.',
    ],
    mainSuccessAction: 'Identify the highest-priority operational leak and direct the right team to clear it.',
    noChaosRule: 'No stuck referral, unresolved rework, scheduling leakage, closed-loop delay, weak Gate quality, or capacity loss should stay invisible to you.',
  },

  roleClarityQuestions: {
    whoAmI: 'I am the Ops Manager.',
    whatAmIHereToDo: 'Read operational signals and direct attention to where work is stuck, leaking, or losing value.',
    whatCanITouch: 'The Operations Command Center (read-only) and Impact Settings.',
    whatCanINotTouch: 'Referral answers, Gate decisions, scheduling/closing/rework actions, and PHI.',
    whatActionCreatesProgress: 'Directing the team to the right problem first, and keeping Impact Settings realistic.',
    whatFormMustExistAfterIAct: 'No referral form — directed attention and accurate operating assumptions.',
  },

  guidedModules: [
    {
      moduleId: 'ops_command_center',
      title: 'Operations Command Center',
      route: '/ops-dashboard',
      sector: SECTORS.ANALYTICS,
      role: ROLES.OPS_MANAGER,
      steps: [
        // ── Orientation ──────────────────────────────────────────────────────
        {
          stepId: 'occ_header_title',
          title: 'Operations Command Center',
          body: 'This is your operational control panel for one clinic group. You do not act on referrals here — you read the signals, spot where work is breaking down, and direct your team’s attention. Read it top to bottom: fires that need action now, then flow, then appointment leakage, then quality and value.',
          teaches: 'The dashboard is a read-and-direct command surface.',
          prevents: 'Treating the dashboard as a passive report or a referral workqueue.',
          focusTarget: 'occ_header_title',
        },
        {
          stepId: 'occ_period_selector',
          title: 'Time Window',
          body: 'Switch between 7, 30, and 90 days to change the period every signal is measured over. Use 7d to investigate a live problem, 30d for normal management rhythm, and 90d to confirm whether a pattern is real.',
          teaches: 'Every number reflects the selected window.',
          prevents: 'Misreading a short-window spike as a long-term trend.',
          focusTarget: 'occ_period_selector',
        },
        {
          stepId: 'occ_specialty_filter',
          title: 'Specialty Filter',
          body: 'Narrow the panel to one specialty to see where problems concentrate. Switch back to All Specialties for the operation-wide view. Use this to decide whether a fire is system-wide or owned by one team.',
          teaches: 'The filter re-scopes the dashboard.',
          prevents: 'Blaming the whole operation for a specialty-specific problem.',
          focusTarget: 'occ_specialty_filter',
        },
        {
          stepId: 'occ_freshness_indicator',
          title: 'Data Freshness',
          body: 'The updated indicator tells you how current the signals are. If the dashboard looks stale, refresh before you act on what you see.',
          teaches: 'Signals have an age.',
          prevents: 'Directing the team from outdated numbers.',
          focusTarget: 'occ_freshness_indicator',
        },
        // ── Live Fires ───────────────────────────────────────────────────────
        {
          stepId: 'occ_live_fires_section',
          title: 'Live Fires',
          body: 'Live Fires are not general statistics. They are operational exceptions happening now: work that is stuck, aging, or leaking. Read this first each day.',
          teaches: 'Live Fires are act-now signals.',
          prevents: 'Skimming past fires as ordinary metrics.',
          focusTarget: 'occ_live_fires_section',
        },
        {
          stepId: 'occ_fire_unclaimed_new',
          title: 'Unclaimed New',
          body: 'New referrals have arrived but no one has taken ownership yet. Unowned work ages before anyone is accountable. Check intake coverage and make sure new referrals are being claimed promptly.',
          teaches: 'Unclaimed work is the earliest leakage point.',
          prevents: 'New referrals sitting untouched.',
          focusTarget: 'occ_fire_unclaimed_new',
        },
        {
          stepId: 'occ_fire_stuck_needs_info',
          title: 'Stuck in Needs Info',
          body: 'Incomplete referrals are waiting too long for the missing information needed to move. Investigate the delay source: is the referrer slow to respond, or is the team not chasing?',
          teaches: 'Aging incomplete referrals are stalled work.',
          prevents: 'Needs Info becoming hidden backlog.',
          focusTarget: 'occ_fire_stuck_needs_info',
        },
        {
          stepId: 'occ_fire_stuck_triage',
          title: 'Stuck in Triage',
          body: 'Referrals are waiting too long for clinical review. An urgent breach here deserves immediate attention because clinical review may be waiting behind the queue. Review queue coverage and inspect urgent breaches first.',
          teaches: 'Triage delay can carry clinical risk.',
          prevents: 'Urgent referrals aging unseen.',
          focusTarget: 'occ_fire_stuck_triage',
        },
        {
          stepId: 'occ_fire_stuck_scheduling',
          title: 'Stuck in Scheduling',
          body: 'Referrals are ready to book but are not being scheduled fast enough. This is verified work that is not converting into appointments. Review scheduler capacity and consultant availability.',
          teaches: 'Ready-but-unbooked work is throughput leakage.',
          prevents: 'Approved referrals stalling before appointment.',
          focusTarget: 'occ_fire_stuck_scheduling',
        },
        {
          stepId: 'occ_fire_long_dated_appointments',
          title: 'Long-Dated Appointments',
          body: 'Appointments are booked unusually far into the future. Long lead times can hide capacity strain and increase later cancellation or no-show risk. Review availability and demand.',
          teaches: 'Far-out bookings reveal capacity pressure.',
          prevents: 'Capacity problems hiding behind “it is scheduled.”',
          focusTarget: 'occ_fire_long_dated_appointments',
        },
        {
          stepId: 'occ_live_fire_unreconciled_visits',
          title: 'Unreconciled Visits',
          body: 'Past appointments are still marked Scheduled even though the appointment date has passed. The visit outcome has not been recorded as Seen, No-Show, or Cancelled. This is the gap before closed-loop work can even begin.',
          teaches: 'Unreconciled Visits show missed visit-outcome recording after the appointment date passes.',
          prevents: 'Confusing “scheduled” with “accounted for” when the appointment already should have a recorded outcome.',
          focusTarget: 'occ_fire_unreconciled_visits',
        },
        {
          stepId: 'occ_fire_seen_not_closed',
          title: 'Seen, Not Closed',
          body: 'Patients have been seen but the referral has not been closed out. An open loop means the outcome is not sealed. Push closed-loop completion so the operation reflects reality.',
          teaches: 'Seen but open is unfinished operational work.',
          prevents: 'Completed care never being closed.',
          focusTarget: 'occ_fire_seen_not_closed',
        },
        // ── Referral Funnel ──────────────────────────────────────────────────
        {
          stepId: 'occ_referral_funnel_section',
          title: 'Referral Funnel',
          body: 'The funnel shows how referral work moves through the operation: submitted, written, entered into ops, scheduled, and closed loop. Read it left to right and watch where numbers drop.',
          teaches: 'The funnel is the throughput view.',
          prevents: 'Reading one stage without the full flow.',
          focusTarget: 'occ_referral_funnel_section',
        },
        {
          stepId: 'occ_funnel_gate_submissions',
          title: 'Gate Submissions',
          body: 'This is the demand entering the Gate in the selected window. Compare it to Referrals Written to see how much incoming demand becomes real referral work. If submissions rise faster than referrals written, inspect whether referrers are abandoning, redirecting, or failing to complete the Gate.',
          teaches: 'Submissions are the top-of-funnel demand baseline.',
          prevents: 'Reading downstream stages without incoming volume.',
          focusTarget: 'occ_funnel_gate_submissions',
        },
        {
          stepId: 'occ_funnel_referrals_written',
          title: 'Referrals Written',
          body: 'These are submissions that became actual referral records. A gap from Gate Submissions means demand arrived but did not become referral work. Use this to separate raw demand from referrals that actually entered the system.',
          teaches: 'Written referrals are demand that became records.',
          prevents: 'Assuming every submission becomes a referral.',
          focusTarget: 'occ_funnel_referrals_written',
        },
        {
          stepId: 'occ_funnel_entered_ops',
          title: 'Entered Ops',
          body: 'These referrals reached operational handling. Compare this with Referrals Written to confirm new work is truly entering the pipeline. If written referrals are not entering ops, check claim flow, intake ownership, or handoff delay.',
          teaches: 'Entered Ops is where referrals become live operational work.',
          prevents: 'Missing a backlog between written and operational handling.',
          focusTarget: 'occ_funnel_entered_ops',
        },
        {
          stepId: 'occ_funnel_scheduled',
          title: 'Scheduled',
          body: 'These referrals converted into booked appointments. A weak number here, paired with Stuck in Scheduling, points to a booking bottleneck.',
          teaches: 'Scheduled shows conversion into appointments.',
          prevents: 'Overlooking scheduling bottlenecks.',
          focusTarget: 'occ_funnel_scheduled',
        },
        {
          stepId: 'occ_funnel_closed_loop',
          title: 'Closed Loop',
          body: 'These referrals were completed and sealed. Compare Scheduled to Closed Loop to see whether work is being finished, not just booked.',
          teaches: 'Closed Loop is the operational finish line.',
          prevents: 'Treating scheduled as done.',
          focusTarget: 'occ_funnel_closed_loop',
        },
        // ── Appointment Exceptions ───────────────────────────────────────────
        {
          stepId: 'occ_appointment_exceptions_section',
          title: 'Appointment Exceptions',
          body: 'These are recorded appointment-stage exception events during the selected window. They show where booked capacity was cancelled, moved, or lost after scheduling. These are counts, not rates.',
          teaches: 'Appointment Exceptions are post-booking leakage counts.',
          prevents: 'Reading them as percentages or cohort rates.',
          focusTarget: 'occ_appointment_exceptions_section',
        },
        {
          stepId: 'occ_appt_patient_cancellations',
          title: 'Patient Cancellations',
          body: 'Appointments cancelled by patients before completion. High counts point to patient-side friction such as communication, reminders, or slot convenience.',
          teaches: 'Patient cancellations are patient-side capacity leakage.',
          prevents: 'Mixing patient and clinic cancellation causes.',
          focusTarget: 'occ_appt_patient_cancellations',
        },
        {
          stepId: 'occ_appt_clinic_cancellations',
          title: 'Clinic Cancellations',
          body: 'Appointments cancelled by the clinic. Rising counts usually point to staffing, availability, or internal scheduling instability.',
          teaches: 'Clinic cancellations are internally caused capacity leakage.',
          prevents: 'Blaming patient behavior for clinic-side disruption.',
          focusTarget: 'occ_appt_clinic_cancellations',
        },
        {
          stepId: 'occ_appt_reschedules',
          title: 'Reschedules',
          body: 'Appointments moved to a new date after booking. Some movement is normal, but high counts show scheduling friction or instability.',
          teaches: 'Reschedules show churn after booking.',
          prevents: 'Ignoring appointment movement that erodes access.',
          focusTarget: 'occ_appt_reschedules',
        },
        {
          stepId: 'occ_appt_patient_no_shows',
          title: 'Patient No-Shows',
          body: 'Scheduled appointments where the patient did not attend. This is the most direct loss of booked specialist capacity. Review reminders and confirmation steps.',
          teaches: 'No-shows are lost scheduled capacity.',
          prevents: 'Treating no-shows as unavoidable.',
          focusTarget: 'occ_appt_patient_no_shows',
        },
        {
          stepId: 'occ_appt_total_exceptions',
          title: 'Total Appointment Exceptions',
          body: 'This combines all recorded appointment-stage exception events in the window. Use it as the headline leakage gauge, then inspect the individual causes.',
          teaches: 'Total exceptions show overall post-booking leakage.',
          prevents: 'Missing the headline trend inside individual counts.',
          focusTarget: 'occ_appt_total_exceptions',
        },
        // ── System Quality & Value ───────────────────────────────────────────
        {
          stepId: 'occ_system_quality_value_section',
          title: 'System Quality & Value',
          body: 'This section shows whether ReferralGate is improving the operation: cleaner referrals, less rework waste, and capacity protected. Use it to judge value, not today’s fires.',
          teaches: 'This is the quality and value lens.',
          prevents: 'Confusing long-run quality with live exceptions.',
          focusTarget: 'occ_system_quality_value_section',
        },
        {
          stepId: 'occ_gate_quality_section',
          title: 'Gate Quality',
          body: 'Gate Quality shows how cleanly referrals pass through the Gate and whether the Pack logic is working. Weakness here usually means referrer or Pack problems upstream.',
          teaches: 'Gate Quality reflects upstream referral and Pack health.',
          prevents: 'Treating downstream symptoms without checking the source.',
          focusTarget: 'occ_gate_quality_section',
        },
        {
          stepId: 'occ_gq_first_pass_acceptance',
          title: 'First-Pass Acceptance',
          body: 'The share of referrals accepted by the Gate on the first try. Higher means referrers are sending cleaner work. If it drops, look for avoidable rework at the source.',
          teaches: 'First-pass acceptance measures incoming referral quality.',
          prevents: 'Missing rising rework pressure upstream.',
          focusTarget: 'occ_gq_first_pass_acceptance',
        },
        {
          stepId: 'occ_gq_routine_clean_rate',
          title: 'Routine Clean Rate',
          body: 'The share of routine referrals that arrive clean. A strong routine clean rate means everyday referrals can flow without extra handling.',
          teaches: 'Routine clean rate isolates ordinary referral quality.',
          prevents: 'Letting routine quality erode unnoticed.',
          focusTarget: 'occ_gq_routine_clean_rate',
        },
        {
          stepId: 'occ_gq_closed_loop_rate',
          title: 'Closed-Loop Rate',
          body: 'The share of referrals reaching final completion. This tells you whether the pipeline finishes work, not just moves it forward.',
          teaches: 'Closed-loop rate measures completion discipline.',
          prevents: 'Mistaking activity for completion.',
          focusTarget: 'occ_gq_closed_loop_rate',
        },
        {
          stepId: 'occ_gq_not_yet_conversion',
          title: 'Not-Yet Conversion',
          body: 'The share of incomplete referrals later recovered and accepted by the Gate. Watch it to confirm information gaps are being fixed instead of abandoned.',
          teaches: 'Not-Yet Conversion shows incomplete work being recovered.',
          prevents: 'Assuming incomplete referrals are simply lost.',
          focusTarget: 'occ_gq_not_yet_conversion',
        },
        {
          stepId: 'occ_gq_override_rate',
          title: 'Override Rate',
          body: 'How often Gate decisions are manually overridden. Some overrides are legitimate, but a rising rate can reveal Pack mismatch or shortcut behavior.',
          teaches: 'Overrides reveal friction between Pack logic and real practice.',
          prevents: 'Override creep undermining the Gate.',
          focusTarget: 'occ_gq_override_rate',
        },
        // ── Rework Reality ───────────────────────────────────────────────────
        {
          stepId: 'occ_rework_reality_section',
          title: 'Rework Reality',
          body: 'Rework Reality shows the workload incomplete referrals create: requests opened, outreach logged, and missing items cleared. These are effort and item counts.',
          teaches: 'Rework Reality quantifies the cost of incomplete referrals.',
          prevents: 'Treating rework as invisible labor.',
          focusTarget: 'occ_rework_reality_section',
        },
        {
          stepId: 'occ_rework_requests',
          title: 'Rework Requests',
          body: 'The number of missing-information requests opened. Each request is a missing item the team had to chase instead of moving the referral forward.',
          teaches: 'Rework Requests size the gap-filling workload.',
          prevents: 'Treating incomplete referrals as free to recover.',
          focusTarget: 'occ_rework_requests',
        },
        {
          stepId: 'occ_rework_touches',
          title: 'Rework Touches',
          body: 'The number of outreach attempts logged to chase missing items. High touches compared with requests means recovery is consuming staff effort.',
          teaches: 'Touches measure the effort cost of recovery.',
          prevents: 'Missing staff effort consumed by chasing.',
          focusTarget: 'occ_rework_touches',
        },
        {
          stepId: 'occ_rework_resolved',
          title: 'Rework Resolved',
          body: 'The number of missing-information items received or cleared. This is an item count, not a referral recovery rate.',
          teaches: 'Rework Resolved counts cleared missing items.',
          prevents: 'Misreading resolved items as recovered referrals.',
          focusTarget: 'occ_rework_resolved',
        },
        // ── Capacity Created ─────────────────────────────────────────────────
        {
          stepId: 'occ_capacity_created_section',
          title: 'Capacity Created',
          body: 'Capacity Created translates cleaner referral work into reclaimed operational time. It is an estimated value signal, not stopwatch-measured time.',
          teaches: 'Capacity Created frames the Gate’s value as protected capacity.',
          prevents: 'Reading the estimate as exact measurement.',
          focusTarget: 'occ_capacity_created_section',
        },
        {
          stepId: 'occ_capacity_time_reclaimed',
          title: 'Time Reclaimed',
          body: 'An estimate of operational time protected by reducing and preventing referral chaos. If it feels too high or too low, review the assumption in Impact Settings.',
          teaches: 'Time Reclaimed is an estimated value signal.',
          prevents: 'Treating the estimate as exact or ignoring it as meaningless.',
          focusTarget: 'occ_capacity_time_reclaimed',
        },
      ],
    },
    {
      moduleId: 'impact_settings',
      title: 'Impact Settings',
      route: '/impact-settings',
      sector: SECTORS.TENANT_ADMIN,
      role: ROLES.OPS_MANAGER,
      steps: [
        {
          stepId: 'impact_settings_overview',
          title: 'Impact Settings Overview',
          body: 'Impact Settings are the operating rules behind the Command Center. They decide when work becomes stuck, when delay becomes a fire, when the SLA clock runs, and how reclaimed time is estimated. If these settings are wrong, the dashboard may either create noise or hide real operational risk.',
          teaches: 'Impact Settings shape how the Command Center reads risk, delay, and value.',
          prevents: 'Treating dashboard signals as fixed truth instead of configured operational judgment.',
          focusTarget: 'impact_settings_overview',
        },
        {
          stepId: 'impact_sla_stuck_queue_settings',
          title: 'SLA / Stuck Queue Settings',
          body: 'These thresholds define when each queue becomes operationally stuck. They control the Live Fires on the Command Center: unclaimed work, Needs Info delay, triage delay, scheduling delay, long-dated appointments, and seen-but-not-closed work. Set them to match real operational pain. If they are too tight, every queue looks broken. If they are too loose, real bottlenecks stay invisible.',
          teaches: 'SLA and stuck-queue settings define when work becomes visible operational risk.',
          prevents: 'Creating false fires with unrealistic thresholds or hiding real fires with thresholds that are too forgiving.',
          focusTarget: 'impact_sla_stuck_queue_settings',
        },
        {
          stepId: 'impact_business_hours_sla_clock',
          title: 'Business Hours for SLA Clock',
          body: 'Business Hours decide when the SLA clock is allowed to run. This keeps nights, weekends, and closed clinic hours from making work look falsely overdue. The goal is not to make the dashboard softer — it is to make delay measurement honest. Set this to the hours your team can actually act.',
          teaches: 'Business Hours make stuck-work timing match real working time.',
          prevents: 'Off-hours creating false SLA breaches and misleading Live Fires.',
          focusTarget: 'impact_business_hours_sla_clock',
        },
        {
          stepId: 'impact_scheduling_availability',
          title: 'Scheduling Availability',
          body: 'Scheduling Availability controls when schedulers can book appointments.\n\nUse it to keep booking inside the clinic’s real appointment capacity. Start by copying Business Hours when appointment booking follows the same schedule, then adjust the available days or times if scheduling is narrower.\n\nBusiness Hours measure delay. Scheduling Availability controls bookable slots.',
          teaches: 'Use Scheduling Availability to keep appointments inside real booking capacity.',
          prevents: 'Letting schedulers book outside available clinic days or hours.',
          focusTarget: 'impact_scheduling_availability',
        },
        {
          stepId: 'impact_capacity_value_settings',
          title: 'Capacity Value Settings',
          body: 'Capacity Value Settings control how the dashboard estimates Time Reclaimed. This is not stopwatch-measured time. It is an operational value estimate based on how much staff effort the Gate prevents or reduces. If Time Reclaimed feels too high or too low, adjust the assumption here so the value signal matches your clinic’s reality.',
          teaches: 'Capacity settings turn operational improvement into a realistic time-reclaimed estimate.',
          prevents: 'Distrusting the value signal without understanding that the assumption is configurable.',
          focusTarget: 'impact_capacity_value_settings',
        },
        {
          stepId: 'impact_outcome_definitions',
          title: 'Outcome Definitions',
          body: 'Outcome Definitions tell the analytics layer which Gate outcomes count as completed, prevented, or operationally meaningful. These labels help the Command Center interpret referral quality and value consistently. Do not treat them as cosmetic labels — they shape how outcomes are grouped and understood.',
          teaches: 'Outcome definitions keep analytics interpretation consistent across the operation.',
          prevents: 'Misreading Gate outcomes because the meaning behind each category is unclear.',
          focusTarget: 'impact_outcome_definitions',
        },
        {
          // Role-aware final card. Ops Manager has read-only access to settings, so
          // it gets review-oriented copy; tenant_admin / platform_admin can save, so
          // they get save-oriented copy. Resolved at render time from ctx.role.
          stepId: 'impact_save_settings',
          title: 'Save and Review',
          body: 'Settings only matter after they are saved. After saving, return to the Operations Command Center and check whether the fires, timers, and Time Reclaimed signal now match operational reality. If the dashboard still feels noisy or too quiet, adjust the settings again.',
          teaches: 'Saved settings should be reviewed against the Command Center.',
          prevents: 'Expecting dashboard behavior to change from unsaved edits or untested assumptions.',
          focusTarget: 'impact_save_settings',
          getTitle: (ctx) => ctx?.role === 'ops_manager'
            ? 'Review Against Command Center'
            : 'Save and Review',
          getBody: (ctx) => ctx?.role === 'ops_manager'
            ? 'Ops Managers review these settings to understand how the Command Center measures work.\n\nUse this page to see which SLA timers, scheduling windows, and value assumptions shape the dashboard. Then return to the Ops Command Center and compare the settings against real operational pressure.'
            : 'Settings only change the Command Center after they are saved. After saving, return to the Ops Command Center and check whether Live Fires, timers, and value metrics match operational reality.',
          getTeaches: (ctx) => ctx?.role === 'ops_manager'
            ? 'Review the settings that explain how the Command Center measures stuck work, booking constraints, and value.'
            : 'Save settings, then review them against the Command Center.',
          getPrevents: (ctx) => ctx?.role === 'ops_manager'
            ? 'Treating read-only settings as actions you can save.'
            : 'Expecting dashboard behavior to change from unsaved or untested assumptions.',
        },
      ],
    },
  ],
};

// ============================================================================
// TENANT ADMIN — Governs the clinic's ReferralGate operating environment
//
// Read-and-govern role: manages access (Staff), invitations (Invites), audit
// visibility (Audit Logs), tenant settings (Impact Settings), and operational
// oversight (Ops Command Center). Reuses the Ops Manager OCC + Impact step
// arrays, overriding only the intro card with governance/configuration framing.
// ============================================================================

// Reuse the Ops Manager step arrays verbatim (same focus hooks, same metric copy),
// adjusting only each module's first card to Tenant Admin framing.
const _opsModules = OPS_MANAGER_MAP.guidedModules;
const _occSteps = _opsModules.find((m) => m.moduleId === 'ops_command_center').steps;
const _impactSteps = _opsModules.find((m) => m.moduleId === 'impact_settings').steps;

const TENANT_ADMIN_OCC_STEPS = [
  {
    ..._occSteps[0],
    body: 'This is your tenant’s operational health view. Use it for oversight: check whether the clinic is healthy, spot unresolved operational pressure, and see where configuration or staffing may need adjustment. You do not work the Intake, Triage, Scheduling, or Closed Loop queues from here — your operational roles act on these signals; you govern whether the signals are healthy.',
    teaches: 'Tenant Admin uses the Command Center for oversight and governance, not daily queue work.',
    prevents: 'Treating the Command Center as a place to work operational queues.',
  },
  ..._occSteps.slice(1),
];

const TENANT_ADMIN_IMPACT_STEPS = [
  {
    ..._impactSteps[0],
    body: 'Impact Settings are the rules you configure so the Command Center’s signals stay accurate. You set the SLA thresholds, business hours, scheduling availability, capacity value, and outcome definitions that decide when work counts as stuck, when a fire appears, and how value is estimated. Configure these so your Ops Manager’s signals reflect real operational reality.',
    teaches: 'Tenant Admin configures the settings that make the Command Center’s signals meaningful.',
    prevents: 'Treating these as daily operating actions rather than the rules behind the signals.',
  },
  ..._impactSteps.slice(1),
];

const TENANT_ADMIN_MAP = {
  role: ROLES.TENANT_ADMIN,
  roleDisplayName: ROLE_DISPLAY[ROLES.TENANT_ADMIN]?.name || 'Tenant Admin',

  workflow: {
    id: 'users',
    label: 'Tenant Administration',
    description: 'The governance surface where the Tenant Admin manages access, invites, audit visibility, settings, and operational oversight.',
  },

  valueNeeded: 'A safe, correctly-configured, accountable tenant where every operational role can do its work.',
  objective: 'Keep access clear, invites managed, audit proof available, settings correct, and operations healthy.',

  roleFormMap: {
    intent: 'Govern one clinic’s ReferralGate environment so it stays safe, configured, and accountable.',
    decision: 'Is access correct, are invites managed, is audit proof available, and are settings accurate?',
    criteria: [
      'Staff access and active/inactive status.',
      'Pending and historical invites.',
      'Audit proof and receipts.',
      'Tenant Impact/SLA + scheduling settings.',
      'Operational health on the Command Center.',
    ],
    activities: [
      'Review staff and manage active/inactive access.',
      'Create, resend, and revoke invites.',
      'Review audit logs, receipts, and audit records.',
      'Configure Impact Settings.',
      'Oversee the Command Center for tenant health.',
    ],
    checkpoints: ['Staff', 'Invites', 'Audit Logs', 'Impact Settings', 'Ops Command Center'],
    action: 'Manage access, invites, settings, and oversight — not the daily referral queue.',
    form: 'No referral form; the output is correct access, clean invites, available audit proof, and accurate settings.',
    metrics: ['Staff status', 'Invite states', 'Audit evidence', 'SLA/value settings', 'Command Center health'],
    valueCreated: 'A tenant that is safe, correctly configured, and accountable.',
    learningMoments: [],
  },

  orientationCard: {
    loggedInAs: ROLE_DISPLAY[ROLES.TENANT_ADMIN]?.name || 'Tenant Admin',
    yourJob: 'Govern the clinic’s ReferralGate environment: manage staff access, send secure invites, review audit proof, configure impact settings, and monitor operational health.',
    mainWorkspace: { label: 'Staff', route: '/users' },
    workspacesHeading: 'Your Admin Areas',
    workspaces: [
      { label: 'Ops Command Center', route: '/ops-dashboard' },
      { label: 'Staff',              route: '/users' },
      { label: 'Invites',           route: '/invites' },
      { label: 'Impact Settings',   route: '/impact-settings' },
      { label: 'Audit Logs',        route: '/audit-logs' },
    ],
    can: [
      'Manage staff access and active/inactive status.',
      'Create, resend, and revoke invites.',
      'Review audit logs, receipts, and audit records.',
      'Configure tenant Impact/SLA and scheduling settings.',
      'Monitor the Ops Command Center for tenant health.',
    ],
    cannot: [
      'Work the Intake, Triage, Scheduling, or Closed Loop queues.',
      'Make triage or scheduling decisions or complete closed loop.',
      'Edit a user’s role or specialty on the Staff page (set at invite/creation).',
      'Access PHI.',
    ],
    mainSuccessAction: 'Keep access clear, invites managed, audit proof available, and settings correct.',
    noChaosRule: 'Never leave access unclear, pending invites unmanaged, audit proof hidden, or operational settings misconfigured.',
  },

  roleClarityQuestions: {
    whoAmI: 'I am the Tenant Admin.',
    whatAmIHereToDo: 'Govern access, invites, audit visibility, settings, and operational oversight for one clinic.',
    whatCanITouch: 'Staff access/status, invites, audit logs, Impact Settings, and the Command Center (oversight).',
    whatCanINotTouch: 'The daily referral queues, clinical decisions, role/specialty editing on Staff, and PHI.',
    whatActionCreatesProgress: 'Managing access and invites, reviewing audit proof, and configuring settings correctly.',
    whatFormMustExistAfterIAct: 'No referral form — clear access, clean invites, available proof, and accurate settings.',
  },

  guidedModules: [
    {
      moduleId: 'ta_staff',
      title: 'Staff Management',
      route: '/users',
      sector: SECTORS.TENANT_ADMIN,
      role: ROLES.TENANT_ADMIN,
      steps: [
        {
          stepId: 'ta_staff_header',
          title: 'Staff Management',
          body: 'Use Staff to see who belongs to this tenant, what role they hold, which specialties apply, and whether their account is active.\n\nThis page gives you access-control visibility. It is not where you rewrite a user’s role or specialty after creation.',
          teaches: 'Check the staff list before changing access.',
          prevents: 'Changing access without confirming the user, role, and status.',
          focusTarget: 'ta_staff_header',
        },
        {
          stepId: 'ta_staff_status_filter',
          title: 'Active and Inactive Staff',
          body: 'Use the status filter to separate active users from inactive users.\n\nActive users can continue using their permitted ReferralGate areas. Inactive users should no longer operate inside the tenant.',
          teaches: 'Use staff status to control who can work inside the tenant.',
          prevents: 'Leaving old or incorrect access active.',
          focusTarget: 'ta_staff_status_filter',
        },
        {
          stepId: 'ta_staff_status_button',
          title: 'Change Staff Status',
          body: 'Click a user’s status button when you need to activate or deactivate their access.\n\nActive users can use their permitted ReferralGate areas. Inactive users should no longer operate inside the tenant. Confirm the correct user and role before changing status.',
          teaches: 'Use the status button to control whether a staff member can access the tenant.',
          prevents: 'Leaving access active for someone who should no longer work in ReferralGate.',
          focusTarget: 'ta_staff_status_button',
        },
        {
          stepId: 'ta_staff_table',
          title: 'Staff Table',
          body: 'Review the user, role, specialties, status, and available actions before making an access change.\n\nA safe access change starts with confirming the exact person and their current role.',
          teaches: 'Read the full staff row before acting.',
          prevents: 'Deactivating or reactivating the wrong user.',
          focusTarget: 'ta_staff_table',
        },
        {
          stepId: 'ta_staff_invites',
          title: 'Add Staff Through Invites',
          body: 'Create new staff through secure email invites instead of direct account creation.\n\nThe invite link lets the correct person claim the account, create their password, and enter the tenant through a controlled path.',
          teaches: 'New staff enter through invite-based account setup.',
          prevents: 'Unsafe or unclear account creation.',
          focusTarget: 'ta_staff_invites',
        },
      ],
    },
    {
      moduleId: 'ta_invites',
      title: 'Invites',
      route: '/invites',
      sector: SECTORS.TENANT_ADMIN,
      role: ROLES.TENANT_ADMIN,
      steps: [
        {
          stepId: 'ta_invites_header',
          title: 'Invites',
          body: 'Use Invites to bring staff into the tenant through a secure email link.\n\nThe invite defines the user’s role before they register. The recipient claims the invite, creates their password, and enters the clinic with the correct access.',
          teaches: 'Provision users through controlled email invites.',
          prevents: 'Creating accounts without clear ownership or secure claiming.',
          focusTarget: 'ta_invites_header',
        },
        {
          stepId: 'ta_invites_tabs',
          title: 'Pending vs All',
          body: 'Use Pending to track invites that still need action.\n\nUse All to review the full invite history, including invites that were accepted, revoked, expired, or resent.',
          teaches: 'Separate open invite work from invite history.',
          prevents: 'Missing an invite that still needs follow-up.',
          focusTarget: 'ta_invites_tabs',
        },
        {
          stepId: 'ta_invites_create',
          title: 'Create Invite',
          body: 'Click Create Invite when you need to add a new staff member to the tenant.\n\nChoose the correct role before sending. For Specialist invites, select the specialist’s specialty so their access matches their clinical area.',
          teaches: 'Create the right invite with the right role and specialty.',
          prevents: 'Sending an invite that gives the wrong access.',
          focusTarget: 'ta_invites_create',
        },
        {
          stepId: 'ta_invites_modal_role_specialty',
          title: 'Role and Specialty Access',
          body: 'Choose the staff member’s role before sending the invite.\n\nIf you select Specialist, also select the correct specialty. This links the invited specialist to the clinical area they should work in after registration.',
          teaches: 'Assign the correct role and specialty before sending the invite.',
          prevents: 'Inviting a specialist without the right specialty access.',
          focusTarget: 'ta_invites_modal_role_specialty',
        },
        {
          stepId: 'ta_invites_actions',
          title: 'Resend or Revoke',
          body: 'Use Resend when the correct person still needs the invite.\n\nUse Revoke when the invite should no longer be claimable.',
          teaches: 'Keep pending invites clean and intentional.',
          prevents: 'Leaving unused invite links active.',
          focusTarget: 'ta_invites_actions',
        },
      ],
    },
    {
      moduleId: 'ta_audit_logs',
      title: 'Audit Logs',
      route: '/audit-logs',
      sector: SECTORS.AUDIT,
      role: ROLES.TENANT_ADMIN,
      steps: [
        {
          stepId: 'ta_audit_header',
          title: 'Audit Logs',
          body: 'Use Audit Logs to review referral proof across the tenant.\n\nEach row shows when a referral was submitted, which specialty and pack were used, what the Gate decided, who submitted it, and the current status signal.',
          teaches: 'Audit referrals by decision, pack, submitter, and status.',
          prevents: 'Searching operational proof in the wrong place.',
          focusTarget: 'ta_audit_header',
        },
        {
          stepId: 'ta_audit_filters',
          title: 'Filters and Refresh',
          body: 'Use Filters to narrow the audit list when you need a specific outcome, specialty, pack, submitter, or date range.\n\nUse Refresh when you need the latest loaded records.',
          teaches: 'Narrow audit evidence before opening records.',
          prevents: 'Reviewing the wrong referral or outdated list.',
          focusTarget: 'ta_audit_filters',
        },
        {
          stepId: 'ta_audit_receipt',
          title: 'Receipt',
          body: 'Open Receipt to view the referral’s non-PHI operational proof.\n\nThe receipt shows the referral summary, Gate decision, criteria snapshot, status updates, and activity timeline. Use it as the main audit proof for what happened.',
          teaches: 'Use receipts as operational proof of referral handling.',
          prevents: 'Opening duplicate audit views for the same referral question.',
          focusTarget: 'ta_audit_receipt',
        },
        {
          stepId: 'ta_audit_export',
          title: 'Export CSV',
          body: 'Use Export CSV when you need an offline governance or review file from the current audit view.\n\nFilter first, then export, so the file matches the question you are trying to answer.',
          teaches: 'Export audit evidence after narrowing the view.',
          prevents: 'Creating broad exports that are harder to review.',
          focusTarget: 'ta_audit_export',
        },
      ],
    },
    {
      moduleId: 'impact_settings',
      title: 'Impact Settings',
      route: '/impact-settings',
      sector: SECTORS.TENANT_ADMIN,
      role: ROLES.TENANT_ADMIN,
      steps: TENANT_ADMIN_IMPACT_STEPS,
    },
    {
      moduleId: 'ops_command_center',
      title: 'Operations Command Center',
      route: '/ops-dashboard',
      sector: SECTORS.ANALYTICS,
      role: ROLES.TENANT_ADMIN,
      steps: TENANT_ADMIN_OCC_STEPS,
    },
  ],
};

// ============================================================================
// ROLE LEARNING MAPS — Complete registry
// ============================================================================

export const ROLE_LEARNING_MAPS = {
  [ROLES.GP_EMPLOYEE]: GP_EMPLOYEE_MAP,
  [ROLES.INTAKE_COORDINATOR]: INTAKE_COORDINATOR_MAP,
  [ROLES.SCHEDULER]: SCHEDULER_MAP,
  [ROLES.PLATFORM_ADMIN]: buildSkeletonMap(ROLES.PLATFORM_ADMIN),
  [ROLES.TENANT_ADMIN]: TENANT_ADMIN_MAP,
  [ROLES.OPS_MANAGER]: OPS_MANAGER_MAP,
  [ROLES.TRIAGE_NURSE]: TRIAGE_NURSE_MAP,
  [ROLES.SPECIALIST]: SPECIALIST_MAP,
  [ROLES.SPECIALIST_ADMIN]: SPECIALIST_ADMIN_MAP,
  [ROLES.PENDING]: buildSkeletonMap(ROLES.PENDING),
};

// ============================================================================
// FULLY CONFIGURED ROLES — roles with complete (non-skeleton) learning maps
// ============================================================================

const CONFIGURED_ROLES = new Set([
  ROLES.GP_EMPLOYEE,
  ROLES.INTAKE_COORDINATOR,
  ROLES.SCHEDULER,
  ROLES.TRIAGE_NURSE,
  ROLES.SPECIALIST,
  ROLES.SPECIALIST_ADMIN,
  ROLES.OPS_MANAGER,
  ROLES.TENANT_ADMIN,
]);

// ============================================================================
// ACCESSORS
// ============================================================================

export function resolveStepContent(step, ctx) {
  if (!step) return { stepId: '_empty', title: '', body: '', teaches: null, prevents: null, focusTarget: null };
  return {
    ...step,
    title: step.getTitle ? step.getTitle(ctx) : step.title,
    body: step.getBody ? step.getBody(ctx) : step.body,
    teaches: step.getTeaches ? step.getTeaches(ctx) : step.teaches,
    prevents: step.getPrevents ? step.getPrevents(ctx) : step.prevents,
    focusTarget: step.getFocusTarget ? step.getFocusTarget(ctx) : step.focusTarget,
  };
}

export function getRoleLearningMap(role) {
  return ROLE_LEARNING_MAPS[role] || null;
}

export function getOrientationCard(role) {
  const map = ROLE_LEARNING_MAPS[role];
  return map ? map.orientationCard : null;
}

// ============================================================================
// SHARED MODULE — UNDERSTAND REFERRAL PINGS
//
// One module for every role that can access the Referral Pings page. It teaches
// both signal types that live there: Referral Pings (referral-bound action
// threads) and Queue Pings (acknowledge-only sector fires). UI-only; teaches the
// real page behavior. focusTargets map to highlight hooks in ReferralPingsView.
// ============================================================================

const REFERRAL_PINGS_MODULE_STEPS = [
  {
    stepId: 'rp_attention_home',
    title: 'Your Attention Home',
    body: 'Your Referral Pings workspace separates action from noise.\n\nReferral Pings are referral-bound action threads: one referral, one action, one receiver. Use them to reply, open the exact referral, and close the thread when the action is complete.\n\nQueue Pings are sector-fire alerts: one queue problem, one responsible role, acknowledgment only.\n\nNo PHI. No documents. No loose chat.',
    teaches: 'Referral Pings is where action signals land.',
    prevents: 'Treating this page like chat or document exchange.',
    focusTarget: 'referral_pings_workspace',
  },
  {
    stepId: 'rp_signal_type',
    title: 'Choose the Signal Type',
    body: 'Use the top filter to separate the kind of signal you are handling.\n\nAll shows both signal types. Referral Pings shows referral-bound action threads. Queue Pings shows sector-fire alerts.\n\nStart with the signal type, then act from the correct section.',
    teaches: 'Signal type decides the work pattern.',
    prevents: 'Handling a queue fire like a one-referral conversation, or a referral action like a general announcement.',
    focusTarget: 'referral_pings_type_filter',
  },
  {
    stepId: 'rp_queue_pings',
    title: 'Queue Pings: Acknowledge Sector Fires',
    body: 'Queue Pings come from active operational fires. They tell your role that a queue needs attention now.\n\nNew shows queue signals you have not acknowledged. Acknowledged keeps reviewed signals out of your active attention lane.\n\nOpen Queue takes you to the workspace where the fire is handled.',
    teaches: 'Queue Pings direct sector attention.',
    prevents: 'Letting acknowledged queue fires stay mixed with new work.',
    focusTarget: 'referral_pings_queue_section',
  },
  {
    stepId: 'rp_referral_pings',
    title: 'Referral Pings: Work One Referral',
    body: 'Referral Pings point to one referral and one action.\n\nWaiting on Me shows pings where you need to act. Sent by Me shows pings you started. Resolved shows completed threads. Cancelled shows withdrawn requests.\n\nOpen the ping, read the action needed, and move the referral from there.',
    teaches: 'Referral Pings direct referral-level action.',
    prevents: 'Using pings as loose chat without opening the referral.',
    focusTarget: 'referral_pings_referral_tabs',
  },
  {
    stepId: 'rp_open_referral',
    title: 'Open the Exact Referral',
    body: 'Use Open Referral to go directly to the referral that needs work.\n\nThe ping is the signal. The referral is where the real work happens.\n\nReply when clarification is needed, but complete documents, updates, and workflow movement on the referral itself.',
    teaches: 'Open Referral moves attention to the work surface.',
    prevents: 'Trying to complete referral work inside the message thread.',
    focusTarget: 'referral_pings_open_referral',
  },
  {
    stepId: 'rp_replies_system',
    title: 'Replies and System Updates',
    body: 'Use replies for short non-PHI coordination.\n\nSystem updates appear when ReferralGate can prove the requested action happened: missing information submitted, consult note added, returned item resolved.\n\nSystem updates are proof signals. They are not written by users.',
    teaches: 'Replies coordinate. System updates prove movement.',
    prevents: 'Sending PHI, documents, filenames, or clinical details inside replies.',
    focusTarget: 'referral_pings_thread_area',
  },
  {
    stepId: 'rp_close_thread',
    title: 'Close the Thread Correctly',
    body: 'Use the closing action that matches the situation.\n\nAcknowledge means you saw the request.\n\nOnly the sender can close the thread.\n\nResolve Ping means the sender confirmed a conversation-based action. Mark Done or Mark Complete means the system has proof that the action happened. Cancel means the sender withdrew the request.\n\nClosing the ping never changes workflow state. It only clears the communication thread.',
    teaches: 'Close the signal only after the action is complete or withdrawn.',
    prevents: 'Letting receivers close requests they did not create, or leaving completed pings active.',
    focusTarget: 'referral_pings_action_row',
  },
];

function buildReferralPingsModule(role) {
  return {
    moduleId: 'referral_pings_intro',
    title: 'Understand Referral Pings',
    route: '/referral-pings',
    sector: SECTORS.WORKQUEUE,
    role,
    steps: REFERRAL_PINGS_MODULE_STEPS,
  };
}

export function getGuidedModulesForRole(role) {
  const map = ROLE_LEARNING_MAPS[role];
  const base = map ? map.guidedModules : [];
  // Append the shared Referral Pings module for every role that can ACCESS the
  // Referral Pings page (page-access is the right gate — broader than the
  // receiver-only REFERRAL_PINGS_ENABLED_ROLES, which excludes ops_manager even
  // though ops_manager opens this page as the Queue Ping sender). One shared
  // module, no per-role duplication; returned as a new array (no map mutation).
  if (getAllowedRoutes(role).includes('referral-pings')) {
    return [...base, buildReferralPingsModule(role)];
  }
  return base;
}

export function getLearningScenariosForRole(role) {
  const map = ROLE_LEARNING_MAPS[role];
  return map ? (map.learningScenarios || []) : [];
}

export function isRoleLearningConfigured(role) {
  return CONFIGURED_ROLES.has(role);
}

export default ROLE_LEARNING_MAPS;
